/**
 * Order sheet — inline product details accordion (media, variant table, add selected, weight modal).
 */
(function () {
  'use strict';

  const FILTER_EVT = 'filter:update';
  const WEIGHT_SEARCH_DEBOUNCE_MS = 150;
  const WEIGHT_FETCH_TIMEOUT_MS = 15000;
  const WEIGHT_MODAL_FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="68" height="68" viewBox="0 0 68 68"><rect width="68" height="68" rx="8" fill="#f3f4f6"/><path d="M16 47l11-12 9 9 7-7 9 10" fill="none" stroke="#9ca3af" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/><circle cx="26" cy="24" r="4" fill="#d1d5db"/></svg>'
    );
  const TOGGLE_SEL = '[data-order-sheet-details-toggle], [data-plp-details-toggle]';
  const ITEM_SEL = '[data-order-sheet-item], [data-plp-inventory-item], [data-plp-table-item]';
  const ROW_SEL = '[data-order-sheet-row], [data-product-row].inventory-product-row';
  const PANEL_SEL = '[data-order-sheet-details-panel], [data-plp-details-panel]';

  /** @type {WeakMap<HTMLElement, AbortController>} */
  const hostControllers = new WeakMap();
  /** @type {Record<string, { entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>} */
  const weightCache = Object.create(null);
  /** @type {Record<string, Promise<{ entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>>} */
  const weightPending = Object.create(null);

  let documentToggleBound = false;
  let toastHost = /** @type {HTMLElement | null} */ (null);

  /** @param {string} s */
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** @param {string} message @param {'error'|'info'} [type] */
  function showToast(message, type) {
    if (!toastHost) {
      toastHost = document.createElement('div');
      toastHost.className = 'order-sheet-weight-toast-host';
      toastHost.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastHost);
    }
    const el = document.createElement('div');
    el.className = 'order-sheet-weight-toast' + (type === 'error' ? ' order-sheet-weight-toast--error' : '');
    el.textContent = message;
    toastHost.appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    window.setTimeout(() => {
      el.classList.remove('is-visible');
      window.setTimeout(() => el.remove(), 300);
    }, 4200);
  }

  /** @param {Element | null | undefined} cfgEl */
  function parseWeightConfigElement(cfgEl) {
    const defaults = { apiBaseUrl: '', weightsPath: '/public/products' };
    if (!cfgEl || !cfgEl.textContent) return defaults;
    try {
      const parsed = JSON.parse(cfgEl.textContent);
      const apiBaseUrl = String(
        (parsed && (parsed.apiBaseUrl || parsed.baseUrl)) || ''
      )
        .trim()
        .replace(/\/+$/, '');
      return {
        apiBaseUrl,
        weightsPath:
          parsed && parsed.weightsPath
            ? String(parsed.weightsPath).replace(/\/+$/, '')
            : '/public/products',
      };
    } catch {
      return defaults;
    }
  }

  /** @param {HTMLElement} root */
  function getFetchWeightConfig(root) {
    /** @type {Array<Element | null | undefined>} */
    const candidates = [
      root.querySelector('[data-plp-fetch-weight-config], [data-quick-view-weight-config]'),
      root.closest('[data-plp-expanded-details]')?.querySelector(
        '[data-plp-fetch-weight-config], [data-quick-view-weight-config]'
      ),
      root.closest('[data-quick-view-root], #QuickViewModal')?.querySelector(
        '[data-plp-fetch-weight-config], [data-quick-view-weight-config]'
      ),
      root.closest('[data-pdp-section]')?.querySelector('[data-pdp-fetch-weight-config]'),
      document.querySelector('#QuickViewModal [data-plp-fetch-weight-config], #QuickViewModal [data-quick-view-weight-config]'),
      document.querySelector('[data-plp-fetch-weight-config-section]'),
      document.querySelector('[data-batch-weight-config]'),
    ];

    /** @type {{ apiBaseUrl: string, weightsPath: string }} */
    let resolved = { apiBaseUrl: '', weightsPath: '/public/products' };
    for (const el of candidates) {
      if (el && el.hasAttribute('data-batch-weight-config')) {
        try {
          const raw = JSON.parse(el.textContent || '{}');
          if (raw && raw.enabled === false) continue;
        } catch {
          /* ignore */
        }
      }
      const cfg = parseWeightConfigElement(el);
      if (cfg.weightsPath) resolved.weightsPath = cfg.weightsPath;
      if (cfg.apiBaseUrl) {
        resolved = cfg;
        break;
      }
    }
    return resolved;
  }

  /** @param {string} base @param {string} path */
  function joinUrl(base, path) {
    const b = String(base || '').replace(/\/+$/, '');
    let p = String(path || '');
    if (!p) return b || '';
    if (/^https?:\/\//i.test(p)) return p;
    if (p.charAt(0) !== '/') p = '/' + p;
    return b ? b + p : p;
  }

  /** @param {string} url */
  function getWeightRequestHeaders(url) {
    /** @type {Record<string, string>} */
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
    try {
      const parsed = new URL(url, window.location.origin);
      const host = (parsed.hostname || '').toLowerCase();
      if (host.indexOf('ngrok') >= 0) headers['ngrok-skip-browser-warning'] = 'true';
    } catch {
      /* ignore */
    }
    return headers;
  }

  /**
   * Shared weight cache hydrated by weight-service products-batch (via quick-view sync).
   * @returns {{
   *   rawBySku?: Record<string, unknown>,
   *   dataBySku?: Record<string, { entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }>,
   *   pendingBySku?: Record<string, Promise<void>>
   * } | null}
   */
  function getSharedWeightStore() {
    const w = /** @type {any} */ (window);
    return w.__aoneWeightSharedStore || null;
  }

  /**
   * Wait for the in-flight products-batch request. Never starts a new weight API call.
   * @returns {Promise<void>}
   */
  async function waitForCurrentWeightData() {
    const w = /** @type {any} */ (window);
    const api = w.AOneProductWeightsAPI || w.AoneBatchWeight;
    if (api && typeof api.waitForCurrentWeightData === 'function') {
      await api.waitForCurrentWeightData();
      return;
    }
    if (w.AOneProductWeights?.activeRequest) {
      await w.AOneProductWeights.activeRequest;
    }
  }

  /**
   * Look up a SKU item from the product-level in-memory cache.
   * @param {string} sku
   * @returns {Record<string, unknown> | null}
   */
  function getCachedSkuItem(sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return null;
    const store = /** @type {any} */ (window).AOneProductWeights;
    if (!store?.skus) return null;
    const entry =
      store.skus.get(safeSku) ||
      store.skus.get(safeSku.toUpperCase()) ||
      store.skus.get(safeSku.toLowerCase());
    if (entry?.item && typeof entry.item === 'object') {
      return /** @type {Record<string, unknown>} */ (entry.item);
    }
    return null;
  }

  /** @param {string} sku @param {{ entries: Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }> }} normalized */
  function setWeightCacheForSku(sku, normalized) {
    const safeSku = String(sku || '').trim();
    if (!safeSku || !normalized || !Array.isArray(normalized.entries)) return;
    weightCache[safeSku] = normalized;
    const shared = getSharedWeightStore();
    if (shared && shared.dataBySku) {
      shared.dataBySku[safeSku.toLowerCase()] = normalized;
    }
  }

  /** @param {string} sku */
  function readSharedWeightForSku(sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return null;
    const shared = getSharedWeightStore();
    if (!shared) return null;
    const key = safeSku.toLowerCase();

    const normalized = shared.dataBySku?.[key];
    if (normalized && Array.isArray(normalized.entries) && normalized.entries.length) {
      return normalized;
    }

    const raw = shared.rawBySku?.[key];
    if (raw === undefined) return null;
    const entries = extractWeightEntries(raw);
    if (!entries.length) return null;
    const fromRaw = { entries };
    if (shared.dataBySku) shared.dataBySku[key] = fromRaw;
    return fromRaw;
  }

  /** @param {string} key */
  function weightFieldLabel(key) {
    return String(key || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim();
  }

  /** @param {string} key @param {unknown} value */
  function formatWeightFieldValue(key, value) {
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number' && Number.isFinite(value)) {
      const k = String(key || '').toLowerCase();
      if (k.indexOf('weight') >= 0 || k.indexOf('gram') >= 0) return value.toFixed(2) + ' g';
      return String(value);
    }
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    const k = String(key || '').toLowerCase();
    if (k.indexOf('updated') >= 0 || k.indexOf('date') >= 0 || k.indexOf('time') >= 0) {
      const dt = new Date(trimmed);
      if (!Number.isNaN(dt.getTime())) {
        try {
          return dt.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          });
        } catch {
          return dt.toISOString();
        }
      }
    }
    return trimmed;
  }

  /** @param {object | number | string} entry @param {number} idx */
  function normalizeWeightEntry(entry, idx) {
    let id = 'weight-' + idx;
    /** @type {Array<{ label: string, value: string }>} */
    const fields = [];
    let label = '';

    if (typeof entry === 'number' || typeof entry === 'string') {
      const simpleValue = formatWeightFieldValue('weight', entry);
      if (!simpleValue) return null;
      fields.push({ label: 'Weight', value: simpleValue });
      return { id, label: simpleValue, fields };
    }

    if (!entry || typeof entry !== 'object') return null;
    const obj = /** @type {Record<string, unknown>} */ (entry);
    if (obj.id != null) id = String(obj.id);
    if (obj.weight_id != null) id = String(obj.weight_id);

    const serialNumberRaw =
      obj.serialNumber != null
        ? obj.serialNumber
        : obj.serial_number != null
          ? obj.serial_number
          : null;
    const serialNumber =
      serialNumberRaw === null || serialNumberRaw === undefined ? '' : String(serialNumberRaw).trim();

    if (serialNumber && obj.weight != null) {
      const weightNum = Number(obj.weight);
      const weightLabel = formatWeightFieldValue('weight', obj.weight);
      if (!weightLabel || !Number.isFinite(weightNum) || weightNum <= 0) return null;
      return {
        id: serialNumber,
        label: weightLabel,
        serialNumber,
        weightGrams: weightNum,
        unit: obj.unit != null ? String(obj.unit) : 'g',
        fields: [
          { label: 'Serial', value: serialNumber },
          { label: 'Weight', value: weightLabel },
        ],
      };
    }

    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
      const raw = obj[key];
      if (raw == null || typeof raw === 'object') continue;
      const formatted = formatWeightFieldValue(key, raw);
      if (!formatted) continue;
      fields.push({ label: weightFieldLabel(key), value: formatted });
    }

    if (!fields.length) return null;
    const preferred = [
      'gross_weight',
      'gross_weight_g',
      'net_weight',
      'net_weight_g',
      'metal_weight',
      'diamond_weight',
      'weight',
      'weight_g',
    ];
    for (const pref of preferred) {
      const prefLabel = weightFieldLabel(pref);
      const match = fields.find((f) => f.label === prefLabel);
      if (match) {
        label = match.value;
        break;
      }
    }
    if (!label) label = fields[0].value || 'Weight #' + (idx + 1);
    return { id, label, fields };
  }

  /**
   * Prefer availableOptions (serial + weight) over availableWeights (numbers only).
   * App sync requires real WMS serial numbers; plain weights produce fake ids like "weight-0".
   * @param {Record<string, unknown>} obj
   * @returns {Array<object | number | string> | null}
   */
  function pickWeightEntryList(obj) {
    if (Array.isArray(obj.availableOptions)) return obj.availableOptions;
    if (Array.isArray(obj.availableWeights)) return obj.availableWeights;
    if (Array.isArray(obj.weights)) return obj.weights;
    return null;
  }

  /** @param {unknown} payload @param {{ skipReservedFilter?: boolean }} [options] */
  function extractWeightEntries(payload, options) {
    const opts = options || {};
    /** @type {Array<object | number | string>} */
    let rawEntries = [];
    if (Array.isArray(payload)) {
      rawEntries = payload;
    } else if (payload && typeof payload === 'object') {
      const root = /** @type {Record<string, unknown>} */ (payload);
      const rootList = pickWeightEntryList(root);
      if (rootList) rawEntries = rootList;
      else if (Array.isArray(root.data)) rawEntries = root.data;
      else if (Array.isArray(root.items)) rawEntries = root.items;
      else if (Array.isArray(root.results)) rawEntries = root.results;
      else if (root.data && typeof root.data === 'object') {
        const data = /** @type {Record<string, unknown>} */ (root.data);
        const dataList = pickWeightEntryList(data);
        if (dataList) rawEntries = dataList;
      } else {
        rawEntries = [root];
      }
    }

    /** @type {Array<{ id: string, label: string, serialNumber?: string, fields: Array<{ label: string, value: string }> }>} */
    const entries = [];
    const inventoryApi = getWmsAvailabilityApi();
    for (let i = 0; i < rawEntries.length; i++) {
      const normalized = normalizeWeightEntry(rawEntries[i], i);
      if (!normalized) continue;
      const serial = String(normalized.serialNumber || '').trim();
      if (
        !opts.skipReservedFilter &&
        serial &&
        inventoryApi?.isSerialReserved?.(serial)
      ) {
        continue;
      }
      entries.push(normalized);
    }
    return entries;
  }

  /**
   * @param {Record<string, unknown>} obj
   * @returns {string}
   */
  function extractSkuFromObject(obj) {
    const keys = [
      'sku',
      'SKU',
      'productSku',
      'product_sku',
      'variantSku',
      'variant_sku',
      'itemCode',
      'item_code',
      'code',
    ];
    for (const key of keys) {
      if (!(key in obj)) continue;
      const sku = String(obj[key] || '').trim();
      if (sku) return sku;
    }
    return '';
  }

  /**
   * Extract node for a single SKU from batch payload shapes.
   * @param {unknown} payload
   * @param {string} targetSku
   * @returns {unknown | null}
   */
  function extractBatchNodeForSku(payload, targetSku) {
    const needle = String(targetSku || '').trim().toLowerCase();
    if (!needle || !payload || typeof payload !== 'object') return null;

    /**
     * @param {unknown} node
     * @returns {unknown | null}
     */
    const fromList = (node) => {
      if (!Array.isArray(node)) return null;
      for (const item of node) {
        if (!item || typeof item !== 'object') continue;
        const obj = /** @type {Record<string, unknown>} */ (item);
        const sku = extractSkuFromObject(obj).toLowerCase();
        if (sku === needle) return obj;
      }
      return null;
    };

    const root = /** @type {Record<string, unknown>} */ (payload);
    const rootByList = fromList(root);
    if (rootByList) return rootByList;

    for (const key of ['products', 'results', 'items', 'data', 'weights']) {
      const bucket = root[key];
      if (!bucket) continue;

      const matchFromList = fromList(bucket);
      if (matchFromList) return matchFromList;

      if (bucket && typeof bucket === 'object' && !Array.isArray(bucket)) {
        const obj = /** @type {Record<string, unknown>} */ (bucket);
        const sku = extractSkuFromObject(obj).toLowerCase();
        if (sku === needle) return obj;

        for (const [k, v] of Object.entries(obj)) {
          if (String(k).trim().toLowerCase() === needle) return v;
        }

        const nestedItems = obj.items;
        const matchNested = fromList(nestedItems);
        if (matchNested) return matchNested;
      }
    }

    for (const [k, v] of Object.entries(root)) {
      if (String(k).trim().toLowerCase() === needle) return v;
    }
    return null;
  }

  /** @param {HTMLElement} root @param {string} sku */
  function fetchWeightPayload(root, sku) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return Promise.reject(new Error('SKU missing'));
    if (weightCache[safeSku]) return Promise.resolve(weightCache[safeSku]);
    if (weightPending[safeSku]) return weightPending[safeSku];

    const sharedImmediate = readSharedWeightForSku(safeSku);
    if (sharedImmediate) {
      setWeightCacheForSku(safeSku, sharedImmediate);
      return Promise.resolve(sharedImmediate);
    }

    const fromProductCache = getCachedSkuItem(safeSku);
    if (fromProductCache) {
      const entries = extractWeightEntries(fromProductCache);
      const normalized = { entries };
      setWeightCacheForSku(safeSku, normalized);
      const shared = getSharedWeightStore();
      if (shared?.rawBySku) {
        shared.rawBySku[safeSku.toLowerCase()] = fromProductCache;
      }
      return Promise.resolve(normalized);
    }

    const WEIGHT_UNAVAILABLE_MSG =
      'Weight information is currently unavailable. Please try again.';

    /**
     * Resolve from in-memory cache only after any in-flight products-batch completes.
     * Never calls /weights/batch or /weights/products-batch from weight selection.
     */
    const resolveFromCacheOnly = () =>
      waitForCurrentWeightData()
        .then(() => {
          const fromShared = readSharedWeightForSku(safeSku);
          if (fromShared) {
            setWeightCacheForSku(safeSku, fromShared);
            return fromShared;
          }

          const item = getCachedSkuItem(safeSku);
          if (item) {
            const entries = extractWeightEntries(item);
            const normalized = { entries };
            setWeightCacheForSku(safeSku, normalized);
            const shared = getSharedWeightStore();
            if (shared?.rawBySku) {
              shared.rawBySku[safeSku.toLowerCase()] = item;
            }
            return normalized;
          }

          throw new Error(WEIGHT_UNAVAILABLE_MSG);
        })
        .catch((err) => {
          const message =
            err && typeof err.message === 'string' && err.message
              ? err.message
              : WEIGHT_UNAVAILABLE_MSG;
          throw new Error(message);
        });

    const shared = getSharedWeightStore();
    const sharedPending = shared?.pendingBySku?.[safeSku.toLowerCase()] || null;
    const req = (sharedPending
      ? Promise.resolve(sharedPending).then(() => {
          const fromShared = readSharedWeightForSku(safeSku);
          if (fromShared) {
            setWeightCacheForSku(safeSku, fromShared);
            return fromShared;
          }
          return resolveFromCacheOnly();
        })
      : resolveFromCacheOnly()
    ).finally(() => {
      delete weightPending[safeSku];
    });

    weightPending[safeSku] = req;
    return req;
  }

  /** @param {HTMLElement} panel */
  function clearCloseTimer(panel) {
    const id = panel.dataset.closeTimerId;
    if (id) {
      window.clearTimeout(Number(id));
      delete panel.dataset.closeTimerId;
    }
  }

  /** @param {HTMLElement} host */
  function allowMultiple(host) {
    return host.dataset.plpDetailsMulti === 'true';
  }

  /** @param {HTMLInputElement} input */
  function readQty(input) {
    const raw = String(input.value ?? '').trim();
    if (raw === '') return 0;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /**
   * Optional per-variant inventory ceiling from data-variant-inventory-qty / input.max.
   * @param {HTMLElement} row
   * @param {HTMLInputElement} [input]
   * @returns {number | null}
   */
  function getRowQtyMax(row, input) {
    const fromRow = Number(row.dataset.variantInventoryQty);
    if (Number.isFinite(fromRow) && fromRow >= 0) return fromRow;
    if (input) {
      const maxAttr = input.getAttribute('max');
      if (maxAttr != null && maxAttr !== '') {
        const fromMax = Number(maxAttr);
        if (Number.isFinite(fromMax) && fromMax >= 0) return fromMax;
      }
    }
    return null;
  }

  /**
   * Numeric grams for a normalized weight entry (never string-sort weights).
   * @param {{ weightGrams?: number, label?: string, fields?: Array<{ label: string, value: string }> } | null | undefined} entry
   * @returns {number}
   */
  function parseEntryWeightGrams(entry) {
    if (!entry) return Number.POSITIVE_INFINITY;
    const direct = Number(entry.weightGrams);
    if (Number.isFinite(direct) && direct > 0) return direct;
    const fromLabel = parseWeightGrams(String(entry.label || ''));
    if (fromLabel > 0) return fromLabel;
    const weightField = Array.isArray(entry.fields)
      ? entry.fields.find((f) => /weight/i.test(String(f.label || '')))
      : null;
    if (weightField) {
      const fromField = parseWeightGrams(String(weightField.value || ''));
      if (fromField > 0) return fromField;
    }
    return Number.POSITIVE_INFINITY;
  }

  /**
   * Serials already claimed by other variant rows in the same details root.
   * @param {HTMLElement | null} detailsRoot
   * @param {HTMLElement} currentRow
   * @returns {Set<string>}
   */
  function collectSelectedSerialsOutsideRow(detailsRoot, currentRow) {
    /** @type {Set<string>} */
    const used = new Set();
    if (!(detailsRoot instanceof HTMLElement)) return used;
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || row === currentRow) return;
      String(row.dataset.serialNumbersValue || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((serial) => used.add(serial));
    });
    return used;
  }

  /**
   * Valid selectable WMS options for a SKU: unique serials, valid weights, not cart-reserved,
   * not claimed by other rows. Sorted by numeric weight ascending.
   * @param {string} sku
   * @param {Set<string> | string[]} [excludeSerials]
   * @returns {Array<{ id: string, label: string, serialNumber?: string, weightGrams?: number, unit?: string, fields: Array<{ label: string, value: string }> }>}
   */
  function getSelectableWeightEntriesForSku(sku, excludeSerials) {
    const safeSku = String(sku || '').trim();
    if (!safeSku) return [];

    /** @type {Array<{ id: string, label: string, serialNumber?: string, weightGrams?: number, unit?: string, fields: Array<{ label: string, value: string }> }>} */
    let entries = [];
    const inventoryApi = getWmsAvailabilityApi();

    // Prefer shared cart-aware effective options so QV/PDP/Order Sheet stay aligned.
    if (inventoryApi && typeof inventoryApi.filterAvailableOptions === 'function') {
      entries = extractWeightEntries(inventoryApi.filterAvailableOptions(safeSku), {
        skipReservedFilter: true,
      });
    }
    if (!entries.length && inventoryApi && typeof inventoryApi.getAvailableOptions === 'function') {
      entries = extractWeightEntries(inventoryApi.getAvailableOptions(safeSku), {
        skipReservedFilter: true,
      });
    }

    if (!entries.length) {
      const shared = getSharedWeightStore();
      const key = safeSku.toLowerCase();
      const raw = shared?.rawBySku?.[key];
      if (raw != null) {
        entries = extractWeightEntries(raw);
      } else {
        const cached = readSharedWeightForSku(safeSku);
        if (cached?.entries?.length) entries = cached.entries.slice();
      }
    }

    const exclude =
      excludeSerials instanceof Set
        ? excludeSerials
        : new Set(Array.isArray(excludeSerials) ? excludeSerials : []);
    /** @type {Set<string>} */
    const seen = new Set();
    /** @type {typeof entries} */
    const out = [];
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const serial = String(entry?.serialNumber || '').trim();
      if (!serial) continue;
      if (seen.has(serial) || exclude.has(serial)) continue;
      if (inventoryApi && typeof inventoryApi.isSerialReserved === 'function' && inventoryApi.isSerialReserved(serial)) {
        continue;
      }
      const grams = parseEntryWeightGrams(entry);
      if (!Number.isFinite(grams) || grams <= 0 || grams === Number.POSITIVE_INFINITY) continue;
      seen.add(serial);
      out.push(entry);
    }
    out.sort((a, b) => {
      const weightDiff = parseEntryWeightGrams(a) - parseEntryWeightGrams(b);
      if (weightDiff !== 0) return weightDiff;
      return String(a.serialNumber || '').localeCompare(String(b.serialNumber || ''));
    });
    return out;
  }

  /**
   * Effective qty ceiling: min(WMS inventory attribute, currently selectable option count).
   * @param {HTMLElement} row
   * @param {HTMLInputElement} [input]
   * @returns {number | null}
   */
  function getRowSelectableMax(row, input) {
    const inventoryMax = getRowQtyMax(row, input);
    const sku = getRowSku(row);
    if (!sku) return inventoryMax;
    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]');
    const exclude =
      detailsRoot instanceof HTMLElement
        ? collectSelectedSerialsOutsideRow(detailsRoot, row)
        : new Set();
    const optionMax = getSelectableWeightEntriesForSku(sku, exclude).length;
    const shopifyQty = getRowShopifyQty(row);
    const weightStatus = String(row.dataset.wmsStatus || '');
    const weightsPending =
      weightStatus === '' ||
      weightStatus === 'loading' ||
      row.dataset.qvmInventoryPending === 'true';
    const storefrontStockRow =
      row.classList.contains('premium-pdp-matrix__variant') ||
      row.classList.contains('premium-pdp__variant-row') ||
      row.classList.contains('quick-view-chain-matrix__variant') ||
      row.classList.contains('quick-view-size-selector__row');
    // While products-batch is still loading, Shopify qty is the stepper max.
    // An empty weight list must not collapse an in-stock SKU to qty 0.
    if (storefrontStockRow && shopifyQty != null && shopifyQty > 0 && weightsPending) {
      return shopifyQty;
    }
    if (inventoryMax == null) return optionMax;
    return Math.min(inventoryMax, optionMax);
  }

  /**
   * Centralized quantity → weight reconciliation for a variant row.
   * Keeps quantity === selected serial count for valid SKU rows.
   * @param {HTMLElement} row
   * @param {number} requestedQty
   * @param {{ silent?: boolean, skipNotify?: boolean }} [opts]
   * @returns {number}
   */
  function reconcileRowQuantitySelection(row, requestedQty, opts = {}) {
    const input = getRowQtyInput(row);
    if (!(row instanceof HTMLElement) || !input) return 0;

    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]');
    const panel =
      (detailsRoot instanceof HTMLElement &&
        detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel], [data-quick-view-details-panel]')) ||
      null;

    const finish = (qty) => {
      // Keep all row UI synchronized (min weight / Coming Soon) because one row’s
      // selected serials reduce selectable options for other rows.
      if (detailsRoot instanceof HTMLElement) {
        detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((r) => {
          if (!(r instanceof HTMLElement)) return;
          syncRowMinusState(r);
          syncRowWeightActionLabel(r);
        });
      } else {
        syncRowMinusState(row);
      }
      if (panel instanceof HTMLElement) syncBulkMap(panel, row);
      if (detailsRoot instanceof HTMLElement) {
        updateAddSelectedState(detailsRoot);
        updateDetailsSummary(detailsRoot);
        if (!opts.skipNotify) notifyQtyUpdated(detailsRoot);
      }
      return qty;
    };

    if (!isRowAvailable(row) && getRowCartBaselineQty(row) <= 0) {
      writeQty(input, 0, { silent: true });
      clearRowWeight(row);
      return finish(0);
    }
    // Allow editing carted / all-in-cart rows even when variantAvailable is false.
    // Previously this branch force-restored cart and blocked qty/weight changes.
    if (input.disabled && getRowCartBaselineQty(row) <= 0 && !isRowAvailable(row)) {
      writeQty(input, 0, { silent: true });
      clearRowWeight(row);
      return finish(0);
    }
    if (input.disabled && (isRowAvailable(row) || getRowCartBaselineQty(row) > 0)) {
      input.disabled = false;
      input.removeAttribute('aria-disabled');
    }

    let next = Number.isFinite(requestedQty) ? Math.max(0, Math.floor(requestedQty)) : 0;
    const cartBaseline = getRowCartBaselineQty(row);
    if (next !== cartBaseline) markRowSelectionDirty(row);
    // Allow reducing below cart baseline so Edit Weight can remove cart units.
    const sku = getRowSku(row);

    if (!sku) {
      const max = getRowQtyMax(row, input);
      if (max != null) next = Math.min(next, max);
      writeQty(input, next, { silent: true });
      return finish(next);
    }

    const exclude =
      detailsRoot instanceof HTMLElement
        ? collectSelectedSerialsOutsideRow(detailsRoot, row)
        : new Set();
    const selectable = getSelectableWeightEntriesForSku(sku, exclude);
    const selectableMax = getRowSelectableMax(row, input);
    // Cap at cart baseline + remaining selectable stock. Allow going below cart baseline when editing.
    const addableCap =
      selectableMax != null
        ? Math.max(0, selectableMax)
        : Math.max(0, selectable.length);
    next = Math.min(next, cartBaseline + addableCap);

    if (next <= 0) {
      writeQty(input, 0, { silent: true });
      clearRowWeight(row);
      return finish(0);
    }

    const baselineSerials = String(row.dataset.cartBaselineSerials || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const baselineWeights = String(row.dataset.cartBaselineWeights || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const availableBySerial = new Map();
    selectable.forEach((entry) => {
      const serial = String(entry.serialNumber || '').trim();
      if (serial) availableBySerial.set(serial, entry);
    });

    /** @type {typeof selectable} */
    let kept = [];
    // Prefer currently selected serials first (supports reducing/changing cart units).
    String(row.dataset.serialNumbersValue || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((serial) => {
        if (kept.length >= next) return;
        if (kept.some((k) => String(k.serialNumber || '').trim() === serial)) return;
        const entry = availableBySerial.get(serial);
        if (entry) {
          kept.push(entry);
          return;
        }
        const baselineIndex = baselineSerials.indexOf(serial);
        if (baselineIndex < 0) return;
        const label = baselineWeights[baselineIndex] || baselineWeights[0] || serial;
        kept.push({
          id: serial,
          label,
          serialNumber: serial,
          fields: [{ label: 'Weight', value: label }],
        });
      });

    baselineSerials.forEach((serial, index) => {
      if (kept.length >= next) return;
      if (kept.some((k) => String(k.serialNumber || '').trim() === serial)) return;
      const entry = availableBySerial.get(serial);
      if (entry) {
        kept.push(entry);
        return;
      }
      const label = baselineWeights[index] || baselineWeights[0] || serial;
      kept.push({
        id: serial,
        label,
        serialNumber: serial,
        fields: [{ label: 'Weight', value: label }],
      });
    });

    if (kept.length > next) {
      kept = kept.slice(0, next);
    }

    if (kept.length < next) {
      const keptSerials = new Set(kept.map((k) => String(k.serialNumber || '').trim()).filter(Boolean));
      const remaining = selectable.filter((entry) => {
        const serial = String(entry.serialNumber || '').trim();
        return serial && !keptSerials.has(serial);
      });
      for (let i = 0; i < remaining.length && kept.length < next; i += 1) {
        kept.push(remaining[i]);
      }
    }

    next = kept.length;
    writeQty(input, next, { silent: true });

    if (next <= 0) {
      clearRowWeight(row);
      return finish(0);
    }

    setRowWeightValue(
      row,
      kept.map((entry) => entry.label).join(', '),
      kept.map((entry) => String(entry.serialNumber || '').trim()).join(', ')
    );
    return finish(next);
  }

  /**
   * Absolute qty ceiling for a row = cart baseline + remaining selectable stock.
   * variantInventoryQty is cart-aware remaining stock only — never treat it as total display max.
   * @param {HTMLElement} row
   * @param {HTMLInputElement} [input]
   * @returns {number | null}
   */
  function getRowAbsoluteQtyMax(row, input) {
    const remaining = getRowQtyMax(row, input);
    const cartBaseline = getRowCartBaselineQty(row);
    if (remaining == null) return null;
    return Math.max(0, remaining) + Math.max(0, cartBaseline);
  }

  /** @param {HTMLInputElement} input @param {number} val @param {{ silent?: boolean }} [opts] */
  function writeQty(input, val, opts = {}) {
    let next = Math.max(0, Number.isFinite(val) ? Math.floor(val) : 0);
    const row = input.closest('[data-details-variant-row]');
    if (row instanceof HTMLElement) {
      // Allow cart baseline + remaining WMS qty (do not clamp cart items away).
      const absoluteMax = getRowAbsoluteQtyMax(row, input);
      if (absoluteMax != null) next = Math.min(next, absoluteMax);
    }
    const asString = String(next);
    if (input.value === asString) return;
    input.value = asString;
    if (!opts.silent) {
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  /** @param {string} weightStr */
  function parseWeightGrams(weightStr) {
    if (!weightStr) return 0;
    return weightStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .reduce((sum, part) => {
        const match = part.match(/([\d.]+)/);
        if (!match) return sum;
        const n = parseFloat(match[1]);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0);
  }

  /** @param {number} grams */
  function formatWeightTotal(grams) {
    if (!Number.isFinite(grams) || grams <= 0) return '0.00g';
    return grams.toFixed(2) + 'g';
  }

  /** @param {string} label */
  function formatWeightLabelForDisplay(label) {
    const text = String(label || '').trim();
    if (!text) return text;
    const match = text.match(/(-?\d+(?:\.\d+)?)(?:\s*([a-zA-Z]+))?/);
    if (!match) return text;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return text;
    const rawUnit = (match[2] || 'g').trim();
    const unit = rawUnit.toLowerCase();
    const grams = unit === 'kg' ? value * 1000 : value;
    if (!Number.isFinite(grams)) return text;
    return `${grams.toFixed(2)}g`;
  }

  /** @param {number} cents */
  function formatMoney(cents) {
    if (!Number.isFinite(cents) || cents <= 0) return '—';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /** @param {number} grams */
  function formatGramValue(grams) {
    if (!Number.isFinite(grams) || grams <= 0) return '—';
    return grams.toFixed(2) + 'g';
  }

  /** @param {string[]} values */
  function sortMatrixValues(values) {
    const list = values.slice();
    list.sort((a, b) => {
      const aNum = parseFloat(String(a).replace(/[^0-9.]+/g, ''));
      const bNum = parseFloat(String(b).replace(/[^0-9.]+/g, ''));
      const aHasNum = Number.isFinite(aNum);
      const bHasNum = Number.isFinite(bNum);
      if (aHasNum && bHasNum && aNum !== bNum) return aNum - bNum;
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }

  /** @param {HTMLElement} detailsRoot */
  function parseDetailsProductJson(detailsRoot) {
    const script = detailsRoot.querySelector('[data-plp-product-json]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @returns {Record<string, unknown>[]}
   */
  function readLinkedProductsJson(detailsRoot) {
    const script = detailsRoot.querySelector('[data-linked-products-json]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return [];
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * Include custom.linked_products variants in the order-sheet matrix product.
   * @param {HTMLElement} detailsRoot
   * @param {Record<string, unknown>} product
   * @returns {Record<string, unknown>}
   */
  function withLinkedFamily(detailsRoot, product) {
    const api = /** @type {any} */ (window).AoneLinkedProducts;
    if (!api || typeof api.mergeFamily !== 'function' || !product) return product;
    if (product.__linkedFamilyMerged === true) return product;
    const linked = readLinkedProductsJson(detailsRoot);
    if (!linked.length) return product;
    const merged = api.mergeFamily(product, linked);
    if (!merged || merged === product || merged.__linkedFamilyMerged !== true) return product;
    const script = detailsRoot.querySelector('[data-plp-product-json]');
    if (script) {
      try {
        script.textContent = JSON.stringify(merged);
      } catch (_error) {
        /* in-memory product is still merged */
      }
    }
    return /** @type {Record<string, unknown>} */ (merged);
  }

  /** @param {unknown} source */
  function getImageSrc(source) {
    if (!source) return '';
    if (typeof source === 'string') return source.trim();
    if (typeof source !== 'object') return '';
    const obj = /** @type {Record<string, unknown>} */ (source);
    const direct =
      normalizeString(obj.src) ||
      normalizeString(obj.url) ||
      normalizeString(obj.image) ||
      normalizeString(obj.featured_image);
    if (direct) return direct;
    if (obj.preview_image && typeof obj.preview_image === 'object') {
      const preview = /** @type {Record<string, unknown>} */ (obj.preview_image);
      return normalizeString(preview.src) || normalizeString(preview.url);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   * @param {number} variantId
   */
  function getVariantImageSrc(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !Number.isFinite(variantId)) return '';
    const variant =
      /** @type {Record<string, unknown> | undefined} */ (
        product.variants.find((entry) => Number(entry && entry.id) === variantId)
      ) || null;
    if (!variant) return '';

    const direct =
      getImageSrc(variant.featured_image) ||
      getImageSrc(variant.featured_media) ||
      getImageSrc(variant.image) ||
      getImageSrc(variant.preview_image);
    if (direct) return direct;

    const variantImageId = Number(variant.featured_image_id || variant.image_id);
    if (Number.isFinite(variantImageId) && Array.isArray(product.media)) {
      const mediaMatch = /** @type {Record<string, unknown> | undefined} */ (
        product.media.find((m) => Number(m && m.id) === variantImageId)
      );
      const mediaSrc = getImageSrc(mediaMatch);
      if (mediaSrc) return mediaSrc;
    }

    return '';
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   */
  function getProductFallbackImageSrc(product) {
    if (!product || typeof product !== 'object') return '';
    const direct =
      getImageSrc(product.featured_image) || getImageSrc(product.image) || getImageSrc(product.preview_image);
    if (direct) return direct;
    if (Array.isArray(product.media) && product.media.length) {
      const mediaSrc = getImageSrc(product.media[0]);
      if (mediaSrc) return mediaSrc;
    }
    if (Array.isArray(product.images) && product.images.length) {
      const imageSrc = getImageSrc(product.images[0]);
      if (imageSrc) return imageSrc;
    }
    return '';
  }

  /** @param {unknown} value */
  function normalizeString(value) {
    return value == null ? '' : String(value).trim();
  }

  /** @param {unknown} value */
  function normalizeKey(value) {
    return normalizeString(value).toLowerCase();
  }

  /**
   * Material / karat option names must never be used as the WIDTH (MM) axis.
   * @param {unknown} key
   */
  function isMaterialAxisKey(key) {
    const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    return /(material|karat|carat|metal|purity|jewellery|jewelry)/.test(normalized);
  }

  /**
   * @param {unknown} key
   */
  function isWidthAxisKey(key) {
    const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    if (isMaterialAxisKey(normalized)) return false;
    return /(width|thickness|gauge|\bmm\b|diameter|\bdia\b)/.test(normalized);
  }

  /**
   * @param {unknown} key
   */
  function isLengthAxisKey(key) {
    const normalized = normalizeKey(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
    return /(length|inch|inches|\blen\b)/.test(normalized);
  }

  /**
   * @param {unknown} value
   */
  function isKaratLikeValue(value) {
    return /^\d{1,2}\s*k(?:t)?$/i.test(normalizeString(value));
  }

  /**
   * Display width as the numeric mm value (10mm → 10).
   * @param {unknown} value
   */
  function normalizeWidthLabel(value) {
    const raw = normalizeString(value);
    if (!raw || raw === '—') return raw || '—';
    if (isKaratLikeValue(raw)) return raw;
    const mmMatch = raw.match(/^(\d+(?:\.\d+)?)\s*mm$/i);
    if (mmMatch) return mmMatch[1];
    return raw;
  }

  /**
   * Read fixed product width from title/handle/tags when Width is not a variant option.
   * @param {Record<string, unknown>} product
   * @returns {string}
   */
  function extractWidthFromProduct(product) {
    if (!product || typeof product !== 'object') return '';
    const texts = [];
    if (product.title) texts.push(String(product.title));
    if (product.handle) texts.push(String(product.handle).replace(/[-_]+/g, ' '));
    if (Array.isArray(product.tags)) {
      product.tags.forEach(function (tag) {
        if (tag != null && String(tag).trim()) texts.push(String(tag));
      });
    }
    for (let i = 0; i < texts.length; i += 1) {
      const match = texts[i].match(/(\d+(?:\.\d+)?)\s*mm\b/i);
      if (match) return normalizeWidthLabel(match[1]);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {number} idx
   * @returns {string[]}
   */
  function getOptionSampleValues(product, idx) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const values = [];
    const seen = Object.create(null);
    for (let i = 0; i < variants.length; i += 1) {
      const variant = variants[i];
      if (!variant || typeof variant !== 'object') continue;
      const value = getVariantOptionValue(/** @type {Record<string, unknown>} */ (variant), idx);
      if (!value || seen[value]) continue;
      seen[value] = true;
      values.push(value);
      if (values.length >= 8) break;
    }
    return values;
  }

  /**
   * @param {Record<string, unknown>} product
   * @param {number} idx
   * @returns {string}
   */
  function getOptionName(product, idx) {
    const opts = Array.isArray(product.options) ? product.options : [];
    const opt = opts[idx];
    if (typeof opt === 'string') return opt;
    if (opt && typeof opt === 'object' && 'name' in opt) {
      return normalizeString(opt.name);
    }
    return '';
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {number} idx
   * @returns {string}
   */
  function getVariantOptionValue(variant, idx) {
    const direct = variant['option' + String(idx + 1)];
    const directStr = normalizeString(direct);
    if (directStr) return directStr;
    const arr = Array.isArray(variant.options) ? variant.options : [];
    return normalizeString(arr[idx]);
  }

  /**
   * Keep first mapped variant stable.
   * Do not prefer Shopify variant.available — WMS owns cell availability by SKU.
   * @param {Record<string, unknown> | undefined} current
   * @param {Record<string, unknown>} next
   * @returns {Record<string, unknown>}
   */
  function chooseMatrixVariant(current, next) {
    if (!current) return next;
    return current;
  }

  /**
   * @param {Record<string, unknown>} product
   * @returns {{ widthIdx: number, lengthIdx: number, fixedWidth: string }}
   */
  function resolveChainAxes(product) {
    const maxOptions = Math.min(3, Array.isArray(product.options) ? product.options.length : 0);
    let widthIdx = -1;
    let lengthIdx = -1;

    for (let i = 0; i < maxOptions; i += 1) {
      const key = normalizeKey(getOptionName(product, i));
      if (widthIdx < 0 && isWidthAxisKey(key)) widthIdx = i;
      if (lengthIdx < 0 && isLengthAxisKey(key)) lengthIdx = i;
    }

    // Never fall back to material/karat (e.g. "14KT") as the WIDTH axis.
    if (widthIdx < 0) {
      for (let i = 0; i < maxOptions; i += 1) {
        if (i === lengthIdx) continue;
        if (isMaterialAxisKey(normalizeKey(getOptionName(product, i)))) continue;
        const samples = getOptionSampleValues(product, i);
        if (!samples.length) continue;
        if (samples.every(isKaratLikeValue)) continue;
        const widthLikeCount = samples.filter(function (value) {
          const raw = normalizeString(value);
          return /^\d+(?:\.\d+)?(?:\s*mm)?$/i.test(raw) && !isKaratLikeValue(raw);
        }).length;
        if (widthLikeCount > 0) {
          widthIdx = i;
          break;
        }
      }
    }

    if (lengthIdx < 0) {
      for (let i = 0; i < maxOptions; i += 1) {
        if (i === widthIdx) continue;
        if (isMaterialAxisKey(normalizeKey(getOptionName(product, i)))) continue;
        lengthIdx = i;
        break;
      }
    }

    if (lengthIdx < 0) {
      if (maxOptions > 1) lengthIdx = widthIdx === 0 ? 1 : 0;
      else if (maxOptions === 1 && widthIdx !== 0) lengthIdx = 0;
      else lengthIdx = widthIdx;
    }
    if (lengthIdx === widthIdx && maxOptions > 1) {
      lengthIdx = widthIdx === 0 ? 1 : 0;
    }

    const fixedWidth = widthIdx < 0 ? extractWidthFromProduct(product) : '';
    return { widthIdx, lengthIdx, fixedWidth };
  }

  /**
   * @param {Record<string, unknown>} variant
   * @returns {number}
   */
  function getVariantWeightGrams(variant) {
    const raw = Number(variant.weight);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    const unit = normalizeKey(variant.weight_unit);
    if (unit === 'kg') return raw * 1000;
    return raw;
  }

  /**
   * @param {Record<string, Record<string, Record<string, unknown>>>} map
   * @param {string[]} widths
   * @param {string[]} lengths
   */
  function countMatrixVariants(map, widths, lengths) {
    let count = 0;
    for (const width of widths) {
      for (const length of lengths) {
        if (map[width]?.[length]) count += 1;
      }
    }
    return count;
  }

  /** @param {number} count */
  function formatMatrixVariantCount(count) {
    const total = Math.max(0, Math.floor(Number(count) || 0));
    return `${total} ${total === 1 ? 'Variant' : 'Variants'}`;
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {'in-stock'|'low-stock'|'sold-out'|string} stockLevel
   */
  function getMatrixAvailableQuantity(variant, stockLevel) {
    if (stockLevel !== 'in-stock' && stockLevel !== 'low-stock') return null;
    const qty = Number(variant.inventory_quantity);
    if (!Number.isFinite(qty)) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[chain-matrix] missing inventory_quantity for variant', variant.id);
      }
      return null;
    }
    const normalized = Math.max(0, Math.floor(qty));
    if (normalized <= 0) return null;
    return normalized;
  }

  /**
   * @param {'in-stock'|'low-stock'|'sold-out'|string} stockLevel
   * @param {Record<string, unknown>} variant
   */
  function buildMatrixAvailabilityHtml(stockLevel, variant) {
    const availableQty = getMatrixAvailableQuantity(variant, stockLevel);
    if (availableQty === null) return '';
    const modClass =
      stockLevel === 'low-stock'
        ? 'order-sheet-details-chain-matrix__availability--low-stock'
        : 'order-sheet-details-chain-matrix__availability--in-stock';
    return `<p class="order-sheet-details-chain-matrix__availability bulk-matrix-availability ${modClass}">Available: ${availableQty}</p>`;
  }

  /**
   * @param {HTMLElement} matrixHost
   * @param {number} count
   */
  function updateMatrixVariantCountLabel(matrixHost, count) {
    const countEl = matrixHost.querySelector('[data-chain-matrix-variant-count]');
    if (!(countEl instanceof HTMLElement)) return;
    countEl.textContent = formatMatrixVariantCount(count);
    countEl.removeAttribute('aria-hidden');
  }

  /**
   * @param {HTMLElement} scrollEl
   */
  function syncChainMatrixStickyHeaderHeights(scrollEl) {
    if (!(scrollEl instanceof HTMLElement)) return;
    const groupHeader = scrollEl.querySelector(
      '.order-sheet-details-chain-matrix__length-group, .quick-view-chain-matrix__length-group'
    );
    const subHeader = scrollEl.querySelector(
      '.order-sheet-details-chain-matrix__length, .quick-view-chain-matrix__length'
    );
    if (groupHeader instanceof HTMLElement) {
      const mainHeight = Math.ceil(groupHeader.getBoundingClientRect().height);
      if (mainHeight >= 24 && mainHeight <= 80) {
        scrollEl.style.setProperty('--matrix-main-header-height', `${mainHeight}px`);
      } else {
        scrollEl.style.removeProperty('--matrix-main-header-height');
      }
    }
    if (subHeader instanceof HTMLElement) {
      const subHeight = Math.ceil(subHeader.getBoundingClientRect().height);
      if (subHeight >= 24 && subHeight <= 80) {
        scrollEl.style.setProperty('--matrix-sub-header-height', `${subHeight}px`);
      } else {
        scrollEl.style.removeProperty('--matrix-sub-header-height');
      }
    }
  }

  /**
   * @param {HTMLElement} scrollEl
   */
  function bindChainMatrixStickyHeaderSync(scrollEl) {
    if (!(scrollEl instanceof HTMLElement)) return;
    syncChainMatrixStickyHeaderHeights(scrollEl);
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @returns {boolean}
   */
  function buildChainMatrix(detailsRoot) {
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    if (!(matrixHost instanceof HTMLElement)) return false;
    if (matrixHost.dataset.matrixBuilt === 'true') return true;

    const parsedProduct = parseDetailsProductJson(detailsRoot);
    if (!parsedProduct) return false;
    const product = withLinkedFamily(detailsRoot, parsedProduct);
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return false;

    matrixHost.dataset.productType = normalizeString(product.product_type || product.type);

    const { widthIdx, lengthIdx, fixedWidth } = resolveChainAxes(product);
    const widthsSet = new Set();
    const lengthsSet = new Set();
    /** @type {Record<string, Record<string, Record<string, unknown>>>} */
    const map = Object.create(null);

    for (const variant of product.variants) {
      if (!variant || typeof variant !== 'object') continue;
      const widthVal = normalizeWidthLabel(
        widthIdx >= 0 ? getVariantOptionValue(variant, widthIdx) || '—' : fixedWidth || '—'
      );
      const lengthVal = getVariantOptionValue(variant, lengthIdx) || '—';
      widthsSet.add(widthVal);
      lengthsSet.add(lengthVal);
      if (!map[widthVal]) map[widthVal] = Object.create(null);
      map[widthVal][lengthVal] = chooseMatrixVariant(
        /** @type {Record<string, unknown> | undefined} */ (map[widthVal][lengthVal]),
        variant
      );
    }

    const widths = sortMatrixValues(Array.from(widthsSet));
    const lengths = sortMatrixValues(Array.from(lengthsSet));
    if (!widths.length || !lengths.length) return false;

    const head = matrixHost.querySelector('[data-chain-matrix-head]');
    const body = matrixHost.querySelector('[data-chain-matrix-body]');
    const table = matrixHost.querySelector('.order-sheet-details-chain-matrix__table');
    if (!(head instanceof HTMLElement) || !(body instanceof HTMLElement)) return false;

    if (table instanceof HTMLTableElement) {
      let colgroup = table.querySelector('colgroup');
      if (!colgroup) {
        colgroup = document.createElement('colgroup');
        table.insertBefore(colgroup, table.firstChild);
      }
      colgroup.innerHTML =
        '<col class="order-sheet-details-chain-matrix__col-axis">' +
        `<col span="${lengths.length}">`;
    }

    const lowThreshold = Number(matrixHost.dataset.lowStockThreshold || 5) || 5;
    const weightPlaceholder = matrixHost.dataset.weightPlaceholder || 'Select Weight';
    const stockAvailableLabel = matrixHost.dataset.stockAvailableLabel || 'In Stock';
    const stockSoldOutLabel = 'Out of Stock';
    const stockLowLabel = matrixHost.dataset.stockLowLabel || 'Low Stock';
    const productId = normalizeString(detailsRoot.dataset.productId);

    let headHtml =
      '<tr><th scope="col" rowspan="2" class="order-sheet-details-chain-matrix__corner order-sheet-details-chain-matrix__corner-row1">' +
      '<span class="order-sheet-details-chain-matrix__corner-label">WIDTH (MM)</span></th>' +
      `<th scope="col" colspan="${lengths.length}" class="order-sheet-details-chain-matrix__length-group">Length (inches)</th></tr><tr>`;
    for (const length of lengths) {
      headHtml += `<th scope="col" class="order-sheet-details-chain-matrix__length">${escapeHtml(length)}</th>`;
    }
    headHtml += '</tr>';
    head.innerHTML = headHtml;

    let bodyHtml = '';
    for (const width of widths) {
      bodyHtml += `<tr><th scope="row" class="order-sheet-details-chain-matrix__width">${escapeHtml(width)}</th>`;
      for (const length of lengths) {
        const variant = map[width]?.[length];
        if (!variant) {
          bodyHtml += `<td class="order-sheet-details-chain-matrix__cell order-sheet-details-chain-matrix__cell--missing"><div class="order-sheet-details-chain-matrix__cell-empty" aria-hidden="true"></div></td>`;
          continue;
        }

        const variantId = Number(variant.id);
        const available = false;
        const sku = normalizeString(variant.sku);
        const priceCents = Number(variant.price) || 0;
        const shopifyQty = resolveVariantShopifyQty(variant);
        const stockLevel = shopifyQty > 0 ? 'in-stock' : 'sold-out';
        const stockText = formatShopifyStockLabel(shopifyQty);
        const disabledAttr = ' disabled';
        const rowClass = ' order-sheet-details-chain-matrix__variant--sold-out';
        const qtyId = `PlpChainQty-${productId}-${variantId}`;
        const availabilityHtml = '';
        const invQty = 0;
        const maxAttr = ' max="0" data-variant-inventory-qty="0"';

        bodyHtml += `
          <td class="order-sheet-details-chain-matrix__cell order-sheet-details-chain-matrix__cell--loading">
            <div
              class="order-sheet-details-chain-matrix__variant${rowClass}"
              data-details-variant-row
              data-variant-id="${variantId}"
              data-source-product-id="${escapeHtml(normalizeString(variant.product_id || variant.productId || product.id))}"
              data-variant-sku="${escapeHtml(sku)}"
              data-variant-price-cents="${priceCents}"
              data-variant-available="${available ? 'true' : 'false'}"
              data-wms-status="loading"
              data-shopify-inventory-qty="${shopifyQty}"
              data-matrix-width="${escapeHtml(width)}"
              data-matrix-length="${escapeHtml(length)}" data-variant-inventory-qty="${invQty}"
            >
              <p class="order-sheet-details-chain-matrix__stock-badge-wrap">
                <span class="order-sheet-details-chain-matrix__stock-badge order-sheet-details-chain-matrix__stock-badge--${stockLevel}">
                  ${escapeHtml(stockText)}
                </span>
              </p>
              ${availabilityHtml}
              <div class="order-sheet-details-qty quantity-control" role="group" aria-label="Quantity for ${escapeHtml(width)} x ${escapeHtml(length)}">
                <button type="button" class="order-sheet-details-qty__btn" data-details-qty-minus aria-label="Decrease quantity"${disabledAttr}>−</button>
                <input
                  type="number"
                  id="${escapeHtml(qtyId)}"
                  class="order-sheet-details-qty__input"
                  value="0"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  data-details-qty-input
                  aria-label="Quantity for ${escapeHtml(width)} x ${escapeHtml(length)}"
                  ${disabledAttr}${maxAttr}
                >
                <button type="button" class="order-sheet-details-qty__btn" data-details-qty-plus aria-label="Increase quantity"${disabledAttr}>+</button>
              </div>
              <div class="order-sheet-details-chain-matrix__weight-wrap">
                ${
                  sku
                    ? `<button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-chain-matrix__weight-btn" data-details-weight-open aria-haspopup="dialog" data-matrix-width="${escapeHtml(width)}" data-matrix-length="${escapeHtml(length)}"${disabledAttr}><span data-details-weight-label>${escapeHtml(weightPlaceholder)}</span></button>`
                    : `<button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-chain-matrix__weight-btn" disabled><span class="order-sheet-details-chain-matrix__weight-na">Variant Not Available</span></button>`
                }
              </div>
            </div>
          </td>
        `;
      }
      bodyHtml += '</tr>';
    }

    body.innerHTML = bodyHtml;
    updateMatrixVariantCountLabel(matrixHost, countMatrixVariants(map, widths, lengths));
    matrixHost.dataset.matrixBuilt = 'true';
    const scrollEl = matrixHost.querySelector('.order-sheet-details-chain-matrix__scroll');
    if (scrollEl instanceof HTMLElement) {
      requestAnimationFrame(() => bindChainMatrixStickyHeaderSync(scrollEl));
    }
    return true;
  }

  /** @param {HTMLElement} detailsRoot */
  function notifyQtyUpdated(detailsRoot) {
    detailsRoot.dispatchEvent(new CustomEvent('plp-details-qty-update', { bubbles: true }));
  }

  /** @param {HTMLElement} row */
  function isRowAvailable(row) {
    return row.dataset.variantAvailable === 'true';
  }

  /** @param {HTMLElement} row */
  function getRowQtyInput(row) {
    return /** @type {HTMLInputElement | null} */ (row.querySelector('[data-details-qty-input]'));
  }

  /** @param {HTMLElement} row */
  function getRowSku(row) {
    return String(row.dataset.variantSku || '').trim();
  }

  /** @param {HTMLElement} row */
  function getRowCartBaselineQty(row) {
    const baseline = Number(row.dataset.cartBaselineQty || 0);
    return Number.isFinite(baseline) && baseline > 0 ? Math.floor(baseline) : 0;
  }

  /** @param {HTMLElement} row */
  function getRowAddableQty(row) {
    const qty = getRowQty(row);
    return Math.max(0, qty - getRowCartBaselineQty(row));
  }

  /** @param {string} value */
  function normalizeSerialCsv(value) {
    return String(value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .join(',');
  }

  /**
   * True when matrix draft qty/serials differ from the persisted cart baseline.
   * @param {HTMLElement} row
   */
  function isRowDraftDifferentFromCart(row) {
    if (!(row instanceof HTMLElement)) return false;
    const qty = getRowQty(row);
    const cartBaseline = getRowCartBaselineQty(row);
    if (qty !== cartBaseline) return true;
    if (qty <= 0) return false;
    return (
      normalizeSerialCsv(row.dataset.serialNumbersValue || '') !==
      normalizeSerialCsv(row.dataset.cartBaselineSerials || '')
    );
  }

  /**
   * Draft row is ready to show in footer totals (full selection, not delta-only).
   * @param {HTMLElement} row
   */
  function isRowDraftVisibleInSummary(row) {
    if (!(row instanceof HTMLElement)) return false;
    const qty = getRowQty(row);
    if (qty <= 0) return false;
    const available = isRowAvailable(row);
    const cartBaseline = getRowCartBaselineQty(row);
    if (!available && cartBaseline <= 0) return false;
    const sku = getRowSku(row);
    if (!sku && !isRowQtyModalOnly(row)) return true;
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    if (!weight || !serialNumbers) return false;
    const weightCount = weight.split(',').map((s) => s.trim()).filter(Boolean).length;
    const serialCount = serialNumbers.split(',').map((s) => s.trim()).filter(Boolean).length;
    return weightCount === qty && serialCount === qty;
  }

  /**
   * Draft row can be committed by Add Selected To Cart (delta add, edit, or clear).
   * @param {HTMLElement} row
   */
  function isRowDraftCommittable(row) {
    if (!(row instanceof HTMLElement)) return false;
    if (!isRowDraftDifferentFromCart(row)) return false;
    const qty = getRowQty(row);
    const cartBaseline = getRowCartBaselineQty(row);
    const available = isRowAvailable(row);
    if (!available && cartBaseline <= 0 && qty <= 0) return false;
    // Cleared draft for a carted SKU → commit removal.
    if (qty <= 0) return cartBaseline > 0 && row.dataset.selectionDirty === 'true';
    if (!available && cartBaseline <= 0) return false;
    const sku = getRowSku(row);
    if (!sku && !isRowQtyModalOnly(row)) return true;
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    if (!weight || !serialNumbers) return false;
    const weightCount = weight.split(',').map((s) => s.trim()).filter(Boolean).length;
    const serialCount = serialNumbers.split(',').map((s) => s.trim()).filter(Boolean).length;
    return weightCount === qty && serialCount === qty;
  }

  /**
   * Full draft payload for one variant row (not cart-delta only).
   * @param {HTMLElement} row
   * @returns {{ qty: number, weight: string, serialNumbers: string }}
   */
  function getRowFullDraftPayload(row) {
    const qty = getRowQty(row);
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    if (qty <= 0) return { qty: 0, weight: '', serialNumbers: '' };
    const weightParts = weight.split(',').map((s) => s.trim()).filter(Boolean);
    const serialParts = serialNumbers.split(',').map((s) => s.trim()).filter(Boolean);
    return {
      qty,
      weight: weightParts.join(', '),
      serialNumbers: serialParts.join(', '),
    };
  }

  /**
   * Resolve display labels for cart serials using raw WMS options when possible.
   * @param {string} sku
   * @param {string[]} serialNumbers
   * @param {string[]} fallbackLabels
   * @returns {string[]}
   */
  function resolveCartWeightLabelsForSku(sku, serialNumbers, fallbackLabels) {
    if (!serialNumbers.length) return [];
    const availabilityApi = getWmsAvailabilityApi();
    /** @type {Map<string, string>} */
    const labelBySerial = new Map();

    if (availabilityApi && typeof availabilityApi.getInventoryBySku === 'function') {
      const inventory = availabilityApi.getInventoryBySku(sku);
      const rawOptions = Array.isArray(inventory?.rawAvailableOptions)
        ? inventory.rawAvailableOptions
        : Array.isArray(inventory?.availableOptions)
          ? inventory.availableOptions
          : [];
      extractWeightEntries(rawOptions, { skipReservedFilter: true }).forEach((entry) => {
        const serial = String(entry.serialNumber || '').trim();
        const label = String(entry.label || '').trim();
        if (serial && label) labelBySerial.set(serial, label);
      });
    }

    if (!labelBySerial.size) {
      const shared = getSharedWeightStore();
      const raw = shared?.rawBySku?.[String(sku || '').trim().toLowerCase()];
      if (raw != null) {
        extractWeightEntries(raw, { skipReservedFilter: true }).forEach((entry) => {
          const serial = String(entry.serialNumber || '').trim();
          const label = String(entry.label || '').trim();
          if (serial && label) labelBySerial.set(serial, label);
        });
      }
    }

    return serialNumbers.map((serial, index) => {
      const fromWms = labelBySerial.get(serial);
      if (fromWms) return fromWms;
      if (fallbackLabels[index]) return fallbackLabels[index];
      return fallbackLabels[0] || serial;
    });
  }

  /**
   * Paint cart qty + selected weights onto a variant row (Quick View / Order Sheet / PDP).
   * @param {HTMLElement} row
   * @param {{ force?: boolean }} [options]
   */
  function applyCartSelectionToRow(row, options = {}) {
    if (!(row instanceof HTMLElement)) return;
    if (!options.force && row.dataset.selectionDirty === 'true') return;

    const sku = getRowSku(row);
    if (!sku) return;

    const availabilityApi = getWmsAvailabilityApi();
    if (!availabilityApi || typeof availabilityApi.getCartSkuSummary !== 'function') return;
    if (typeof availabilityApi.isCartStateReady === 'function' && !availabilityApi.isCartStateReady()) {
      return;
    }

    const summary = availabilityApi.getCartSkuSummary(sku);
    const cartQty = Math.max(0, Math.floor(Number(summary.quantity) || 0));

    row.dataset.cartBaselineQty = String(cartQty);
    if (cartQty <= 0) {
      delete row.dataset.cartBaselineSerials;
      delete row.dataset.cartBaselineWeights;
      // No cart units: reset display qty/weights so leaked modal drafts cannot survive.
      const input = getRowQtyInput(row);
      if (input) writeQty(input, 0, { silent: true });
      const displayInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
      );
      if (displayInput) displayInput.value = '0';
      clearRowWeight(row);
      syncRowMinusState(row);
      syncWeightButtonState(row);
      syncRowWeightActionLabel(row);
      return;
    }

    const serialNumbers = Array.isArray(summary.serialNumbers)
      ? summary.serialNumbers.map((s) => String(s || '').trim()).filter(Boolean)
      : [];
    const fallbackLabels = Array.isArray(summary.weightLabels)
      ? summary.weightLabels.map((s) => String(s || '').trim()).filter(Boolean)
      : [];
    const weightLabels = serialNumbers.length
      ? resolveCartWeightLabelsForSku(sku, serialNumbers, fallbackLabels)
      : fallbackLabels;

    // Prefer real cart unit count when serial/weight lists are incomplete.
    const unitCount = Math.max(cartQty, serialNumbers.length, weightLabels.length);
    row.dataset.cartBaselineQty = String(unitCount);
    row.dataset.cartBaselineSerials = serialNumbers.join(', ');
    row.dataset.cartBaselineWeights = weightLabels.join(', ');

    const input = getRowQtyInput(row);
    if (input) {
      const remaining = getRowQtyMax(row, input);
      const absoluteMax = remaining == null ? unitCount : unitCount + Math.max(0, remaining);
      input.max = String(absoluteMax);
      writeQty(input, unitCount, { silent: true });
      const displayInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
      );
      if (displayInput) displayInput.value = String(unitCount);
    }

    if (serialNumbers.length && weightLabels.length) {
      setRowWeightValue(row, weightLabels.join(', '), serialNumbers.join(', '));
    } else if (weightLabels.length) {
      setRowWeightValue(row, weightLabels.join(', '));
    }

    syncRowMinusState(row);
    syncWeightButtonState(row);
    syncRowWeightActionLabel(row);

    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]') ||
      row.closest('[data-premium-pdp-details]') ||
      null;
    if (detailsRoot instanceof HTMLElement) {
      updateAddSelectedState(detailsRoot);
      updateDetailsSummary(detailsRoot);
    }
  }

  /** @param {HTMLElement} row */
  function markRowSelectionDirty(row) {
    if (!(row instanceof HTMLElement)) return;
    row.dataset.selectionDirty = 'true';
  }

  /** @param {HTMLElement} detailsRoot */
  function clearRowSelectionDirtyFlags(detailsRoot) {
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      delete row.dataset.selectionDirty;
    });
  }

  /** @returns {any} */
  function getWmsAvailabilityApi() {
    return (
      /** @type {any} */ (window).WMSInventory ||
      /** @type {any} */ (window).AOneWmsAvailability ||
      null
    );
  }

  /**
   * True when Shopify inventory was stamped. Missing qty is still loading, not zero.
   * @param {Record<string, unknown>|null|undefined} variant
   * @returns {boolean}
   */
  function isVariantShopifyQtyKnown(variant) {
    if (!variant || typeof variant !== 'object') return false;
    if (Number.isFinite(Number(/** @type {any} */ (variant).shopify_inventory_quantity))) return true;
    return Number.isFinite(Number(/** @type {any} */ (variant).inventory_quantity));
  }

  /**
   * Shopify inventory qty for a variant object (never WMS).
   * @param {Record<string, unknown>|null|undefined} variant
   * @returns {number}
   */
  function resolveVariantShopifyQty(variant) {
    if (!variant || typeof variant !== 'object') return 0;
    const preserved = Number(/** @type {any} */ (variant).shopify_inventory_quantity);
    if (Number.isFinite(preserved) && preserved >= 0) return Math.floor(preserved);
    const qty = Number(/** @type {any} */ (variant).inventory_quantity);
    if (Number.isFinite(qty) && qty >= 0) return Math.floor(qty);
    return 0;
  }

  /**
   * Read stamped Shopify inventory from a details/matrix row.
   * @param {HTMLElement} row
   * @returns {number|null}
   */
  function getRowShopifyQty(row) {
    const attr = row.getAttribute('data-shopify-inventory-qty');
    if (attr != null && Number.isFinite(Number(attr))) {
      return Math.max(0, Math.floor(Number(attr)));
    }
    return null;
  }

  /**
   * Stock badge / status label from Shopify qty.
   * @param {number} shopifyQty
   * @param {{ numericOnly?: boolean }} [options]
   * @returns {string}
   */
  function formatShopifyStockLabel(shopifyQty, options = {}) {
    const qty = Math.max(0, Math.floor(Number(shopifyQty) || 0));
    if (options.numericOnly) return qty > 0 ? String(qty) : '0';
    return qty > 0 ? `IN-STOCK: ${qty}` : 'COMING SOON';
  }

  /**
   * Stamp data-shopify-inventory-qty on rows from embedded product JSON when missing.
   * @param {HTMLElement} detailsRoot
   */
  function stampShopifyInventoryQtyOnDetailsRows(detailsRoot) {
    const product = parseDetailsProductJson(detailsRoot);
    if (!product || !Array.isArray(product.variants)) return;
    /** @type {Record<string, any>} */
    const byId = Object.create(null);
    product.variants.forEach((variant) => {
      if (!variant || variant.id == null) return;
      byId[String(variant.id)] = variant;
    });
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.getAttribute('data-shopify-inventory-qty') != null) return;
      const variant = byId[String(node.dataset.variantId || '')];
      if (!variant) return;
      node.setAttribute('data-shopify-inventory-qty', String(resolveVariantShopifyQty(variant)));
    });
  }

  /**
   * Fetch Shopify inventory map via product.qty view.
   * @param {string} handle
   * @returns {Promise<Record<string, number>>}
   */
  async function fetchShopifyStockMapForHandle(handle) {
    const safeHandle = String(handle || '').trim();
    if (!safeHandle) return {};
    const url = `/products/${encodeURIComponent(safeHandle)}?view=qty`;
    const res = await fetch(url, { headers: { Accept: 'application/json, text/plain, */*' } });
    if (!res.ok) throw new Error(`[OrderSheet] qty view HTTP ${res.status} for handle="${safeHandle}"`);
    const rawText = await res.text();
    const parsed = JSON.parse(rawText);
    /** @type {Record<string, number>} */
    const map = {};
    if (!parsed || typeof parsed !== 'object') return map;
    Object.keys(parsed).forEach((key) => {
      if (String(key).charAt(0) === '_') return;
      const n = Number(parsed[key]);
      if (!Number.isFinite(n)) return;
      map[String(key)] = Math.max(0, Math.floor(n));
    });
    return map;
  }

  /**
   * Merge Shopify qty onto product.variants (product.js often omits inventory_quantity).
   * @param {Record<string, any>} product
   * @param {Record<string, number>} stockMap
   */
  function applyShopifyStockMapToProduct(product, stockMap) {
    if (!product || !Array.isArray(product.variants) || !stockMap) return;
    product.variants.forEach((variant) => {
      if (!variant || variant.id == null) return;
      const qty = stockMap[String(variant.id)];
      if (!Number.isFinite(Number(qty))) return;
      const safe = Math.max(0, Math.floor(Number(qty)));
      variant.inventory_quantity = safe;
      variant.shopify_inventory_quantity = safe;
    });
  }

  /**
   * @param {HTMLElement} row
   * @param {'loading'|'available'|'coming-soon'|'all-in-cart'|'error'} status
   * @param {number} count Display / remaining qty depending on status
   * @param {{ remainingQty?: number, rawQty?: number }} [options]
   */
  function setRowWmsStatus(row, status, count, options = {}) {
    const safeCount = Number.isFinite(Number(count)) ? Math.max(0, Math.floor(Number(count))) : 0;
    const remainingQty =
      status === 'all-in-cart'
        ? Math.max(
            0,
            Math.floor(
              Number(options.remainingQty != null ? options.remainingQty : 0) || 0
            )
          )
        : safeCount;
    const rawQty =
      status === 'all-in-cart'
        ? safeCount
        : Math.max(
            0,
            Math.floor(Number(options.rawQty != null ? options.rawQty : safeCount) || 0)
          );
    row.dataset.wmsStatus = status;
    // Remaining addable units drive qty max; raw warehouse qty is for display only.
    row.dataset.variantInventoryQty = String(remainingQty);
    row.dataset.wmsRawAvailableQty = String(rawQty);
    row.dataset.variantAvailable = status === 'available' ? 'true' : 'false';
    row.dataset.cellState = status;
    // All-in-cart stays interactive (cart units) — do not paint as permanently sold out.
    const treatAsInactive = status !== 'available' && status !== 'all-in-cart';
    row.classList.toggle('is-unavailable', treatAsInactive);
    row.classList.toggle('is-sold-out', treatAsInactive);
    row.classList.toggle('order-sheet-details-variant-row--sold-out', treatAsInactive);

    const availabilityApi = getWmsAvailabilityApi();
    const stockBadge = row.querySelector(
      '.order-sheet-details-variant-table__stock-badge, .order-sheet-details-chain-matrix__stock-badge'
    );
    const matrixStatus = row.querySelector('.premium-pdp-matrix__status');
    const qvStockQty = row.querySelector('.quick-view-stock-qty');
    const qvMatrixStatusText = row.querySelector('.quick-view-chain-matrix__status-text');
    const pdpStock = row.querySelector('[data-premium-pdp-stock]');
    const detailsRoot = row.closest('[data-plp-expanded-details]');
    const shopifyQty = getRowShopifyQty(row);
    const useShopifyStock = shopifyQty != null;
    const storefrontStockRow =
      row.classList.contains('premium-pdp-matrix__variant') ||
      row.classList.contains('premium-pdp__variant-row') ||
      row.classList.contains('quick-view-chain-matrix__variant') ||
      row.classList.contains('quick-view-size-selector__row');
    if (storefrontStockRow && shopifyQty != null && shopifyQty > 0) {
      row.dataset.variantAvailable = 'true';
      row.dataset.variantInventoryQty = String(shopifyQty);
      if (row.dataset.wmsStatus === 'coming-soon') {
        row.dataset.wmsStatus = 'available';
        row.dataset.cellState = 'available';
      }
      row.classList.remove(
        'is-unavailable',
        'is-sold-out',
        'order-sheet-details-variant-row--sold-out',
        'quick-view-chain-matrix__variant--sold-out',
        'quick-view-size-selector__row--sold-out'
      );
    }
    // Premium PDP / merged matrix cells always keep Coming Soon text beside the tip —
    // never the bare "0" used by non-chain expanded Order Sheet rows.
    const isPremiumMatrixRow = row.classList.contains('premium-pdp-matrix__variant');
    const expandedNonChainNumericStock = Boolean(
      detailsRoot instanceof HTMLElement &&
        detailsRoot.dataset.isChainProduct !== 'true' &&
        !isPremiumMatrixRow
    );
    // Qty / IN-STOCK / COMING SOON is Shopify-owned when data-shopify-inventory-qty is set.
    // WMS still owns weight + selectable remaining (variantInventoryQty / interactivity).
    const shopifyStockLabel = useShopifyStock
      ? formatShopifyStockLabel(shopifyQty, { numericOnly: expandedNonChainNumericStock })
      : null;
    const statusLabel = useShopifyStock
      ? shopifyStockLabel
      : status === 'loading'
        ? null
        : status === 'available'
          ? expandedNonChainNumericStock
            ? String(safeCount)
            : `IN-STOCK: ${safeCount}`
          : status === 'all-in-cart'
            ? expandedNonChainNumericStock
              ? '0'
              : 'IN-STOCK: 0'
            : status === 'coming-soon'
              ? expandedNonChainNumericStock
                ? '0'
                : 'Coming Soon'
              : expandedNonChainNumericStock
                ? '0'
                : 'UNAVAILABLE';

    const paintStockReady = useShopifyStock || status !== 'loading';
    const shopifyInStock = useShopifyStock ? shopifyQty > 0 : status === 'available' || status === 'all-in-cart';

    if (qvStockQty instanceof HTMLElement && paintStockReady) {
      qvStockQty.textContent = useShopifyStock
        ? String(shopifyQty)
        : status === 'available'
          ? String(safeCount)
          : status === 'all-in-cart'
            ? '0'
            : '—';
    }
    if (pdpStock instanceof HTMLElement && paintStockReady) {
      pdpStock.textContent = useShopifyStock
        ? shopifyQty > 0
          ? String(shopifyQty)
          : 'COMING SOON'
        : status === 'available'
          ? String(safeCount)
          : status === 'all-in-cart'
            ? '0'
            : 'COMING SOON';
      pdpStock.removeAttribute('aria-busy');
    }
    if (qvMatrixStatusText instanceof HTMLElement && statusLabel) {
      qvMatrixStatusText.textContent = statusLabel;
      if (useShopifyStock) {
        const statusHost = qvMatrixStatusText.closest('.quick-view-chain-matrix__status');
        if (statusHost instanceof HTMLElement) {
          statusHost.classList.toggle(
            'quick-view-chain-matrix__status--available',
            shopifyQty > 0
          );
          statusHost.classList.toggle(
            'quick-view-chain-matrix__status--coming-soon',
            shopifyQty <= 0
          );
          statusHost.classList.remove('quick-view-chain-matrix__status--low-stock');
        }
      }
    }

    if (stockBadge instanceof HTMLElement) {
      stockBadge.classList.remove(
        'order-sheet-details-variant-table__stock-badge--available',
        'order-sheet-details-variant-table__stock-badge--sold-out',
        'order-sheet-details-chain-matrix__stock-badge--in-stock',
        'order-sheet-details-chain-matrix__stock-badge--low-stock',
        'order-sheet-details-chain-matrix__stock-badge--sold-out'
      );
      if (!paintStockReady) {
        stockBadge.innerHTML =
          availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
            ? availabilityApi.getInventorySkeletonMarkup('status')
            : '<span class="aone-inventory-skeleton aone-inventory-skeleton--status" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
        stockBadge.setAttribute('aria-busy', 'true');
      } else if (shopifyInStock) {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--available');
      } else {
        stockBadge.removeAttribute('aria-busy');
        stockBadge.textContent = statusLabel;
        stockBadge.classList.add('order-sheet-details-variant-table__stock-badge--sold-out');
      }
    }

    if (matrixStatus instanceof HTMLElement) {
      const showComingSoonBesideTip = useShopifyStock
        ? shopifyQty <= 0
        : status === 'coming-soon' || status === 'error';
      const qvStatusMod = showComingSoonBesideTip
        ? 'coming-soon'
        : status === 'low-stock'
          ? 'low-stock'
          : !paintStockReady
            ? 'loading'
            : 'available';
      const matrixStatusKey = useShopifyStock
        ? shopifyQty > 0
          ? 'available'
          : 'coming-soon'
        : status;
      // Keep premium-pdp-matrix__status for PDP hooks; add Quick View classes for shared chrome.
      matrixStatus.className =
        `premium-pdp-matrix__status bulk-order-matrix__status quick-view-chain-matrix__status ` +
        `premium-pdp-matrix__status--${matrixStatusKey} quick-view-chain-matrix__status--${qvStatusMod}`;
      if (!paintStockReady) {
        matrixStatus.innerHTML =
          availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
            ? availabilityApi.getInventorySkeletonMarkup('status')
            : '<span class="aone-inventory-skeleton aone-inventory-skeleton--status" aria-hidden="true"></span><span class="visually-hidden">Loading availability</span>';
        matrixStatus.setAttribute('aria-busy', 'true');
      } else {
        matrixStatus.removeAttribute('aria-busy');
        const desktopLabel = showComingSoonBesideTip
          ? 'COMING SOON'
          : statusLabel || formatShopifyStockLabel(shopifyQty || 0);
        // Prefer variant SKU on the row; fall back to data-sku used by PDP matrix cells.
        const sku = String(row.dataset.variantSku || row.dataset.sku || '').trim();
        const safeSku = escapeHtml(sku);
        // Reuse Quick View status markup; keep data-premium-pdp-sku-tip for PDP SKU copy.
        const tipButton =
          `<button type="button" class="quick-view-chain-matrix__info-btn bulk-order-matrix__info-btn"` +
          (sku
            ? ` data-premium-pdp-sku-tip data-tip-sku="${safeSku}" aria-label="View SKU: ${safeSku}" aria-expanded="false"`
            : ` aria-label="View variant SKU" disabled`) +
          `>` +
          `<svg class="quick-view-chain-matrix__info-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.25"></circle><circle cx="8" cy="5.25" r="0.9" fill="currentColor"></circle><path d="M8 7.35v3.9" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"></path></svg>` +
          `</button>`;
        matrixStatus.innerHTML =
          `<span class="quick-view-chain-matrix__status-text">${escapeHtml(
            String(desktopLabel || '')
          )}</span>` + tipButton;
      }
    }

    applyResolvedZeroStockPresentation(row, status);
  }

  /**
   * Order Sheet expanded SKU table only.
   * Hide Shopify qty <= 0 rows and point SKUS / Variants at the visible rows.
   * Does not touch matrix cells or the product.variants array.
   * @param {HTMLElement} detailsRoot
   */
  function syncOrderSheetInStockTable(detailsRoot) {
    if (!(detailsRoot instanceof HTMLElement)) return;
    if (detailsRoot.querySelector('[data-chain-matrix], [data-premium-pdp-matrix]')) return;
    const table = detailsRoot.querySelector('.order-sheet-details-variant-table');
    if (!(table instanceof HTMLTableElement)) return;
    const tbody = table.tBodies[0];
    if (!(tbody instanceof HTMLElement)) return;

    const rows = Array.from(tbody.querySelectorAll('tr.order-sheet-details-variant-row'));
    let visible = 0;
    let pending = false;
    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const shopifyQty = getRowShopifyQty(row);
      if (shopifyQty == null) {
        pending = true;
        row.hidden = false;
        visible += 1;
        return;
      }
      if (shopifyQty <= 0) {
        row.hidden = true;
        return;
      }
      row.hidden = false;
      visible += 1;
    });

    if (!pending) {
      const countEl = detailsRoot.querySelector('.order-sheet-details-variant-table__sku-panel-count');
      if (countEl instanceof HTMLElement) countEl.textContent = `(${visible})`;
      detailsRoot.querySelectorAll('.expanded-product-meta__item').forEach((item) => {
        if (!(item instanceof HTMLElement)) return;
        const label = item.querySelector('.expanded-product-meta__label');
        if (!label || !/variants/i.test(label.textContent || '')) return;
        const value = item.querySelector('.expanded-product-meta__value');
        if (value instanceof HTMLElement) value.textContent = String(visible);
      });
    }

    let emptyRow = tbody.querySelector('[data-order-sheet-stock-empty]');
    const showEmpty = !pending && rows.length > 0 && visible === 0;
    if (showEmpty) {
      if (!(emptyRow instanceof HTMLTableRowElement)) {
        emptyRow = document.createElement('tr');
        emptyRow.setAttribute('data-order-sheet-stock-empty', '');
        const td = document.createElement('td');
        td.colSpan = table.querySelectorAll('thead th').length || 6;
        td.className = 'order-sheet-details-variant-table__empty';
        td.textContent = 'No variants currently in stock.';
        emptyRow.appendChild(td);
        tbody.appendChild(emptyRow);
      }
      emptyRow.hidden = false;
    } else if (emptyRow instanceof HTMLElement) {
      emptyRow.hidden = true;
    }
  }

  /**
   * Display-only. Does not remove variants from product data.
   * Coming Soon after resolve: hide normal PDP table rows, and strip matrix chrome.
   * Order Sheet detail rows with Shopify qty <= 0 are hidden the same way.
   * Loading / error / in-stock / all-in-cart stay as they are.
   * @param {HTMLElement} row
   * @param {string} status
   */
  function applyResolvedZeroStockPresentation(row, _status) {
    const isMatrix =
      row.classList.contains('premium-pdp-matrix__variant') ||
      row.classList.contains('quick-view-chain-matrix__variant');
    const isPdpTable = row.classList.contains('premium-pdp__variant-row');
    const isOrderSheetTable = row.classList.contains('order-sheet-details-variant-row');
    if (!isMatrix && !isPdpTable && !isOrderSheetTable) return;
    if (isOrderSheetTable && !isMatrix && !isPdpTable) {
      const detailsRoot = row.closest('[data-plp-expanded-details]');
      if (detailsRoot instanceof HTMLElement) syncOrderSheetInStockTable(detailsRoot);
      return;
    }
    const shopifyQty = getRowShopifyQty(row);
    // Missing Shopify qty is still loading — do not treat it as Coming Soon / zero.
    if (shopifyQty == null) return;
    const shopifyOut = shopifyQty <= 0;
    if (shopifyOut) {
      if (isMatrix) {
        row.classList.add('is-stock-empty');
        row.setAttribute('aria-hidden', 'true');
        row.querySelectorAll('button, input').forEach((control) => {
          if (control instanceof HTMLButtonElement || control instanceof HTMLInputElement) {
            control.disabled = true;
            control.tabIndex = -1;
          }
        });
      }
      if (isPdpTable) row.hidden = true;
      return;
    }
    if (isMatrix) {
      row.classList.remove('is-stock-empty');
      row.removeAttribute('aria-hidden');
    }
    if (isPdpTable) row.hidden = false;
  }

  /**
   * After live availability resolves, hide zero-stock PDP table rows and
   * show one neutral line when every SKU is out of stock.
   * @param {HTMLElement} detailsRoot
   */
  function syncPremiumPdpListEmptyState(detailsRoot) {
    if (!(detailsRoot instanceof HTMLElement)) return;
    const list = detailsRoot.querySelector('[data-premium-pdp-list]');
    if (!(list instanceof HTMLElement)) return;
    const tbody = list.querySelector('tbody');
    if (!(tbody instanceof HTMLElement)) return;
    const rows = Array.from(tbody.querySelectorAll('[data-details-variant-row]'));
    let visible = 0;
    let pending = false;
    rows.forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const shopifyQty = getRowShopifyQty(row);
      if (shopifyQty == null) {
        pending = true;
        row.hidden = false;
        visible += 1;
        return;
      }
      if (shopifyQty <= 0) {
        row.hidden = true;
        return;
      }
      row.hidden = false;
      visible += 1;
    });
    let emptyRow = tbody.querySelector('[data-premium-pdp-stock-empty]');
    const showEmpty = !pending && rows.length > 0 && visible === 0;
    if (showEmpty) {
      if (!(emptyRow instanceof HTMLTableRowElement)) {
        emptyRow = document.createElement('tr');
        emptyRow.setAttribute('data-premium-pdp-stock-empty', '');
        const td = document.createElement('td');
        td.colSpan = list.querySelectorAll('thead th').length || 6;
        td.className = 'premium-pdp__stock-empty';
        td.textContent = 'No variants currently in stock.';
        emptyRow.appendChild(td);
        tbody.appendChild(emptyRow);
      }
      emptyRow.hidden = false;
    } else if (emptyRow instanceof HTMLElement) {
      emptyRow.hidden = true;
    }
  }

  /** @param {HTMLElement} detailsRoot */
  async function hydrateDetailsWmsAvailability(detailsRoot) {
    const availabilityApi = getWmsAvailabilityApi();
    const productId = normalizeString(detailsRoot.dataset.productId);
    if (!availabilityApi || !productId) return;
    const familyProduct = parseDetailsProductJson(detailsRoot);
    const familyIds = Array.isArray(familyProduct && familyProduct.__linkedProductIds)
      ? familyProduct.__linkedProductIds.map((id) => normalizeString(id)).filter(Boolean)
      : [productId];
    const idsToLoad = familyIds.length ? familyIds : [productId];

    stampShopifyInventoryQtyOnDetailsRows(detailsRoot);

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      setRowWmsStatus(row, 'loading', 0);
      syncRowWeightActionLabel(row, { isLoading: true });
      syncRowMinusState(row);
      syncWeightButtonState(row);
    });
    updateAddSelectedState(detailsRoot);

    await availabilityApi.ensureProductsLoaded(idsToLoad);
    try {
      const forceCart =
        typeof availabilityApi.isCartStateReady === 'function'
          ? !availabilityApi.isCartStateReady()
          : true;
      await availabilityApi.ensureCartSerialsSynced?.(forceCart);
    } catch (_error) {
      /* continue — unresolved cart keeps rows in loading via getSkuAvailability */
    }
    const familyStates = idsToLoad.map((id) => availabilityApi.getProductAvailability?.(id));
    const familyStillLoading = familyStates.some(
      (state) => !state || state.status === 'loading' || state.status === 'idle' || state.loading
    );
    if (familyStillLoading) {
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        setRowWmsStatus(row, 'loading', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
        syncWeightButtonState(row);
      });
      updateAddSelectedState(detailsRoot);
      updateDetailsSummary(detailsRoot);
      syncPremiumPdpListEmptyState(detailsRoot);
      return;
    }
    const anyFamilyLoaded = familyStates.some((state) => state && state.status === 'loaded');
    if (!anyFamilyLoaded) {
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        setRowWmsStatus(row, 'error', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
      });
      updateAddSelectedState(detailsRoot);
      updateDetailsSummary(detailsRoot);
      syncPremiumPdpListEmptyState(detailsRoot);
      return;
    }

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const sku = getRowSku(row);
      if (!sku) {
        setRowWmsStatus(row, 'coming-soon', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
        syncRowMinusState(row);
        return;
      }
      const rowProductId = normalizeString(row.dataset.sourceProductId) || productId;
      const skuState = availabilityApi.getSkuAvailability?.(rowProductId, sku);
      if (!skuState || skuState.status === 'loading' || skuState.status === 'idle') {
        setRowWmsStatus(row, 'loading', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
      } else if (skuState.status !== 'loaded') {
        setRowWmsStatus(row, 'error', 0);
        const input = getRowQtyInput(row);
        if (input) writeQty(input, 0, { silent: true });
        clearRowWeight(row);
      } else {
        const rawCount = Math.max(0, Math.floor(Number(skuState.rawAvailableCount ?? skuState.count) || 0));
        const effectiveCount = Math.max(0, Math.floor(Number(skuState.count) || 0));
        const storefrontStatus =
          skuState.storefrontStatus ||
          (availabilityApi.getStorefrontStatus
            ? availabilityApi.getStorefrontStatus(rawCount, effectiveCount)
            : effectiveCount > 0
              ? 'AVAILABLE'
              : rawCount > 0
                ? 'ALL_IN_CART'
                : 'COMING_SOON');

        if (skuState.found === true && storefrontStatus === 'AVAILABLE' && effectiveCount > 0) {
          setRowWmsStatus(row, 'available', effectiveCount, { rawQty: rawCount });
          if (skuState.minWeight != null && Number(skuState.minWeight) > 0) {
            row.dataset.wmsMinWeight = String(skuState.minWeight);
          } else {
            delete row.dataset.wmsMinWeight;
          }
          if (Array.isArray(skuState.availableOptions) && skuState.availableOptions.length) {
            try {
              row.dataset.wmsOptionsJson = JSON.stringify(skuState.availableOptions);
            } catch (_error) {
              delete row.dataset.wmsOptionsJson;
            }
            const shared = getSharedWeightStore();
            const rawOptions = Array.isArray(skuState.rawAvailableOptions)
              ? skuState.rawAvailableOptions
              : skuState.availableOptions;
            if (shared?.rawBySku && Array.isArray(rawOptions) && rawOptions.length) {
              shared.rawBySku[sku.toLowerCase()] = {
                availableOptions: rawOptions,
                sku,
              };
            }
            const seeded = extractWeightEntries(rawOptions, { skipReservedFilter: true });
            if (seeded.length) setWeightCacheForSku(sku, { entries: seeded });
          } else {
            delete row.dataset.wmsOptionsJson;
          }
          const input = getRowQtyInput(row);
          if (input) {
            // max attribute is remaining WMS stock for addable units; absolute max is
            // applied after cart baseline is painted in applyCartSelectionToRow.
            input.max = String(effectiveCount);
            input.disabled = false;
            input.removeAttribute('aria-disabled');
          }
          const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
          if (plus) {
            plus.disabled = false;
            plus.removeAttribute('aria-disabled');
          }
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) {
            weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
            weightBtn.removeAttribute('aria-disabled');
          }
          syncRowWeightActionLabel(row);
        } else if (skuState.found === true && storefrontStatus === 'ALL_IN_CART') {
          setRowWmsStatus(row, 'all-in-cart', rawCount, { remainingQty: 0 });
          if (skuState.minWeight != null && Number(skuState.minWeight) > 0) {
            row.dataset.wmsMinWeight = String(skuState.minWeight);
          } else {
            delete row.dataset.wmsMinWeight;
          }
          const shared = getSharedWeightStore();
          const rawOptions = Array.isArray(skuState.rawAvailableOptions)
            ? skuState.rawAvailableOptions
            : Array.isArray(skuState.availableOptions)
              ? skuState.availableOptions
              : [];
          if (rawOptions.length) {
            try {
              row.dataset.wmsOptionsJson = JSON.stringify(rawOptions);
            } catch (_error) {
              delete row.dataset.wmsOptionsJson;
            }
            if (shared?.rawBySku) {
              shared.rawBySku[sku.toLowerCase()] = {
                availableOptions: rawOptions,
                sku,
              };
            }
            const seeded = extractWeightEntries(rawOptions, { skipReservedFilter: true });
            if (seeded.length) setWeightCacheForSku(sku, { entries: seeded });
          } else {
            delete row.dataset.wmsOptionsJson;
          }
          const input = getRowQtyInput(row);
          if (input) {
            input.max = '0';
            input.disabled = false;
            input.removeAttribute('aria-disabled');
          }
          const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
          if (plus) {
            plus.disabled = false;
            plus.removeAttribute('aria-disabled');
          }
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) {
            weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
            weightBtn.removeAttribute('aria-disabled');
          }
          // Do not wipe qty/weights here — applyCartSelectionToRow restores cart draft.
          syncRowWeightActionLabel(row);
        } else {
          setRowWmsStatus(row, 'coming-soon', 0);
          const input = getRowQtyInput(row);
          if (input) writeQty(input, 0, { silent: true });
          clearRowWeight(row);
          const weightBtn = row.querySelector('[data-details-weight-open]');
          if (weightBtn instanceof HTMLElement) weightBtn.setAttribute('data-weight-idle-label', 'Select Weight');
          syncRowWeightActionLabel(row);
        }
      }
      syncRowMinusState(row);
      syncWeightButtonState(row);
      if (skuState && skuState.status === 'loaded') applyCartSelectionToRow(row);
      // Re-sync after cart draft paint so Available Stock / Min Weight exclude selected serials.
      syncRowWeightActionLabel(row);
    });

    updateAddSelectedState(detailsRoot);
    updateDetailsSummary(detailsRoot);
    syncPremiumPdpListEmptyState(detailsRoot);
  }

  /** @param {HTMLElement} row */
  function getRowQty(row) {
    const input = getRowQtyInput(row);
    return input ? readQty(input) : 0;
  }

  /** @param {HTMLElement} row */
  function getWeightModalForRow(row) {
    const details = row.closest('[data-plp-expanded-details]');
    if (!(details instanceof HTMLElement)) return null;
    return getWeightModalState(details)?.modal ?? null;
  }

  /** @param {HTMLElement} row */
  function getWeightPlaceholderForRow(row) {
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    const idleLabel = btn?.getAttribute('data-weight-idle-label');
    // If we already computed a real idle label (e.g. "Min wt: 8.94g"), prefer it.
    if (idleLabel && idleLabel !== 'Select Weight' && idleLabel !== 'Edit Weight') return idleLabel;
    const availabilityApi = getWmsAvailabilityApi();
    if (availabilityApi && typeof availabilityApi.resolveWeightActionLabel === 'function') {
      const label = availabilityApi.resolveWeightActionLabel({
        quantity: 0,
        selectedSerialNumbers: '',
        isLoading: false,
        isAvailable: isRowAvailable(row),
      });
      if (label) return label;
    }
    const modal = getWeightModalForRow(row);
    return modal?.dataset.weightPlaceholder || 'Select Weight';
  }

  /**
   * Update SKU-table Min Weight cell from current selectable WMS options
   * (not selected cart weight / totalWeight÷qty).
   * @param {HTMLElement} row
   * @param {number | null | undefined} minWeightGrams
   * @param {string} [unit]
   */
  function syncRowMinWeightCell(row, minWeightGrams, unit) {
    const cells = row.querySelectorAll(
      '.order-sheet-details-variant-table__min-weight, .quick-view-variant-table__min-weight, [data-premium-pdp-min-weight]'
    );
    if (!cells.length) return;
    const grams = Number(minWeightGrams);
    /** @type {string} */
    let text = 'N/A';
    if (Number.isFinite(grams) && grams > 0) {
      const u = String(unit || 'g').trim() || 'g';
      const availabilityApi = getWmsAvailabilityApi();
      if (availabilityApi && typeof availabilityApi.formatMinimumWeightLabel === 'function') {
        const labeled = String(availabilityApi.formatMinimumWeightLabel(grams, { unit: u }) || '');
        const stripped = labeled.replace(/^Min\s*wt:\s*/i, '').trim();
        text = stripped || `${grams.toFixed(3).replace(/\.?0+$/, '')}${u}`;
      } else {
        text = `${grams.toFixed(3).replace(/\.?0+$/, '')}${u}`;
      }
    }
    cells.forEach((cell) => {
      if (cell instanceof HTMLElement) cell.textContent = text;
    });
  }

  /** @param {HTMLElement} row */
  function syncRowMinWeightLoadingCell(row) {
    const cells = row.querySelectorAll(
      '.order-sheet-details-variant-table__min-weight, .quick-view-variant-table__min-weight, [data-premium-pdp-min-weight]'
    );
    cells.forEach((cell) => {
      if (cell instanceof HTMLElement) cell.textContent = 'Checking...';
    });
  }

  /**
   * Available Stock display: Shopify qty when stamped; else WMS remaining selectable.
   * Does not mutate variantInventoryQty (qty max still uses cart-aware WMS remaining).
   * @param {HTMLElement} row
   * @param {number} remainingCount
   * @param {{ isComingSoon?: boolean }} [options]
   */
  function syncRowAvailableStockDisplay(row, remainingCount, options = {}) {
    const wmsStatus = String(row.dataset.wmsStatus || '');
    if (wmsStatus === 'loading' || row.dataset.qvmInventoryPending === 'true') {
      // Shopify stock can paint immediately while WMS weight is still loading.
      const shopifyWhileLoading = getRowShopifyQty(row);
      if (shopifyWhileLoading == null) return;
    }

    const shopifyQty = getRowShopifyQty(row);
    const detailsRoot = row.closest('[data-plp-expanded-details]');
    const isPremiumMatrixRow = row.classList.contains('premium-pdp-matrix__variant');
    const expandedNonChainNumericStock = Boolean(
      detailsRoot instanceof HTMLElement &&
        detailsRoot.dataset.isChainProduct !== 'true' &&
        !isPremiumMatrixRow
    );

    if (shopifyQty != null) {
      const shopifyLabel = formatShopifyStockLabel(shopifyQty, {
        numericOnly: expandedNonChainNumericStock,
      });
      const stockBadge = row.querySelector(
        '.order-sheet-details-variant-table__stock-badge, .order-sheet-details-chain-matrix__stock-badge'
      );
      if (stockBadge instanceof HTMLElement) {
        stockBadge.textContent = shopifyLabel;
        stockBadge.removeAttribute('aria-busy');
      }
      const qvStockQty = row.querySelector('.quick-view-stock-qty');
      if (qvStockQty instanceof HTMLElement) {
        qvStockQty.textContent = String(shopifyQty);
      }
      const pdpStock = row.querySelector('[data-premium-pdp-stock]');
      if (pdpStock instanceof HTMLElement) {
        pdpStock.textContent = shopifyQty > 0 ? String(shopifyQty) : 'COMING SOON';
        pdpStock.removeAttribute('aria-busy');
      }
      const matrixStatus = row.querySelector('.premium-pdp-matrix__status-label--desktop');
      const matrixCompact = row.querySelector('.premium-pdp-matrix__status-label--compact');
      if (matrixStatus instanceof HTMLElement) {
        matrixStatus.textContent =
          shopifyQty > 0 ? `IN-STOCK: ${shopifyQty}` : 'COMING SOON';
      }
      if (matrixCompact instanceof HTMLElement) {
        matrixCompact.textContent = shopifyQty > 0 ? `QTY: ${shopifyQty}` : 'COMING SOON';
      }
      const qvMatrixStatusText = row.querySelector(
        '.premium-pdp-matrix__status .quick-view-chain-matrix__status-text, .quick-view-chain-matrix__status-text'
      );
      if (qvMatrixStatusText instanceof HTMLElement) {
        qvMatrixStatusText.textContent =
          shopifyQty > 0 ? `IN-STOCK: ${shopifyQty}` : 'COMING SOON';
      }
      return;
    }

    const isComingSoon =
      options.isComingSoon === true || wmsStatus === 'coming-soon' || wmsStatus === 'error';
    const safeRemaining = Math.max(0, Math.floor(Number(remainingCount) || 0));

    const stockBadge = row.querySelector(
      '.order-sheet-details-variant-table__stock-badge, .order-sheet-details-chain-matrix__stock-badge'
    );
    if (stockBadge instanceof HTMLElement) {
      if (isComingSoon) {
        stockBadge.textContent = expandedNonChainNumericStock ? '0' : 'COMING SOON';
      } else if (expandedNonChainNumericStock) {
        stockBadge.textContent = String(safeRemaining);
      } else {
        stockBadge.textContent = `IN-STOCK: ${safeRemaining}`;
      }
    }

    const qvStockQty = row.querySelector('.quick-view-stock-qty');
    if (qvStockQty instanceof HTMLElement) {
      qvStockQty.textContent = isComingSoon ? '—' : String(safeRemaining);
    }

    const pdpStock = row.querySelector('[data-premium-pdp-stock]');
    if (pdpStock instanceof HTMLElement) {
      pdpStock.textContent = isComingSoon ? 'COMING SOON' : String(safeRemaining);
    }

    const matrixStatus = row.querySelector('.premium-pdp-matrix__status-label--desktop');
    const matrixCompact = row.querySelector('.premium-pdp-matrix__status-label--compact');
    if (matrixStatus instanceof HTMLElement && !isComingSoon) {
      matrixStatus.textContent = `IN-STOCK: ${safeRemaining}`;
    }
    if (matrixCompact instanceof HTMLElement && !isComingSoon) {
      matrixCompact.textContent = `QTY: ${safeRemaining}`;
    }

    // Shared Quick View / PDP matrix status text (single label inside the pink bar).
    const qvMatrixStatusText = row.querySelector(
      '.premium-pdp-matrix__status .quick-view-chain-matrix__status-text, .quick-view-chain-matrix__status-text'
    );
    if (qvMatrixStatusText instanceof HTMLElement) {
      if (isComingSoon) {
        qvMatrixStatusText.textContent = 'COMING SOON';
      } else {
        qvMatrixStatusText.textContent = `IN-STOCK: ${safeRemaining}`;
      }
    }
  }

  /**
   * Apply shared Select Weight / Edit Weight label (or skeleton while loading).
   * Also refreshes Available Stock + Min Weight from remaining selectable options
   * (excludes cart-reserved and draft-selected serials, including this row).
   * @param {HTMLElement} row
   * @param {{ isLoading?: boolean }} [options]
   */
  function syncRowWeightActionLabel(row, options = {}) {
    const labelEl = row.querySelector('[data-details-weight-label]');
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));

    const availabilityApi = getWmsAvailabilityApi();
    const selected = String(row.dataset.serialNumbersValue || '').trim();
    const isLoading =
      options.isLoading === true ||
      row.dataset.wmsStatus === 'loading' ||
      row.dataset.qvmInventoryPending === 'true';
    const isAvailable = isRowAvailable(row);
    const wmsStatus = String(row.dataset.wmsStatus || '');
    const shopifyQtyForLabel = getRowShopifyQty(row);
    const shopifyInStock = shopifyQtyForLabel != null && shopifyQtyForLabel > 0;
    const isComingSoon = !shopifyInStock && (wmsStatus === 'coming-soon' || wmsStatus === 'error');

    if (isLoading) {
      if (labelEl instanceof HTMLElement) {
        labelEl.innerHTML =
          availabilityApi && typeof availabilityApi.getInventorySkeletonMarkup === 'function'
            ? availabilityApi.getInventorySkeletonMarkup('weight-action')
            : '<span class="aone-inventory-skeleton aone-inventory-skeleton--weight-action" aria-hidden="true"></span>';
      }
      if (btn) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.setAttribute('data-weight-idle-label', '');
      }
      syncRowMinWeightLoadingCell(row);
      return;
    }

    const sku = getRowSku(row);
    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]') ||
      row.closest('[data-order-sheet-details]') ||
      null;
    const exclude =
      detailsRoot instanceof HTMLElement ? collectSelectedSerialsOutsideRow(detailsRoot, row) : new Set();
    // Remaining Available Stock / Min Weight exclude this row's draft selection too.
    // Qty max still uses getRowSelectableMax (other rows only) so selected units stay addable.
    String(selected)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((serial) => exclude.add(serial));

    const selectable = sku ? getSelectableWeightEntriesForSku(sku, exclude) : [];
    const remainingCount = selectable.length;
    const cartBaseline = getRowCartBaselineQty(row);
    const hasCartSelection = cartBaseline > 0 || Boolean(selected);
    const minEntry = selectable.length ? selectable[0] : null;
    const minWeight = minEntry ? parseEntryWeightGrams(minEntry) : null;
    const unit = minEntry && minEntry.unit ? String(minEntry.unit) : 'g';

    // Remaining-only min weight. When remaining is 0, show N/A — never fall back to
    // the original dataset/WMS min (0 remaining is valid, not "unknown").
    const displayMin =
      !isComingSoon && minWeight != null && Number.isFinite(minWeight) && minWeight > 0
        ? minWeight
        : null;
    syncRowMinWeightCell(row, displayMin, unit);
    syncRowAvailableStockDisplay(row, isComingSoon ? 0 : remainingCount, { isComingSoon });

    if (!(labelEl instanceof HTMLElement)) return;

    // Weight button still needs selectable options that include this row's current
    // selection so Edit Weight / qty caps stay correct.
    const excludeOthers =
      detailsRoot instanceof HTMLElement ? collectSelectedSerialsOutsideRow(detailsRoot, row) : new Set();
    const selectableForAction = sku ? getSelectableWeightEntriesForSku(sku, excludeOthers) : [];
    const effectiveQtyForAction = selectableForAction.length;

    const state =
      availabilityApi && typeof availabilityApi.resolveWeightButtonState === 'function'
        ? availabilityApi.resolveWeightButtonState({
            isLoading: false,
            isAvailable: isAvailable || hasCartSelection,
            effectiveAvailableQuantity: Math.max(effectiveQtyForAction, cartBaseline),
            minimumWeight: displayMin,
            minimumWeightLabel:
              displayMin != null && Number.isFinite(Number(displayMin)) && Number(displayMin) > 0
                ? availabilityApi.formatMinimumWeightLabel(displayMin, { unit })
                : null,
            selectedSerialNumbers: selected,
            unit,
          })
        : {
            state: 'minimum-weight',
            label:
              hasCartSelection || selected
                ? 'Edit weight'
                : !isAvailable || (effectiveQtyForAction <= 0 && !hasCartSelection)
                  ? 'Coming Soon'
                  : displayMin != null && Number.isFinite(Number(displayMin)) && Number(displayMin) > 0
                    ? `Min wt: ${Number(displayMin).toFixed(3).replace(/\.?0+$/, '')}${unit}`
                    : remainingCount <= 0 && (isAvailable || hasCartSelection)
                      ? 'Edit weight'
                      : 'Coming Soon',
            disabled: !(isAvailable || hasCartSelection) || (effectiveQtyForAction <= 0 && !hasCartSelection),
          };

    if (btn) {
      btn.removeAttribute('aria-busy');
      // Premium matrix: Coming Soon lives beside the tip icon — do not duplicate it on the weight button.
      const isPremiumMatrixRow = row.classList.contains('premium-pdp-matrix__variant');
      const rawWeightLabel =
        hasCartSelection || selected
          ? 'Edit weight'
          : isPremiumMatrixRow && isComingSoon
            ? 'Select Weight'
            : state.label || 'Coming Soon';
      const weightLabelText =
        shopifyInStock && /^coming soon$/i.test(String(rawWeightLabel).trim())
          ? 'Select Weight'
          : rawWeightLabel;
      btn.setAttribute('data-weight-idle-label', weightLabelText || 'Min wt: N/A');
      const disabled = Boolean(state.disabled) && !hasCartSelection;
      btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      const buttonLabel = String(weightLabelText || '').trim().toLowerCase();
      const isComingSoonState =
        !hasCartSelection &&
        !selected &&
        (buttonLabel === 'coming soon' || (disabled && !isAvailable && !isPremiumMatrixRow));
      btn.classList.toggle('order-sheet-details-variant-table__weight-btn--coming-soon', isComingSoonState);
      if (btn instanceof HTMLButtonElement) btn.disabled = disabled;
    }
    const resolvedWeightLabel =
      hasCartSelection || selected
        ? 'Edit weight'
        : row.classList.contains('premium-pdp-matrix__variant') && isComingSoon
          ? 'Select Weight'
          : state.label || 'Coming Soon';
    labelEl.textContent =
      shopifyInStock && /^coming soon$/i.test(String(resolvedWeightLabel).trim())
        ? 'Select Weight'
        : resolvedWeightLabel;
  }

  /** @param {HTMLElement} row */
  function clearRowWeight(row) {
    delete row.dataset.weightValue;
    delete row.dataset.serialNumbersValue;
    row.classList.remove('has-weight-selected');
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (btn) btn.classList.remove('is-confirmed');
    syncRowWeightActionLabel(row);
  }

  /** @param {HTMLElement} row */
  function syncWeightButtonState(row) {
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (!btn) return;
    const sku = getRowSku(row);
    const available = isRowAvailable(row);
    const loading = row.dataset.wmsStatus === 'loading' || row.dataset.qvmInventoryPending === 'true';

    const detailsRoot =
      row.closest('[data-plp-expanded-details]') ||
      row.closest('[data-quick-view-details]') ||
      row.closest('[data-order-sheet-details]') ||
      null;
    const exclude =
      detailsRoot instanceof HTMLElement ? collectSelectedSerialsOutsideRow(detailsRoot, row) : new Set();
    const selectable = sku ? getSelectableWeightEntriesForSku(sku, exclude) : [];
    const effectiveQty = selectable.length;
    const cartBaseline = getRowCartBaselineQty(row);

    const disabled = loading || !sku || (cartBaseline <= 0 && (!available || effectiveQty <= 0));
    btn.disabled = disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  /** @param {HTMLElement} row @param {string} value @param {string} [serialValue] */
  function setRowWeightValue(row, value, serialValue) {
    const trimmed = String(value || '').trim();
    const serialTrimmed = String(serialValue || '').trim();
    const btn = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));
    if (!trimmed) {
      clearRowWeight(row);
      return;
    }

    row.dataset.weightValue = trimmed;
    if (serialTrimmed) row.dataset.serialNumbersValue = serialTrimmed;
    else delete row.dataset.serialNumbersValue;
    row.classList.add('has-weight-selected');
    if (btn) btn.classList.add('is-confirmed');
    syncRowWeightActionLabel(row);
    syncWeightButtonState(row);
  }

  /** @param {HTMLElement} row */
  function isRowQtyModalOnly(row) {
    return !!row.querySelector('[data-details-qty-modal-only]');
  }

  /** @param {HTMLElement} row */
  function syncRowMinusState(row) {
    const input = getRowQtyInput(row);
    const displayInput = /** @type {HTMLInputElement | null} */ (
      row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
    );
    const minus = /** @type {HTMLButtonElement | null} */ (
      row.querySelector('[data-details-qty-minus], [data-qvm-qty-minus]')
    );
    const plus = /** @type {HTMLButtonElement | null} */ (
      row.querySelector('[data-details-qty-plus], [data-qvm-qty-plus]')
    );
    if (!input) return;
    if (displayInput) displayInput.value = input.value;

    const sku = getRowSku(row);
    const qty = readQty(input);
    const max = getRowSelectableMax(row, input);
    const cartBaseline = getRowCartBaselineQty(row);
    const addableMax = max != null && Number.isFinite(Number(max)) ? Number(max) : 0;
    const totalMax = cartBaseline + addableMax;
    const available = isRowAvailable(row) && (Boolean(sku) || !isRowQtyModalOnly(row));
    const canInteract =
      available ||
      cartBaseline > 0 ||
      String(row.dataset.wmsStatus || '') === 'all-in-cart';

    // Modal-only (SKU/weight) rows: keep hidden qty input enabled when WMS stock > 0 so
    // +/- can call reconcileRowQuantitySelection (same pattern as Quick View).
    if (isRowQtyModalOnly(row)) {
      input.disabled = !canInteract;
      input.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');
      input.readOnly = true;
      if (displayInput) {
        displayInput.disabled = !canInteract;
        displayInput.readOnly = false;
        displayInput.removeAttribute('tabindex');
        displayInput.setAttribute('aria-disabled', !canInteract ? 'true' : 'false');
        displayInput.value = input.value;
      }
      if (minus) {
        minus.disabled = !canInteract || qty <= 0;
        minus.removeAttribute('aria-hidden');
      }
      if (plus) {
        plus.disabled = !canInteract || (totalMax > 0 ? qty >= totalMax : !available);
        plus.removeAttribute('aria-hidden');
      }
      syncWeightButtonState(row);
      return;
    }

    if (!minus) return;

    input.disabled = !canInteract;
    input.setAttribute('aria-disabled', input.disabled ? 'true' : 'false');

    minus.disabled = !canInteract || qty <= 0;
    if (plus) {
      plus.disabled = !canInteract || (totalMax > 0 ? qty >= totalMax : !available);
    }
    syncWeightButtonState(row);
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLElement} row
   */
  function syncBulkMap(panel, row) {
    const host = panel.closest('.plp-view-host');
    const bulk = window.plpBulkSelection;
    if (!(host instanceof HTMLElement) || !bulk) return;
    const variantId = Number(row.dataset.variantId);
    if (!Number.isFinite(variantId)) return;
    const input = getRowQtyInput(row);
    const qty = input ? readQty(input) : 0;
    const sku = getRowSku(row);
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    bulk.setQty(host, variantId, qty, { sku, weight, serialNumbers });
  }

  /** @param {HTMLElement} detailsRoot */
  function resetVariantTable(detailsRoot) {
    const panel = detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    const host = panel?.closest('.plp-view-host');
    if (host instanceof HTMLElement && window.plpBulkSelection) {
      window.plpBulkSelection.clearProductVariants(host, detailsRoot);
    }

    closeWeightModal(detailsRoot);

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const input = getRowQtyInput(row);
      if (input) {
        writeQty(input, 0, { silent: true });
        input.disabled = !isRowAvailable(row);
      }
      const plus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-plus]'));
      const minus = /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-qty-minus]'));
      if (plus) plus.disabled = !isRowAvailable(row);
      if (minus) minus.disabled = true;
      clearRowWeight(row);
      syncWeightButtonState(row);
    });
    updateAddSelectedState(detailsRoot);
    updateDetailsSummary(detailsRoot);
    notifyQtyUpdated(detailsRoot);
  }

  /** @param {HTMLElement} detailsRoot */
  function hasSelectedQty(detailsRoot) {
    return Array.from(detailsRoot.querySelectorAll('[data-details-variant-row]')).some((row) => {
      if (!(row instanceof HTMLElement)) return false;
      return isRowDraftCommittable(row);
    });
  }

  /** @param {HTMLElement} detailsRoot */
  function updateDetailsSummary(detailsRoot) {
    let selectedVariants = 0;
    let totalQty = 0;
    let totalWeight = 0;
    let totalPriceCents = 0;

    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      if (!isRowDraftVisibleInSummary(row)) return;
      const qty = getRowQty(row);
      selectedVariants += 1;
      totalQty += qty;
      const weightRaw = String(row.dataset.weightValue || '').trim();
      totalWeight += parseWeightGrams(weightRaw);
      const priceCents = Number(row.dataset.variantPriceCents || 0);
      if (Number.isFinite(priceCents) && priceCents > 0) {
        totalPriceCents += priceCents * qty;
      }
    });

    const roundedWeight = formatWeightTotal(totalWeight);
    detailsRoot.querySelectorAll('[data-details-selected-count]').forEach((el) => {
      el.textContent = String(selectedVariants);
    });
    detailsRoot.querySelectorAll('[data-details-total-qty], [data-summary-total-qty]').forEach((el) => {
      el.textContent = String(totalQty);
    });
    detailsRoot.querySelectorAll('[data-details-total-weight], [data-summary-total-weight]').forEach((el) => {
      el.textContent = roundedWeight;
    });
    detailsRoot.querySelectorAll('[data-details-estimated-price]').forEach((el) => {
      el.textContent = formatMoney(totalPriceCents);
    });

    const hasMeaningfulTotals = totalQty > 0 || totalWeight > 0;
    const panel = detailsRoot.closest('[data-plp-details-panel], [data-order-sheet-details-panel]');
    const row = panel?.closest(ITEM_SEL)?.querySelector(ROW_SEL);
    // Non-chain Order Sheet: keep totals in expanded footer only — never on the product-card header.
    const isNonChainExpanded =
      detailsRoot.getAttribute('data-is-chain-product') !== 'true' &&
      (detailsRoot.hasAttribute('data-plp-expanded-details') ||
        (row instanceof HTMLElement && row.getAttribute('data-is-chain-product') !== 'true'));
    if (row instanceof HTMLElement) {
      if (isNonChainExpanded) {
        row.classList.remove('has-order-summary');
        row.querySelectorAll('[data-row-total-qty]').forEach((el) => {
          el.textContent = '0';
        });
        row.querySelectorAll('[data-row-total-weight]').forEach((el) => {
          el.textContent = '0.00g';
        });
      } else {
        row.querySelectorAll('[data-row-total-qty]').forEach((el) => {
          el.textContent = String(totalQty);
        });
        row.querySelectorAll('[data-row-total-weight]').forEach((el) => {
          el.textContent = roundedWeight;
        });
        row.classList.toggle('has-order-summary', hasMeaningfulTotals);
      }
    }
  }

  /** @param {HTMLElement} detailsRoot */
  function updateAddSelectedState(detailsRoot) {
    const btn = /** @type {HTMLButtonElement | null} */ (detailsRoot.querySelector('[data-details-add-selected]'));
    if (!btn || btn.dataset.addState === 'loading') return;
    btn.disabled = !hasSelectedQty(detailsRoot);
  }

  /** @param {HTMLButtonElement} btn @param {'idle'|'loading'|'success'} state */
  function setAddSelectedButtonState(btn, state) {
    const textEl = btn.querySelector('[data-details-add-selected-text]');
    const label = btn.dataset.addLabel || 'ADD SELECTED TO CART';
    const loading = btn.dataset.addLoading || 'ADDING…';
    const success = btn.dataset.addSuccess || 'ADDED';

    btn.dataset.addState = state;
    if (state === 'loading') {
      btn.disabled = true;
      if (textEl) textEl.textContent = loading;
      return;
    }
    if (state === 'success') {
      btn.disabled = true;
      if (textEl) textEl.textContent = success;
      window.setTimeout(() => {
        btn.dataset.addState = 'idle';
        if (textEl) textEl.textContent = label;
        const details = btn.closest('[data-plp-expanded-details]');
        if (details instanceof HTMLElement) updateAddSelectedState(details);
      }, 1800);
      return;
    }
    btn.dataset.addState = 'idle';
    if (textEl) textEl.textContent = label;
  }

  /** @param {HTMLElement} row */
  function getRowNewSelectionPayload(row) {
    // Legacy delta helper kept for callers that still need addable-only slices.
    const qty = getRowQty(row);
    const cartBaseline = getRowCartBaselineQty(row);
    const addableQty = Math.max(0, qty - cartBaseline);
    const weight = String(row.dataset.weightValue || '').trim();
    const serialNumbers = String(row.dataset.serialNumbersValue || '').trim();
    if (addableQty <= 0) {
      return { qty: 0, weight: '', serialNumbers: '' };
    }
    const weightParts = weight.split(',').map((s) => s.trim()).filter(Boolean);
    const serialParts = serialNumbers.split(',').map((s) => s.trim()).filter(Boolean);
    return {
      qty: addableQty,
      weight: weightParts.slice(cartBaseline).join(', '),
      serialNumbers: serialParts.slice(cartBaseline).join(', '),
    };
  }

  /**
   * Build Shopify cart payloads from draft rows that differ from cart.
   * Uses the FULL draft selection so Edit Weight changes replace correctly.
   * @param {HTMLElement} detailsRoot
   */
  function collectDraftCommitItems(detailsRoot) {
    /** @type {{ id: number, quantity: number, sku: string, properties?: Record<string, string>, keepSerials: string[], addableQty: number }[]} */
    const items = [];
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !isRowDraftCommittable(row)) return;
      const payload = getRowFullDraftPayload(row);
      const id = Number(row.dataset.variantId);
      if (!Number.isFinite(id) || id <= 0) return;

      const sku = getRowSku(row);
      const cartBaseline = getRowCartBaselineQty(row);
      const keepSerials = String(row.dataset.cartBaselineSerials || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      /** @type {Record<string, string>} */
      const properties = {};
      if (sku) properties.SKU = sku;
      if (payload.weight) properties.Weight = payload.weight;
      if (payload.serialNumbers) properties.SerialNumbers = payload.serialNumbers;

      const goldPricing = /** @type {typeof window.AoneGoldPricing | undefined} */ (window.AoneGoldPricing);
      if (goldPricing && payload.qty > 0) {
        const material = goldPricing.resolveJewelryMaterial({ row });
        Object.assign(properties, goldPricing.attachCaptureProperties(properties, material));
      }

      items.push({
        id,
        quantity: payload.qty,
        sku,
        productId: normalizeString(row.dataset.sourceProductId) || normalizeString(detailsRoot.dataset.productId),
        properties: Object.keys(properties).length ? properties : undefined,
        keepSerials,
        addableQty: Math.max(0, payload.qty - cartBaseline),
      });
    });
    return items;
  }

  /** @param {HTMLElement} detailsRoot */
  function validateWeightSelections(detailsRoot) {
    /** @type {string[]} */
    const errors = [];
    detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (!(row instanceof HTMLElement) || !isRowDraftDifferentFromCart(row)) return;
      const qty = getRowQty(row);
      const cartBaseline = getRowCartBaselineQty(row);
      if (qty <= 0) {
        if (cartBaseline > 0 && row.dataset.selectionDirty === 'true') return;
        return;
      }
      const available = isRowAvailable(row);
      if (!available && cartBaseline <= 0) return;
      const sku = getRowSku(row);
      if (!sku && !isRowQtyModalOnly(row)) return;
      const payload = getRowFullDraftPayload(row);
      if (!payload.weight || !payload.serialNumbers) {
        errors.push('Select weight for all variants with quantity greater than 0.');
        return;
      }
      const count = payload.weight.split(',').map((s) => s.trim()).filter(Boolean).length;
      const serialCount = payload.serialNumbers.split(',').map((s) => s.trim()).filter(Boolean).length;
      if (count !== qty || serialCount !== qty) {
        errors.push('Weight selection must match quantity for each variant.');
      }
    });
    return errors.length ? errors[0] : '';
  }

  /**
   * Commit matrix draft → Shopify cart (only path that may mutate cart).
   * New SKUs are batched into one /cart/add.js; existing SKUs keep replace/change.
   * One user click → one success toast after all mutations finish.
   * @param {HTMLElement} detailsRoot
   * @returns {Promise<{ cart: Record<string, unknown>|null, committed: number }>}
   */
  async function commitDraftRowsToCart(detailsRoot) {
    const skuMerge = /** @type {any} */ (window).AoneCartSkuMerge;
    if (!skuMerge || typeof skuMerge.replaceSkuSelection !== 'function') {
      throw new Error('Cart merge helper unavailable');
    }

    const items = collectDraftCommitItems(detailsRoot);
    if (!items.length) return { cart: null, committed: 0 };

    const availabilityApi = getWmsAvailabilityApi();
    /** @type {Array<{ sku: string, quantity: number, serials: string }>} */
    const pendingGuards = [];
    /** @type {Record<string, unknown>|null} */
    let cart = null;
    const toastApi = /** @type {any} */ (window).AoneCartAddToast;
    toastApi?.beginSuppress?.();
    let succeeded = false;

    try {
      // One fresh cart snapshot for classification + existing-line updates.
      if (typeof skuMerge.fetchCart === 'function') {
        cart = await skuMerge.fetchCart();
      } else if (availabilityApi?.fetchCart) {
        cart = await availabilityApi.fetchCart(true);
      } else {
        cart = { items: [] };
      }

      const fallbackProductId = String(detailsRoot.dataset.productId || '').trim();
      /** @type {typeof items} */
      const existingItems = [];
      /** @type {typeof items} */
      const newItems = [];
      /** @type {typeof items} */
      const legacyItems = [];

      for (const item of items) {
        const serials = String(item.properties?.SerialNumbers || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const keepSet = new Set(item.keepSerials.map((s) => String(s || '').trim()).filter(Boolean));
        const newSerials = serials.filter((serial) => !keepSet.has(serial));

        if (item.quantity > 0 && newSerials.length && availabilityApi?.isSerialReserved) {
          for (const serial of newSerials) {
            if (availabilityApi.isSerialReserved(serial, { keepSerials: keepSet })) {
              throw new Error('SELECTED WEIGHT IS NO LONGER AVAILABLE');
            }
          }
        }

        if (!item.sku) {
          legacyItems.push(item);
          continue;
        }

        const matches =
          typeof skuMerge.findLinesForSku === 'function' && cart
            ? skuMerge.findLinesForSku(cart, item.sku, item.id)
            : [];
        const isExisting = Array.isArray(matches) && matches.length > 0;

        if (isExisting) {
          // Existing cart SKU with additional units: only guard the addable delta.
          if (item.addableQty > 0 && availabilityApi) {
            const calc =
              typeof availabilityApi.calculateEffectiveAvailability === 'function'
                ? availabilityApi.calculateEffectiveAvailability({
                    productId: item.productId || fallbackProductId,
                    variantId: item.id,
                    sku: item.sku,
                  })
                : availabilityApi.getSkuAvailability?.(item.productId || fallbackProductId, item.sku);
            const remaining = Math.max(
              0,
              Math.floor(Number(calc?.effectiveAvailableQty ?? calc?.count) || 0)
            );
            if (item.addableQty > remaining) {
              throw new Error(
                remaining > 0 ? `ONLY ${remaining} LEFT FOR THIS SKU` : 'AVAILABILITY UNAVAILABLE'
              );
            }
          }
          existingItems.push(item);
        } else if (item.quantity > 0) {
          newItems.push(item);
        }
      }

      // Validate all brand-new SKUs together (one cart read via WMS cache when fresh).
      if (newItems.length && availabilityApi?.guardItems) {
        const guard = await availabilityApi.guardItems(
          newItems.map((item) => ({
            id: item.id,
            quantity: item.quantity,
            sku: item.sku,
            productId: item.productId || fallbackProductId,
            properties: item.properties,
          }))
        );
        if (!guard?.ok) {
          throw new Error(guard?.message || 'AVAILABILITY UNAVAILABLE');
        }
      }

      // Reserve pending qty/serials only after every selected row has passed validation.
      for (const item of items) {
        if (!(item.quantity > 0 && item.addableQty > 0 && item.sku && availabilityApi)) continue;
        const serials = String(item.properties?.SerialNumbers || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        const keepSet = new Set(item.keepSerials.map((s) => String(s || '').trim()).filter(Boolean));
        const newSerials = serials.filter((serial) => !keepSet.has(serial));
        availabilityApi.reservePendingQuantity?.(item.sku, item.addableQty);
        if (newSerials.length) availabilityApi.reserveSerials?.(newSerials.join(', '));
        pendingGuards.push({
          sku: item.sku,
          quantity: item.addableQty,
          serials: newSerials.join(', '),
        });
      }

      // Existing SKUs: preserve replace/change semantics (Weight + SerialNumbers replace).
      for (const item of existingItems) {
        const result = await skuMerge.replaceSkuSelection({
          id: item.id,
          sku: item.sku,
          quantity: item.quantity,
          properties: item.properties || {},
          cart: cart || undefined,
          skipInitialFetch: Boolean(cart && Array.isArray(cart.items)),
        });
        if (result && result.cart) cart = result.cart;
      }

      // Brand-new SKUs: one batched /cart/add.js (not one request per SKU).
      if (newItems.length) {
        const prepared = newItems.map((item) => {
          const incomingProps =
            item.properties && typeof item.properties === 'object' ? item.properties : {};
          const nextProps =
            typeof skuMerge.buildCanonicalProperties === 'function'
              ? skuMerge.buildCanonicalProperties(
                  {},
                  {
                    sku: item.sku,
                    weightCsv: String(incomingProps.Weight || ''),
                    serialCsv: String(incomingProps.SerialNumbers || ''),
                    incomingProps,
                  }
                )
              : Object.assign({}, incomingProps);
          return {
            id: item.id,
            quantity: item.quantity,
            properties: Object.keys(nextProps).length ? nextProps : undefined,
          };
        });

        if (typeof skuMerge.addItemsBatch === 'function') {
          const batchResult = await skuMerge.addItemsBatch(prepared);
          if (batchResult && batchResult.cart) cart = batchResult.cart;
        } else if (typeof skuMerge.addItemsRaw === 'function') {
          await skuMerge.enqueue(async () => {
            await skuMerge.addItemsRaw(prepared);
          });
          cart =
            (typeof skuMerge.fetchCart === 'function'
              ? await skuMerge.fetchCart()
              : availabilityApi?.fetchCart
                ? await availabilityApi.fetchCart(true)
                : cart) || cart;
        } else {
          for (const item of newItems) {
            const result = await skuMerge.replaceSkuSelection({
              id: item.id,
              sku: item.sku,
              quantity: item.quantity,
              properties: item.properties || {},
            });
            if (result && result.cart) cart = result.cart;
          }
        }
      }

      // Rare no-SKU rows: keep previous merge helper path.
      for (const item of legacyItems) {
        if (!(item.quantity > 0 && item.addableQty > 0 && typeof skuMerge.addOrMergeItems === 'function')) {
          continue;
        }
        const result = await skuMerge.addOrMergeItems([
          {
            id: item.id,
            quantity: item.addableQty,
            properties: item.properties,
          },
        ]);
        if (result && result.cart) cart = result.cart;
      }

      succeeded = true;
      return { cart, committed: items.length };
    } finally {
      pendingGuards.forEach((entry) => {
        if (!entry?.sku || !availabilityApi) return;
        availabilityApi.releasePendingQuantity?.(entry.sku, entry.quantity);
        if (entry.serials) availabilityApi.releaseSerials?.(entry.serials);
      });
      toastApi?.endSuppress?.();
      // One user action → one success toast (after all line mutations).
      if (succeeded) toastApi?.showSuccess?.();
    }
  }

  /** @param {HTMLElement} panel @param {HTMLElement} detailsRoot */
  async function addSelectedToCart(panel, detailsRoot) {
    const btn = /** @type {HTMLButtonElement | null} */ (detailsRoot.querySelector('[data-details-add-selected]'));
    if (!btn) return;
    if (btn.dataset.addState === 'loading') return;

    const weightError = validateWeightSelections(detailsRoot);
    if (weightError) {
      showToast(weightError, 'error');
      return;
    }

    const items = collectDraftCommitItems(detailsRoot);
    if (!items.length) {
      showToast('Select at least one available variant with quantity greater than 0.', 'error');
      return;
    }

    const goldPricing = /** @type {typeof window.AoneGoldPricing | undefined} */ (window.AoneGoldPricing);
    if (goldPricing) {
      await goldPricing.waitForReady();
    }

    setAddSelectedButtonState(btn, 'loading');
    try {
      const availabilityApi = getWmsAvailabilityApi();
      const { cart } = await commitDraftRowsToCart(detailsRoot);

      if (cart) {
        if (availabilityApi?.handleSuccessfulCartMutation) {
          await availabilityApi.handleSuccessfulCartMutation(cart, { reason: 'cart-add' });
        }
        if (window.aoneCartCount) {
          window.aoneCartCount.syncBadgeDom?.(
            typeof cart.item_count === 'number' ? cart.item_count : 0
          );
          if (!availabilityApi?.handleSuccessfulCartMutation) {
            window.aoneCartCount.publish(cart, 'plp-details-variant-table');
          }
        }
      } else if (availabilityApi?.handleSuccessfulCartMutation) {
        await availabilityApi.handleSuccessfulCartMutation(null, { reason: 'cart-add' });
      }

      clearRowSelectionDirtyFlags(detailsRoot);
      resetVariantTable(detailsRoot);
      // Premium multidimensional PDP rebuilds cells from shared inventory state.
      if (typeof window.aonePremiumPdpRehydrateInventory === 'function') {
        window.aonePremiumPdpRehydrateInventory();
      }
      await hydrateDetailsWmsAvailability(detailsRoot);
      setAddSelectedButtonState(btn, 'success');
    } catch (err) {
      console.error('[plp-table-details]', err);
      showToast(
        err && typeof err.message === 'string' ? err.message : 'Unable to add items to cart',
        'error'
      );
      setAddSelectedButtonState(btn, 'idle');
      updateAddSelectedState(detailsRoot);
    }
  }

  /**
   * Three selection states (must stay separate):
   * 1) CART — live cart serials (clearBaselineIds / inCartOptionIds)
   * 2) MATRIX/PENDING — row dataset serialNumbersValue / weightValue / qty
   *    (auto-select + Add Selection; NOT cart until Add Selected To Cart)
   * 3) MODAL DRAFT — selectedIds while the modal is open
   *
   * clearBaselineIds = cart-only ids for Clear. Never includes pending drafts.
   * openRowSnapshot = matrix/pending at modal open (restore on X without Add Selection).
   * @typedef {{ modal: HTMLElement, detailsRoot: HTMLElement, activeRow: HTMLElement | null, loadedOptions: Array<{ id: string, label: string, serialNumber?: string, inCart?: boolean, fields: Array<{ label: string, value: string }> }>, selectedIds: string[], clearBaselineIds: string[], inCartOptionIds: Set<string>, requestSeq: number, activeSku: string, insufficientAvailable: boolean, openRowSnapshot: { qty: number, weightValue: string, serialNumbersValue: string, selectionDirty: boolean } | null, selectionConfirmed: boolean }} WeightModalState
   */

  /** @type {WeakMap<HTMLElement, WeightModalState>} */
  const weightModalStates = new WeakMap();
  /** @type {WeakMap<HTMLElement, WeightModalState>} */
  const weightModalByModal = new WeakMap();

  /** @param {WeightModalState} state */
  function registerWeightModalState(state) {
    weightModalStates.set(state.detailsRoot, state);
    weightModalByModal.set(state.modal, state);
    state.modal.dataset.weightDetailsRootKey = String(state.detailsRoot.dataset.productId || '');
  }

  /** @param {HTMLElement} detailsRoot */
  function getWeightModalState(detailsRoot) {
    const cached = weightModalStates.get(detailsRoot);
    if (cached) {
      if (!Array.isArray(cached.clearBaselineIds)) cached.clearBaselineIds = [];
      if (!('openRowSnapshot' in cached)) cached.openRowSnapshot = null;
      if (!('selectionConfirmed' in cached)) cached.selectionConfirmed = false;
      registerWeightModalState(cached);
      return cached;
    }

    const modal = /** @type {HTMLElement | null} */ (
      detailsRoot.querySelector('[data-order-sheet-weight-modal]')
    );
    if (!modal) return null;

    const state = {
      modal,
      detailsRoot,
      activeRow: null,
      loadedOptions: [],
      selectedIds: [],
      clearBaselineIds: [],
      inCartOptionIds: new Set(),
      requestSeq: 0,
      activeSku: '',
      insufficientAvailable: false,
      openRowSnapshot: null,
      selectionConfirmed: false,
    };
    registerWeightModalState(state);
    return state;
  }

  /** @param {HTMLElement} modal */
  function getWeightModalStateFromModal(modal) {
    const cached = weightModalByModal.get(modal);
    if (cached) return cached;

    const productId = modal.dataset.weightDetailsRootKey;
    if (productId) {
      for (const host of document.querySelectorAll('[data-plp-expanded-details]')) {
        if (!(host instanceof HTMLElement)) continue;
        if (host.dataset.productId !== productId) continue;
        const state = weightModalStates.get(host);
        if (state && state.modal === modal) {
          registerWeightModalState(state);
          return state;
        }
      }
    }

    return null;
  }

  /** @param {WeightModalState} state */
  function portalWeightModal(state) {
    const modal = state.modal;
    if (modal.parentElement === document.body) return;
    if (!modal.dataset.weightModalAnchor) {
      const anchor = document.createComment('order-sheet-weight-modal-anchor');
      modal.parentNode?.insertBefore(anchor, modal);
      modal.dataset.weightModalAnchor = 'true';
      /** @type {WeightModalState & { modalAnchor?: Comment }} */ (state).modalAnchor = anchor;
    }
    document.body.appendChild(modal);
  }

  /** @param {WeightModalState} state */
  function restoreWeightModalToAnchor(state) {
    const modal = state.modal;
    const anchor = /** @type {WeightModalState & { modalAnchor?: Comment }} */ (state).modalAnchor;
    if (!(anchor instanceof Comment)) return;
    const parent = anchor.parentNode;
    if (!parent) return;
    if (modal.parentNode === parent && modal.previousSibling === anchor) return;
    parent.insertBefore(modal, anchor.nextSibling);
  }

  /** @param {WeightModalState} state */
  function getModalRequiredCount(state) {
    if (!state.activeRow) return 0;
    const els = getModalEls(state);
    if (els.qtyInput instanceof HTMLInputElement) return readQty(els.qtyInput);
    return getRowQty(state.activeRow);
  }

  /** @param {WeightModalState} state @param {number} qty */
  function syncModalQtyUi(state, qty) {
    const els = getModalEls(state);
    const safeQty = Math.max(0, qty);
    if (els.qtyInput instanceof HTMLInputElement) {
      const next = String(safeQty);
      if (els.qtyInput.value !== next) writeQty(els.qtyInput, safeQty, { silent: true });
    }
    if (els.qtyMinus instanceof HTMLButtonElement) els.qtyMinus.disabled = safeQty <= 0;
  }

  /**
   * @param {WeightModalState} state
   * @param {number} qty
   */
  function applyModalQtyChange(state, qty) {
    const safeQty = Math.max(0, qty);
    syncModalQtyUi(state, safeQty);

    if (!state.loadedOptions.length) return;

    state.insufficientAvailable = state.loadedOptions.length < safeQty;
    if (state.insufficientAvailable) {
      const template =
        state.modal.dataset.weightInsufficientMsg ||
        'Only {available} weight(s) available. You requested {required}.';
      showToast(
        template
          .replace(/\{available\}/g, String(state.loadedOptions.length))
          .replace(/\{required\}/g, String(safeQty)),
        'error'
      );
      state.selectedIds = [];
    } else if (state.selectedIds.length > safeQty) {
      const selectedEntries = state.selectedIds
        .map((id) => state.loadedOptions.find((opt) => opt.id === id))
        .filter(Boolean)
        .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
      state.selectedIds = selectedEntries.slice(0, safeQty).map((opt) => opt.id);
    } else if (state.selectedIds.length < safeQty && !state.insufficientAvailable) {
      const sortedOptions = state.loadedOptions
        .slice()
        .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
      for (const opt of sortedOptions) {
        if (state.selectedIds.length >= safeQty) break;
        if (state.selectedIds.indexOf(opt.id) < 0) state.selectedIds.push(opt.id);
      }
    }
    renderModalCheckboxes(state);
    updateModalSummary(state);
  }

  /** @param {WeightModalState} state */
  function getModalEls(state) {
    const m = state.modal;
    return {
      skuWrap: m.querySelector('[data-order-sheet-weight-sku-wrap]'),
      skuValue: m.querySelector('[data-order-sheet-weight-sku]'),
      qtyInput: /** @type {HTMLInputElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-input]')),
      qtyMinus: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-minus]')),
      qtyPlus: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-qty-plus]')),
      fetchBtn: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-fetch]')),
      fetchLabel: m.querySelector('[data-order-sheet-weight-fetch-label]'),
      spinner: m.querySelector('[data-order-sheet-weight-spinner]'),
      status: m.querySelector('[data-order-sheet-weight-status]'),
      optionsWrap: m.querySelector('[data-order-sheet-weight-options-wrap]'),
      dropdown: m.querySelector('[data-order-sheet-weight-dropdown]'),
      dropdownPanel: m.querySelector('[data-order-sheet-weight-dropdown-panel]'),
      dropdownCount: m.querySelector('[data-order-sheet-weight-dropdown-count]'),
      dropdownSearch: /** @type {HTMLInputElement | null} */ (
        m.querySelector('[data-order-sheet-weight-dropdown-search]')
      ),
      dropdownEmpty: m.querySelector('[data-order-sheet-weight-dropdown-empty]'),
      loading: m.querySelector('[data-order-sheet-weight-loading]'),
      optionsList: m.querySelector('[data-order-sheet-weight-options]'),
      empty: m.querySelector('[data-order-sheet-weight-empty]'),
      summary: m.querySelector('[data-order-sheet-weight-summary]'),
      summaryQty: m.querySelector('[data-order-sheet-weight-summary-qty]'),
      summaryTotal: m.querySelector('[data-order-sheet-weight-summary-total]'),
      error: m.querySelector('[data-order-sheet-weight-error]'),
      confirm: /** @type {HTMLButtonElement | null} */ (m.querySelector('[data-order-sheet-weight-confirm]')),
    };
  }

  /** @param {WeightModalState} state */
  function resetWeightSearch(state) {
    const els = getModalEls(state);
    if (els.dropdownSearch) els.dropdownSearch.value = '';
    if (els.dropdownEmpty) els.dropdownEmpty.hidden = true;
    if (els.optionsList) {
      els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((item) => {
        item.hidden = false;
      });
    }
  }

  /** @param {WeightModalState} state */
  function applyWeightFilter(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;

    const query = (els.dropdownSearch?.value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
    let visible = 0;

    els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const haystack = String(item.dataset.searchText || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      const match = !query || haystack.startsWith(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (els.dropdownEmpty) {
      els.dropdownEmpty.hidden = visible > 0;
    }

    if (els.dropdownCount) {
      const total = els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').length;
      if (!total) {
        els.dropdownCount.textContent = '';
      } else if (query) {
        els.dropdownCount.textContent = visible + ' of ' + total;
      } else {
        els.dropdownCount.textContent = total + ' Total';
      }
    }
  }

  /** @param {WeightModalState} state @param {boolean} visible */
  function setWeightOptionsVisible(state, visible) {
    const els = getModalEls(state);
    if (!els.dropdownPanel) return;
    els.dropdownPanel.hidden = !visible;
    if (visible) {
      applyWeightFilter(state);
    } else {
      resetWeightSearch(state);
    }
  }

  /** @param {WeightModalState} state */
  function bindWeightSearch(state) {
    const search = getModalEls(state).dropdownSearch;
    if (!search || search.dataset.weightSearchBound === 'true') return;
    search.dataset.weightSearchBound = 'true';
    let timer = 0;

    search.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => applyWeightFilter(state), WEIGHT_SEARCH_DEBOUNCE_MS);
    });
    search.addEventListener('keydown', (e) => {
      e.stopPropagation();
    });
    search.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  /** @param {WeightModalState} state @param {boolean} disabled */
  function setModalInteractiveControlsDisabled(state, disabled) {
    const els = getModalEls(state);
    if (els.dropdownSearch) els.dropdownSearch.disabled = disabled;
    if (els.optionsList) {
      els.optionsList.querySelectorAll('button, input').forEach((node) => {
        if (
          node instanceof HTMLButtonElement ||
          node instanceof HTMLInputElement ||
          node instanceof HTMLSelectElement ||
          node instanceof HTMLTextAreaElement
        ) {
          node.disabled = disabled;
        }
      });
    }
    if (disabled && els.confirm) els.confirm.disabled = true;
  }

  /** @param {WeightModalState} state @param {boolean} usePlaceholder */
  function setModalSummaryPlaceholders(state, usePlaceholder) {
    const els = getModalEls(state);
    if (!els.summary || !els.summaryQty || !els.summaryTotal) return;
    if (usePlaceholder) {
      els.summary.textContent = '--';
      els.summaryQty.textContent = '--';
      els.summaryTotal.textContent = '--';
      return;
    }
    if (els.summary.textContent === '--') els.summary.textContent = '0';
    if (els.summaryQty.textContent === '--') els.summaryQty.textContent = '0';
    if (els.summaryTotal.textContent === '--') els.summaryTotal.textContent = '0.00g';
  }

  /** @param {WeightModalState} state @param {'idle'|'loading'|'success'|'empty'|'error'} uiState */
  function setModalFetchUi(state, uiState) {
    const els = getModalEls(state);
    if (!els.status) return;
    state.modal.dataset.fetchState = uiState;

    if (uiState === 'loading') {
      els.status.textContent = 'Fetching available weights...';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = false;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = true;
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, true);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    if (uiState === 'error') {
      els.status.textContent = '';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = true;
      if (els.error) els.error.hidden = false;
      setModalSummaryPlaceholders(state, true);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    if (uiState === 'success') {
      els.status.textContent = '';
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = false;
      if (els.empty) els.empty.hidden = true;
      setWeightOptionsVisible(state, true);
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, false);
      setModalInteractiveControlsDisabled(state, false);
      return;
    }
    if (uiState === 'empty') {
      els.status.textContent = '';
      setWeightOptionsVisible(state, false);
      if (els.loading) els.loading.hidden = true;
      if (els.optionsWrap) els.optionsWrap.hidden = true;
      if (els.empty) els.empty.hidden = false;
      if (els.error) els.error.hidden = true;
      setModalSummaryPlaceholders(state, false);
      setModalInteractiveControlsDisabled(state, true);
      return;
    }
    els.status.textContent = '';
    if (els.loading) els.loading.hidden = true;
    setWeightOptionsVisible(state, false);
    if (els.optionsWrap) els.optionsWrap.hidden = true;
    if (els.empty) els.empty.hidden = true;
    if (els.error) els.error.hidden = true;
    setModalSummaryPlaceholders(state, false);
    setModalInteractiveControlsDisabled(state, true);
  }

  /** @param {string} label */
  function parseWeightLabelGrams(label) {
    const m = String(label || '').match(/([\d.]+)/);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    return Number.isFinite(n) ? n : 0;
  }

  /** @param {WeightModalState} state */
  function buildWeightGroups(state) {
    /** @type {Map<string, { label: string, ids: string[], selectedCount: number, inCartCount: number, totalCount: number }>} */
    const map = new Map();
    const selectedSet = new Set(state.selectedIds);
    const inCartSet = state.inCartOptionIds instanceof Set ? state.inCartOptionIds : new Set();
    for (const opt of state.loadedOptions) {
      const label = String(opt.label || '').trim();
      if (!label) continue;
      if (!map.has(label)) {
        map.set(label, { label, ids: [], selectedCount: 0, inCartCount: 0, totalCount: 0 });
      }
      const group = map.get(label);
      if (!group) continue;
      group.ids.push(opt.id);
      group.totalCount += 1;
      if (selectedSet.has(opt.id)) group.selectedCount += 1;
      if (inCartSet.has(opt.id)) group.inCartCount += 1;
    }
    return Array.from(map.values());
  }

  /**
   * @param {WeightModalState} state
   * @returns {number}
   */
  function getModalMaxSelectable(state) {
    const rowInput = state.activeRow ? getRowQtyInput(state.activeRow) : null;
    const rowMax = state.activeRow ? getRowQtyMax(state.activeRow, rowInput || undefined) : null;
    const cartBaseline = state.activeRow ? getRowCartBaselineQty(state.activeRow) : 0;
    const inCartCount = state.inCartOptionIds instanceof Set ? state.inCartOptionIds.size : 0;
    const baseline = Math.max(cartBaseline, inCartCount);
    const byStock = state.loadedOptions.length;
    const addable =
      rowMax == null
        ? Math.max(0, byStock - baseline)
        : Math.max(0, Math.min(byStock - baseline, rowMax));
    return Math.max(0, baseline + addable);
  }

  /**
   * @param {WeightModalState} state
   * @param {number} groupIndex
   * @param {number} desiredQty
   */
  function setWeightGroupSelection(state, groupIndex, desiredQty) {
    const maxSelectable = getModalMaxSelectable(state);
    const groups = buildWeightGroups(state);
    const group = groups[groupIndex];
    if (!group) return;

    const selectedSet = new Set(state.selectedIds);
    const selectedIdsInGroup = group.ids.filter((id) => selectedSet.has(id));

    let nextQty = Math.max(0, Math.floor(Number.isFinite(desiredQty) ? desiredQty : 0));
    nextQty = Math.min(nextQty, group.totalCount);
    // Allow reducing below in-cart count so Edit Weight can change/remove cart units.

    const selectedOutsideGroup = state.selectedIds.length - selectedIdsInGroup.length;
    const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
    nextQty = Math.min(nextQty, maxForGroup);

    if (nextQty > selectedIdsInGroup.length) {
      const toAdd = nextQty - selectedIdsInGroup.length;
      let added = 0;
      for (const id of group.ids) {
        if (added >= toAdd) break;
        if (selectedSet.has(id)) continue;
        state.selectedIds.push(id);
        selectedSet.add(id);
        added += 1;
      }
    } else if (nextQty < selectedIdsInGroup.length) {
      const toRemove = selectedIdsInGroup.length - nextQty;
      let removed = 0;
      for (let i = state.selectedIds.length - 1; i >= 0; i -= 1) {
        if (removed >= toRemove) break;
        const id = state.selectedIds[i];
        if (group.ids.indexOf(id) < 0) continue;
        state.selectedIds.splice(i, 1);
        removed += 1;
      }
    }

    // Keep draft selection inside the modal only. Parent row qty/serials update
    // only on successful Add Selection (confirmWeightModal) or Clear/close restore.

    syncWeightRowsUi(state);
    updateModalSummary(state);
  }

  /** @param {WeightModalState} state */
  function updateModalSummary(state) {
    const els = getModalEls(state);
    if (!els.status || !els.confirm) return;

    const available = state.loadedOptions.length;
    const selected = state.selectedIds
      .map((id) => state.loadedOptions.find((o) => o.id === id))
      .filter(Boolean);
    const selectedCount = selected.length;
    const maxSelectable = getModalMaxSelectable(state);

    const uniqueWeights = Array.from(new Set(selected.map((o) => String(o.label || '').trim()).filter(Boolean)));
    const totalGrams = selected.reduce((sum, opt) => sum + parseWeightLabelGrams(String(opt.label || '')), 0);

    if (els.summary) {
      els.summary.textContent = String(uniqueWeights.length);
    }
    if (els.summaryQty) els.summaryQty.textContent = String(selected.length);
    if (els.summaryTotal) {
      els.summaryTotal.textContent = formatWeightTotal(totalGrams);
    }

    // Keep filtered visibility in sync when values change.
    applyWeightFilter(state);

    const canConfirm = !state.insufficientAvailable && selectedCount > 0 && state.loadedOptions.length > 0;

    els.confirm.disabled = !canConfirm;

    if (state.insufficientAvailable) {
      els.status.textContent = 'Only ' + available + ' available. Reduce selection.';
    } else {
      els.status.textContent = '';
    }
  }

  /** @param {WeightModalState} state */
  function renderModalCheckboxes(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;

    const maxSelectable = getModalMaxSelectable(state);

    const groups = buildWeightGroups(state);
    els.optionsList.innerHTML = '';
    if (!groups.length) {
      setModalFetchUi(state, 'empty');
      return;
    }
    if (els.empty) els.empty.hidden = true;
    if (els.optionsWrap) els.optionsWrap.hidden = false;
    if (groups.length) {
      const head = document.createElement('div');
      head.className = 'order-sheet-weight-modal__list-head';
      head.innerHTML = `
        <span>WEIGHT</span>
        <span>AVAILABLE QTY</span>
        <span>SELECTED QTY</span>
      `;
      els.optionsList.appendChild(head);
    }
    if (els.dropdownEmpty) {
      els.dropdownEmpty.textContent = 'No matching weights found';
      els.dropdownEmpty.hidden = true;
    }

    groups.forEach((group, idx) => {
      const row = document.createElement('div');
      row.className = 'order-sheet-weight-modal__checkbox-item';
      row.dataset.searchText = String(group.label || '')
        .toLowerCase()
        .replace(/\s+/g, '');
      row.dataset.groupIndex = String(idx);
      if (group.selectedCount > 0) row.classList.add('is-selected');
      if (group.inCartCount > 0) row.classList.add('is-in-cart');

      const left = document.createElement('div');
      left.className = 'order-sheet-weight-modal__weight-main';
      left.innerHTML = `
        <strong class="order-sheet-weight-modal__weight-value">${escapeHtml(formatWeightLabelForDisplay(group.label))}</strong>
      `;

      const center = document.createElement('div');
      center.className = 'order-sheet-weight-modal__stock-meta';
      center.innerHTML = `
        <span class="order-sheet-weight-modal__stock-count">${group.totalCount} In Stock</span>
      `;

      const step = document.createElement('div');
      step.className = 'order-sheet-weight-modal__row-stepper';
      const selectedOutsideGroup = state.selectedIds.length - group.selectedCount;
      const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
      const canInc = group.selectedCount < Math.min(group.totalCount, maxForGroup);
      step.innerHTML = `
        <button type="button" class="order-sheet-weight-modal__row-step-btn" data-order-sheet-weight-row-minus ${
          group.selectedCount <= 0 ? 'disabled' : ''
        } aria-label="Decrease ${escapeHtml(group.label)} selection">−</button>
        <input
          type="number"
          class="order-sheet-weight-modal__row-step-input"
          value="${group.selectedCount}"
          min="0"
          max="${Math.min(group.totalCount, maxForGroup)}"
          step="1"
          inputmode="numeric"
          aria-label="Selected quantity for ${escapeHtml(group.label)}"
          data-order-sheet-weight-row-input
        >
        <button type="button" class="order-sheet-weight-modal__row-step-btn" data-order-sheet-weight-row-plus ${
          canInc ? '' : 'disabled'
        } aria-label="Increase ${escapeHtml(group.label)} selection">+</button>
      `;

      row.appendChild(left);
      row.appendChild(center);
      row.appendChild(step);
      els.optionsList.appendChild(row);
    });

    syncWeightRowsUi(state);
    applyWeightFilter(state);
  }

  /** @param {WeightModalState} state */
  function syncWeightRowsUi(state) {
    const els = getModalEls(state);
    if (!els.optionsList) return;
    const groups = buildWeightGroups(state);
    const maxSelectable = getModalMaxSelectable(state);

    els.optionsList.querySelectorAll('.order-sheet-weight-modal__checkbox-item').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const groupIndex = Number(row.dataset.groupIndex);
      if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
      const group = groups[groupIndex];
      if (!group) return;

      const selectedOutsideGroup = state.selectedIds.length - group.selectedCount;
      const maxForGroup = Math.max(0, maxSelectable - selectedOutsideGroup);
      const maxAllowedForGroup = Math.min(group.totalCount, maxForGroup);
      const canInc = group.selectedCount < maxAllowedForGroup;

      row.classList.toggle('is-selected', group.selectedCount > 0);
      row.classList.toggle('is-in-cart', group.inCartCount > 0);
      const countEl = row.querySelector('.order-sheet-weight-modal__stock-count');
      if (countEl) {
        countEl.textContent = `${group.totalCount} In Stock`;
      }

      const rowInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-input]')
      );
      if (rowInput) {
        rowInput.value = String(group.selectedCount);
        rowInput.max = String(maxAllowedForGroup);
      }
      const minusBtn = /** @type {HTMLButtonElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-minus]')
      );
      if (minusBtn) minusBtn.disabled = group.selectedCount <= 0;
      const plusBtn = /** @type {HTMLButtonElement | null} */ (
        row.querySelector('[data-order-sheet-weight-row-plus]')
      );
      if (plusBtn) plusBtn.disabled = !canInc;

    });
  }

  /**
   * Map preferred serials onto loaded modal option ids (stable order of preferredSerials).
   * @param {WeightModalState} state
   * @param {Iterable<string>} preferredSerials
   * @returns {string[]}
   */
  function mapSerialsToLoadedOptionIds(state, preferredSerials) {
    /** @type {string[]} */
    const selectedIds = [];
    const usedIds = new Set();
    for (const raw of preferredSerials) {
      const serial = String(raw || '').trim();
      if (!serial) continue;
      const match = state.loadedOptions.find(
        (opt) =>
          !usedIds.has(opt.id) &&
          opt.serialNumber != null &&
          String(opt.serialNumber).trim() === serial
      );
      if (!match) continue;
      selectedIds.push(match.id);
      usedIds.add(match.id);
    }
    return selectedIds;
  }

  /**
   * CART-COMMITTED selection ids only (live cart serials).
   * Used by Clear and as clearBaselineIds. Never includes matrix/pending drafts.
   * @param {WeightModalState} state
   * @param {Set<string>} cartSerialSet
   * @returns {string[]}
   */
  function resolveCartWeightSelectionIds(state, cartSerialSet) {
    /** @type {string[]} */
    const preferred = [];
    if (cartSerialSet instanceof Set) {
      cartSerialSet.forEach((serial) => {
        const trimmed = String(serial || '').trim();
        if (trimmed) preferred.push(trimmed);
      });
    }
    if (preferred.length) return mapSerialsToLoadedOptionIds(state, preferred);

    /** @type {string[]} */
    const selectedIds = [];
    const usedIds = new Set();
    state.loadedOptions.forEach((opt) => {
      if (usedIds.has(opt.id)) return;
      const serial = String(opt.serialNumber || '').trim();
      const inCart =
        Boolean(opt.inCart) ||
        (state.inCartOptionIds instanceof Set && state.inCartOptionIds.has(opt.id)) ||
        (serial !== '' && cartSerialSet instanceof Set && cartSerialSet.has(serial));
      if (!inCart) return;
      selectedIds.push(opt.id);
      usedIds.add(opt.id);
    });
    return selectedIds;
  }

  /**
   * MATRIX/PENDING selection ids from the active row (auto-select + Add Selection).
   * Not gated on selectionDirty — pending serials are the source of truth for Edit Weight open.
   * @param {WeightModalState} state
   * @returns {string[]}
   */
  function resolveMatrixPendingWeightSelectionIds(state) {
    const row = state.activeRow;
    if (!(row instanceof HTMLElement)) return [];
    const preferred = String(row.dataset.serialNumbersValue || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return mapSerialsToLoadedOptionIds(state, preferred);
  }

  /**
   * Snapshot matrix/pending row state so X-close can discard modal draft only.
   * @param {HTMLElement} row
   */
  function captureRowSelectionSnapshot(row) {
    return {
      qty: getRowQty(row),
      weightValue: String(row.dataset.weightValue || ''),
      serialNumbersValue: String(row.dataset.serialNumbersValue || ''),
      selectionDirty: row.dataset.selectionDirty === 'true',
    };
  }

  /**
   * Restore matrix/pending from an open-time snapshot (modal close without Add Selection).
   * @param {HTMLElement} row
   * @param {{ qty: number, weightValue: string, serialNumbersValue: string, selectionDirty: boolean } | null | undefined} snapshot
   */
  function restoreRowSelectionSnapshot(row, snapshot) {
    if (!(row instanceof HTMLElement) || !snapshot) return;

    const qty = Math.max(0, Math.floor(Number(snapshot.qty) || 0));
    const rowInput = getRowQtyInput(row);
    if (rowInput) writeQty(rowInput, qty, { silent: true });
    const displayInput = /** @type {HTMLInputElement | null} */ (
      row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
    );
    if (displayInput) displayInput.value = String(qty);

    const weightValue = String(snapshot.weightValue || '').trim();
    const serialNumbersValue = String(snapshot.serialNumbersValue || '').trim();
    if (weightValue || serialNumbersValue) {
      setRowWeightValue(row, weightValue, serialNumbersValue);
    } else {
      clearRowWeight(row);
    }

    if (snapshot.selectionDirty) markRowSelectionDirty(row);
    else delete row.dataset.selectionDirty;

    syncRowMinusState(row);
    syncRowWeightActionLabel(row);
    syncWeightButtonState(row);
  }

  /**
   * @deprecated Use resolveCartWeightSelectionIds / resolveMatrixPendingWeightSelectionIds.
   * Kept as cart-only alias so Clear never treats Add Selection as committed.
   * @param {WeightModalState} state
   * @param {Set<string>} cartSerialSet
   * @returns {string[]}
   */
  function resolveCommittedWeightSelectionIds(state, cartSerialSet) {
    return resolveCartWeightSelectionIds(state, cartSerialSet);
  }

  /**
   * Align row qty with an already-confirmed dirty matrix draft (no cart wipe).
   * @param {HTMLElement} row
   */
  function discardUncommittedWeightModalRowEdits(row) {
    if (!(row instanceof HTMLElement)) return;

    if (row.dataset.selectionDirty === 'true') {
      const serialCount = String(row.dataset.serialNumbersValue || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length;
      const weightCount = String(row.dataset.weightValue || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean).length;
      const count = Math.max(serialCount, weightCount);
      const rowInput = getRowQtyInput(row);
      if (rowInput) writeQty(rowInput, count, { silent: true });
      const displayInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
      );
      if (displayInput) displayInput.value = String(count);
      syncRowMinusState(row);
      syncRowWeightActionLabel(row);
      return;
    }

    applyCartSelectionToRow(row, { force: true });
    if (getRowCartBaselineQty(row) <= 0) {
      const rowInput = getRowQtyInput(row);
      if (rowInput) writeQty(rowInput, 0, { silent: true });
      const displayInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
      );
      if (displayInput) displayInput.value = '0';
      clearRowWeight(row);
      syncRowMinusState(row);
      syncRowWeightActionLabel(row);
    }
  }

  /**
   * Apply selection ids back onto the active matrix row (qty + weights).
   * Used by Clear (cart-only ids) and any restore that must sync matrix/pending.
   * @param {WeightModalState} state
   * @param {string[]} baselineIds
   */
  function applyWeightSelectionIdsToRow(state, baselineIds) {
    const row = state.activeRow;
    if (!(row instanceof HTMLElement)) return;

    const selectedOptions = baselineIds
      .map((id) => state.loadedOptions.find((o) => o.id === id))
      .filter(Boolean);
    const cartBaseline = getRowCartBaselineQty(row);

    if (selectedOptions.length === 0) {
      // Prefer live cart paint whenever cart units exist.
      if (cartBaseline > 0) {
        applyCartSelectionToRow(row, { force: true });
        return;
      }
      const rowInput = getRowQtyInput(row);
      if (rowInput) writeQty(rowInput, 0, { silent: true });
      const displayInput = /** @type {HTMLInputElement | null} */ (
        row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
      );
      if (displayInput) displayInput.value = '0';
      clearRowWeight(row);
      delete row.dataset.selectionDirty;
      return;
    }

    const rowInput = getRowQtyInput(row);
    if (rowInput) writeQty(rowInput, selectedOptions.length, { silent: true });
    const displayInput = /** @type {HTMLInputElement | null} */ (
      row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
    );
    if (displayInput) displayInput.value = String(selectedOptions.length);
    const labels = selectedOptions.map((o) => String(o.label || '').trim()).filter(Boolean);
    const serials = selectedOptions
      .map((o) => (o.serialNumber != null ? String(o.serialNumber).trim() : ''))
      .filter(Boolean);
    if (labels.length) setRowWeightValue(row, labels.join(', '), serials.join(', '));
    else clearRowWeight(row);
    if (isRowDraftDifferentFromCart(row)) markRowSelectionDirty(row);
    else delete row.dataset.selectionDirty;
  }

  /** @param {WeightModalState} state */
  function submitWeightFetch(state) {
    if (!state.activeRow) return;
    const sku = getRowSku(state.activeRow);
    if (!sku) {
      setModalFetchUi(state, 'error');
      return;
    }

    const runId = ++state.requestSeq;
    state.activeSku = sku;
    state.insufficientAvailable = false;
    setModalFetchUi(state, 'loading');

    const els = getModalEls(state);
    if (els.status) els.status.textContent = 'Fetching available weights...';

    const availabilityApi = getWmsAvailabilityApi();
    const cartReady = availabilityApi?.ensureCartSerialsSynced
      ? availabilityApi.ensureCartSerialsSynced(false).catch(() => null)
      : Promise.resolve(null);

    Promise.resolve(cartReady)
      .then(() => fetchWeightPayload(state.detailsRoot, sku))
      .then((data) => {
        if (runId !== state.requestSeq || state.activeSku !== sku) return;

        const detailsRoot = state.detailsRoot;
        const exclude =
          state.activeRow instanceof HTMLElement
            ? collectSelectedSerialsOutsideRow(detailsRoot, state.activeRow)
            : new Set();
        /** @type {Set<string>} */
        const keepSerials = new Set(
          state.activeRow
            ? String(state.activeRow.dataset.serialNumbersValue || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            : []
        );

        const cartSummary =
          availabilityApi && typeof availabilityApi.getCartSkuSummary === 'function'
            ? availabilityApi.getCartSkuSummary(sku)
            : { serialNumbers: [], weightLabels: [] };
        /** @type {Set<string>} */
        const cartSerialSet = new Set(
          (Array.isArray(cartSummary.serialNumbers) ? cartSummary.serialNumbers : [])
            .map((s) => String(s || '').trim())
            .filter(Boolean)
        );

        const shared = getSharedWeightStore();
        const rawNode = shared?.rawBySku?.[sku.toLowerCase()];
        /** @type {typeof data.entries} */
        let sourceEntries = Array.isArray(data.entries) ? data.entries : [];
        if (rawNode != null) {
          const fromRaw = extractWeightEntries(rawNode, { skipReservedFilter: true });
          if (fromRaw.length) sourceEntries = fromRaw;
        } else if (sourceEntries.length) {
          sourceEntries = extractWeightEntries(sourceEntries, { skipReservedFilter: true });
        }

        state.inCartOptionIds = new Set();
        state.loadedOptions = sourceEntries
          .filter((entry) => {
            const serial = String(entry?.serialNumber || '').trim();
            if (!serial) return false;
            if (keepSerials.has(serial) || cartSerialSet.has(serial)) return true;
            if (exclude.has(serial)) return false;
            if (
              availabilityApi &&
              typeof availabilityApi.isSerialReserved === 'function' &&
              availabilityApi.isSerialReserved(serial)
            ) {
              return false;
            }
            return true;
          })
          .map((entry) => {
            const serial = String(entry?.serialNumber || '').trim();
            const inCart = cartSerialSet.has(serial);
            if (inCart) state.inCartOptionIds.add(String(entry.id));
            return { ...entry, inCart };
          })
          .slice()
          .sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));

        cartSerialSet.forEach((serial) => {
          if (state.loadedOptions.some((opt) => String(opt.serialNumber || '').trim() === serial)) return;
          const labels = resolveCartWeightLabelsForSku(
            sku,
            [serial],
            Array.isArray(cartSummary.weightLabels) ? cartSummary.weightLabels : []
          );
          const label = labels[0] || serial;
          const stub = {
            id: serial,
            label,
            serialNumber: serial,
            inCart: true,
            fields: [{ label: 'Weight', value: label }],
          };
          state.inCartOptionIds.add(stub.id);
          state.loadedOptions.push(stub);
        });
        state.loadedOptions.sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));
        state.insufficientAvailable = false;

        // MODAL DRAFT = selectedIds. Initialize from CURRENT matrix/pending selection
        // (auto-select + Add Selection). Fall back to cart only when matrix has none.
        // clearBaselineIds stays cart-only so Clear never treats pending as committed.
        const matrixPendingIds = resolveMatrixPendingWeightSelectionIds(state);
        const cartOnlyIds = resolveCartWeightSelectionIds(state, cartSerialSet);
        state.selectedIds = matrixPendingIds.length ? matrixPendingIds : cartOnlyIds.slice();
        state.clearBaselineIds = cartOnlyIds.slice();

        if (!state.loadedOptions.length) {
          setModalFetchUi(state, 'empty');
          updateModalSummary(state);
          return;
        }

        setModalFetchUi(state, 'success');
        renderModalCheckboxes(state);
        updateModalSummary(state);
      })
      .catch((err) => {
        if (runId !== state.requestSeq || state.activeSku !== sku) return;
        state.loadedOptions = [];
        state.selectedIds = [];
        state.clearBaselineIds = [];
        state.insufficientAvailable = false;
        setModalFetchUi(state, 'error');
        const message =
          err && typeof err.message === 'string' && err.message.indexOf('Weight information') >= 0
            ? err.message
            : 'Weight information is currently unavailable. Please try again.';
        showToast(message, 'error');
      });
  }

  /**
   * @param {unknown} productType
   * @returns {boolean}
   */
  function isChainProductType(productType) {
    const normalized = normalizeKey(productType);
    return normalized === 'chain' || normalized === 'chains';
  }

  /**
   * @param {unknown} productType
   * @returns {boolean}
   */
  function isBraceletProductType(productType) {
    const normalized = normalizeKey(productType);
    return normalized === 'bracelet' || normalized === 'bracelets';
  }

  /**
   * @param {unknown} productType
   * @returns {boolean}
   */
  function isMatrixProductType(productType) {
    return isChainProductType(productType) || isBraceletProductType(productType);
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @param {Record<string, unknown> | null | undefined} [product]
   * @returns {boolean}
   */
  function isChainProductContext(detailsRoot, product) {
    const datasetFlag = detailsRoot.dataset.isChainProduct;
    if (datasetFlag === 'true') return true;
    if (datasetFlag === 'false') return false;
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    if (matrixHost instanceof HTMLElement && matrixHost.dataset.productType) {
      return isMatrixProductType(matrixHost.dataset.productType);
    }
    if (product && typeof product === 'object') {
      return isMatrixProductType(product.product_type || product.type);
    }
    return false;
  }

  /**
   * @param {unknown} value
   * @param {string[]} suffixes
   */
  function stripUnitSuffix(value, suffixes) {
    let text = normalizeString(value);
    if (!text) return '';
    suffixes.forEach((suffix) => {
      const re = new RegExp(`\\s*${suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      text = text.replace(re, '').trim();
    });
    return text.replace(/["']/g, '').trim();
  }

  /** @param {unknown} value */
  function formatModalWidth(value) {
    const raw = stripUnitSuffix(value, ['mm', 'millimeter', 'millimeters']);
    if (!raw) return '';
    return `${raw} MM`;
  }

  /** @param {unknown} value */
  function formatModalLength(value) {
    const raw = stripUnitSuffix(value, ['inches', 'inch', 'in']);
    if (!raw) return '';
    return `${raw} Inches`;
  }

  /**
   * @param {Record<string, unknown> | null | undefined} product
   * @param {number} variantId
   */
  function findProductVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !Number.isFinite(variantId) || variantId <= 0) {
      return null;
    }
    return (
      /** @type {Record<string, unknown> | undefined} */ (
        product.variants.find((entry) => Number(entry && entry.id) === variantId)
      ) || null
    );
  }

  /**
   * @param {HTMLElement} detailsRoot
   * @param {HTMLElement} row
   * @param {HTMLButtonElement | null | undefined} [trigger]
   */
  function getMatrixContextFromRow(detailsRoot, row, trigger) {
    const product = parseDetailsProductJson(detailsRoot);
    const variantId = Number(row.dataset.variantId);
    const variant = findProductVariant(product, variantId);
    const matrixHost = detailsRoot.querySelector('[data-chain-matrix]');
    const productType =
      (matrixHost instanceof HTMLElement && matrixHost.dataset.productType) ||
      (product && (product.product_type || product.type)) ||
      detailsRoot.dataset.productType ||
      '';

    let width =
      (trigger instanceof HTMLElement && trigger.dataset.matrixWidth) ||
      row.dataset.matrixWidth ||
      '';
    let length =
      (trigger instanceof HTMLElement && trigger.dataset.matrixLength) ||
      row.dataset.matrixLength ||
      '';

    if ((!width || !length) && variant && product) {
      const { widthIdx, lengthIdx, fixedWidth } = resolveChainAxes(product);
      width =
        width ||
        (widthIdx >= 0 ? getVariantOptionValue(variant, widthIdx) : '') ||
        fixedWidth ||
        '';
      length = length || getVariantOptionValue(variant, lengthIdx);
      width = normalizeWidthLabel(width);
    }

    return {
      variantId,
      sku: normalizeString(row.dataset.variantSku || (variant && variant.sku)),
      width,
      length,
      productType: normalizeString(productType),
      triggerElement: trigger || null,
    };
  }

  /**
   * @param {HTMLElement} modal
   * @param {{ width?: string, length?: string, productType?: string }} context
   * @param {HTMLElement} detailsRoot
   */
  function updateWeightModalDimensions(modal, context, detailsRoot) {
    const dimensionsWrap = modal.querySelector('[data-weight-modal-dimensions]');
    const widthRow = modal.querySelector('[data-dimension="width"]');
    const lengthRow = modal.querySelector('[data-dimension="length"]');
    const widthValue = modal.querySelector('[data-modal-width]');
    const lengthValue = modal.querySelector('[data-modal-length]');
    const showDimensions =
      isMatrixProductType(context.productType) ||
      isChainProductContext(detailsRoot, parseDetailsProductJson(detailsRoot));

    if (dimensionsWrap instanceof HTMLElement) dimensionsWrap.hidden = !showDimensions;
    if (widthRow instanceof HTMLElement) widthRow.hidden = !showDimensions;
    if (lengthRow instanceof HTMLElement) lengthRow.hidden = !showDimensions;

    if (!showDimensions) {
      if (widthValue instanceof HTMLElement) widthValue.textContent = '';
      if (lengthValue instanceof HTMLElement) lengthValue.textContent = '';
      return;
    }

    const formattedWidth = formatModalWidth(context.width);
    const formattedLength = formatModalLength(context.length);
    if (widthValue instanceof HTMLElement) widthValue.textContent = formattedWidth;
    if (lengthValue instanceof HTMLElement) lengthValue.textContent = formattedLength;

    if (!formattedWidth || !formattedLength) {
      if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[chain-matrix] missing width/length for weight modal', {
          variantId: context.variantId || context.sku,
          width: context.width,
          length: context.length,
        });
      }
    }
  }

  /** @param {HTMLElement} detailsRoot @param {HTMLElement} row */
  function syncWeightModalProductImage(detailsRoot, row) {
    const modal = detailsRoot.querySelector('[data-order-sheet-weight-modal]');
    if (!(modal instanceof HTMLElement)) return;
    const imageEl = modal.querySelector('.order-sheet-weight-modal__product-image');
    if (!(imageEl instanceof HTMLImageElement)) return;

    const product = parseDetailsProductJson(detailsRoot);
    const variantId = Number(row.dataset.variantId);
    const variantSrc = getVariantImageSrc(product, variantId);
    const productSrc = getProductFallbackImageSrc(product);
    const finalSrc = variantSrc || productSrc || WEIGHT_MODAL_FALLBACK_IMAGE;
    const altText = normalizeString(product && product.title) || 'Product image';
    const requestToken = String(Date.now()) + '-' + String(variantId || 0);
    imageEl.dataset.modalImageRequest = requestToken;

    imageEl.src = WEIGHT_MODAL_FALLBACK_IMAGE;
    imageEl.alt = altText;
    imageEl.classList.add('order-sheet-weight-modal__product-image--placeholder');
    imageEl.decoding = 'async';
    imageEl.loading = 'eager';

    if (finalSrc === WEIGHT_MODAL_FALLBACK_IMAGE) return;

    const preload = new Image();
    preload.decoding = 'async';
    preload.onload = function () {
      if (imageEl.dataset.modalImageRequest !== requestToken) return;
      imageEl.src = finalSrc;
      imageEl.alt = altText;
      imageEl.classList.remove('order-sheet-weight-modal__product-image--placeholder');
    };
    preload.onerror = function () {
      if (imageEl.dataset.modalImageRequest !== requestToken) return;
      imageEl.src = WEIGHT_MODAL_FALLBACK_IMAGE;
      imageEl.alt = altText;
      imageEl.classList.add('order-sheet-weight-modal__product-image--placeholder');
    };
    preload.src = finalSrc;
  }

  /** @param {HTMLElement} detailsRoot @param {HTMLElement} row @param {HTMLButtonElement | null} [trigger] */
  function openWeightModal(detailsRoot, row, trigger) {
    if (!(row instanceof HTMLElement)) return;
    const cartBaseline = getRowCartBaselineQty(row);
    if (!isRowAvailable(row) && cartBaseline <= 0) return;
    const wmsStatus = String(row.dataset.wmsStatus || '');
    if (wmsStatus === 'coming-soon' || wmsStatus === 'loading') return;
    if (wmsStatus === 'all-in-cart' && cartBaseline <= 0) return;

    const state = getWeightModalState(detailsRoot);
    if (!state) return;

    const weightTrigger =
      trigger instanceof HTMLButtonElement
        ? trigger
        : /** @type {HTMLButtonElement | null} */ (row.querySelector('[data-details-weight-open]'));

    // Preserve CURRENT matrix/pending selection. Modal draft starts from that
    // after fetch — never wipe auto-select / Add Selection on open.
    state.activeRow = row;
    state.openRowSnapshot = captureRowSelectionSnapshot(row);
    state.selectionConfirmed = false;
    state.loadedOptions = [];
    state.selectedIds = [];
    state.clearBaselineIds = [];
    state.inCartOptionIds = new Set();
    state.requestSeq += 1;
    state.activeSku = '';
    state.insufficientAvailable = false;
    syncWeightModalProductImage(detailsRoot, row);

    const modalMeta = state.modal.querySelector('.order-sheet-weight-modal__product-meta');
    const matrixContext = getMatrixContextFromRow(detailsRoot, row, weightTrigger);
    updateWeightModalDimensions(state.modal, matrixContext, detailsRoot);
    if (modalMeta instanceof HTMLElement && !state.modal.querySelector('[data-weight-modal-dimensions]')) {
      const formattedWidth = formatModalWidth(matrixContext.width);
      const formattedLength = formatModalLength(matrixContext.length);
      if (isChainProductContext(detailsRoot, parseDetailsProductJson(detailsRoot))) {
        modalMeta.innerHTML = `<span>Length: ${escapeHtml(formattedLength || '—')}</span><span aria-hidden="true">•</span><span>Width: ${escapeHtml(formattedWidth || '—')}</span>`;
      } else {
        modalMeta.innerHTML = '';
        modalMeta.hidden = true;
      }
    }

    const els = getModalEls(state);

    if (els.status) els.status.textContent = '';
    if (els.optionsList) els.optionsList.innerHTML = '';
    if (els.summary) els.summary.textContent = '0';
    if (els.summaryQty) els.summaryQty.textContent = '0';
    if (els.summaryTotal) els.summaryTotal.textContent = '0.00g';
    if (els.dropdownCount) els.dropdownCount.textContent = '';
    if (els.dropdownSearch) els.dropdownSearch.value = '';
    if (els.dropdownEmpty) els.dropdownEmpty.hidden = true;
    if (els.loading) els.loading.hidden = true;
    if (els.empty) els.empty.hidden = true;
    setWeightOptionsVisible(state, false);
    if (els.confirm) els.confirm.disabled = true;
    state.modal.querySelectorAll('[data-order-sheet-weight-close]').forEach((node) => {
      if (!(node instanceof HTMLButtonElement)) return;
      const text = String(node.textContent || '').trim().toLowerCase();
      if (!text || text === '×' || text === 'x') return;
      node.textContent = 'Clear';
      node.dataset.orderSheetWeightClear = 'true';
    });

    setModalFetchUi(state, 'idle');
    registerWeightModalState(state);
    bindWeightSearch(state);
    portalWeightModal(state);
    state.modal.hidden = false;
    document.body.classList.add('order-sheet-weight-modal-open');
    submitWeightFetch(state);
    if (els.dropdownSearch) els.dropdownSearch.focus();
  }

  /** @param {HTMLElement} detailsRoot */
  function closeWeightModal(detailsRoot) {
    const state = getWeightModalState(detailsRoot);
    if (!state || state.modal.hidden) return;
    const rowBeingClosed = state.activeRow;
    const openSnapshot = state.openRowSnapshot;
    const selectionConfirmed = state.selectionConfirmed === true;
    updateWeightModalDimensions(state.modal, { width: '', length: '', productType: '' }, detailsRoot);
    state.modal.hidden = true;
    state.activeRow = null;
    state.loadedOptions = [];
    state.selectedIds = [];
    state.clearBaselineIds = [];
    state.inCartOptionIds = new Set();
    state.openRowSnapshot = null;
    state.selectionConfirmed = false;
    state.requestSeq += 1;
    setWeightOptionsVisible(state, false);
    restoreWeightModalToAnchor(state);
    document.body.classList.remove('order-sheet-weight-modal-open');

    // X / backdrop close: discard MODAL DRAFT only — restore matrix/pending
    // from the open-time snapshot. Add Selection sets selectionConfirmed so
    // the confirmed matrix draft is kept.
    if (rowBeingClosed instanceof HTMLElement) {
      if (!selectionConfirmed) {
        restoreRowSelectionSnapshot(rowBeingClosed, openSnapshot);
      } else {
        syncRowMinusState(rowBeingClosed);
        syncRowWeightActionLabel(rowBeingClosed);
      }
      const panel = detailsRoot.closest(
        '[data-plp-details-panel], [data-order-sheet-details-panel], [data-quick-view-details-panel]'
      );
      if (panel instanceof HTMLElement) syncBulkMap(panel, rowBeingClosed);
      updateAddSelectedState(detailsRoot);
      notifyQtyUpdated(detailsRoot);
      updateDetailsSummary(detailsRoot);
    }
  }

  /**
   * Clear = keep ONLY weights that are actually in the live cart.
   * Removes auto-select, modal draft, and Add Selection pending. Modal stays open.
   * Does NOT call cart mutation APIs or onClose.
   * @param {WeightModalState} state
   */
  function clearWeightModalSelection(state) {
    if (!state.activeRow) return;

    const row = state.activeRow;
    const sku = state.activeSku || getRowSku(row);
    const availabilityApi = getWmsAvailabilityApi();
    const cartSummary =
      availabilityApi && typeof availabilityApi.getCartSkuSummary === 'function' && sku
        ? availabilityApi.getCartSkuSummary(sku)
        : { serialNumbers: [], weightLabels: [], quantity: 0 };
    /** @type {Set<string>} */
    const cartSerialSet = new Set(
      (Array.isArray(cartSummary.serialNumbers) ? cartSummary.serialNumbers : [])
        .map((s) => String(s || '').trim())
        .filter(Boolean)
    );

    // Refresh in-cart flags from live cart so Clear never trusts a stale snapshot.
    state.inCartOptionIds = new Set();
    state.loadedOptions.forEach((opt) => {
      const serial = String(opt.serialNumber || '').trim();
      const inCart = serial !== '' && cartSerialSet.has(serial);
      opt.inCart = inCart;
      if (inCart) state.inCartOptionIds.add(opt.id);
    });

    // Ensure cart serials missing from the option list still appear as stubs.
    cartSerialSet.forEach((serial) => {
      if (state.loadedOptions.some((opt) => String(opt.serialNumber || '').trim() === serial)) return;
      const labels = resolveCartWeightLabelsForSku(
        sku,
        [serial],
        Array.isArray(cartSummary.weightLabels) ? cartSummary.weightLabels : []
      );
      const label = labels[0] || serial;
      const stub = {
        id: serial,
        label,
        serialNumber: serial,
        inCart: true,
        fields: [{ label: 'Weight', value: label }],
      };
      state.inCartOptionIds.add(stub.id);
      state.loadedOptions.push(stub);
    });
    state.loadedOptions.sort((a, b) => parseEntryWeightGrams(a) - parseEntryWeightGrams(b));

    let baselineIds = resolveCartWeightSelectionIds(state, cartSerialSet);
    // Fallback open-time cart snapshot only when live cart resolution is empty
    // but the row still reports cart units (transient sync gap).
    if (
      !baselineIds.length &&
      Array.isArray(state.clearBaselineIds) &&
      state.clearBaselineIds.length &&
      getRowCartBaselineQty(row) > 0
    ) {
      baselineIds = state.clearBaselineIds.slice();
    }

    state.selectedIds = baselineIds.slice();
    state.clearBaselineIds = baselineIds.slice();
    applyWeightSelectionIdsToRow(state, baselineIds);
    // Clear updates matrix/pending — refresh open snapshot so X-close keeps Clear result.
    state.openRowSnapshot = captureRowSelectionSnapshot(row);
    syncRowMinusState(row);

    const panel = state.detailsRoot.closest(
      '[data-plp-details-panel], [data-order-sheet-details-panel], [data-quick-view-details-panel]'
    );
    if (panel instanceof HTMLElement) syncBulkMap(panel, row);
    updateAddSelectedState(state.detailsRoot);
    notifyQtyUpdated(state.detailsRoot);
    updateDetailsSummary(state.detailsRoot);

    renderModalCheckboxes(state);
    updateModalSummary(state);
  }

  /**
   * Confirm Edit Weight → draft/matrix state only.
   * Must NEVER call /cart/add.js, /cart/change.js, /cart/update.js, or sku merge helpers.
   * @param {WeightModalState} state
   */
  function confirmWeightModal(state) {
    if (!state.activeRow || state.insufficientAvailable) return;
    const selectedCount = state.selectedIds.length;
    if (selectedCount <= 0) {
      showToast('Set quantity greater than 0 before confirming.', 'error');
      return;
    }

    const selectedOptions = state.selectedIds
      .map((id) => state.loadedOptions.find((o) => o.id === id))
      .filter(Boolean);
    const selectedLabels = selectedOptions.map((o) => o.label);
    const selectedSerials = selectedOptions.map((o) =>
      o.serialNumber != null && String(o.serialNumber).trim() ? String(o.serialNumber).trim() : ''
    );

    if (selectedSerials.some((s) => !s)) {
      showToast(
        'Selected weights are missing serial numbers. Fetch weight again — if this persists, contact support.',
        'error'
      );
      return;
    }

    const row = state.activeRow;
    const cartBaseline = getRowCartBaselineQty(row);
    const baselineSerials = normalizeSerialCsv(row.dataset.cartBaselineSerials || '');
    const nextSerials = normalizeSerialCsv(selectedSerials.join(','));
    if (selectedCount !== cartBaseline || nextSerials !== baselineSerials) {
      markRowSelectionDirty(row);
    } else {
      delete row.dataset.selectionDirty;
    }
    const rowInput = getRowQtyInput(row);
    if (rowInput) writeQty(rowInput, selectedCount, { silent: true });
    const displayInput = /** @type {HTMLInputElement | null} */ (
      row.querySelector('[data-details-qty-display], [data-qvm-qty-display-input]')
    );
    if (displayInput) displayInput.value = String(selectedCount);

    const panel = state.detailsRoot.closest(
      '[data-plp-details-panel], [data-order-sheet-details-panel], [data-quick-view-details-panel]'
    );
    syncRowMinusState(row);
    setRowWeightValue(row, selectedLabels.join(', '), selectedSerials.join(', '));
    // Mark confirmed BEFORE close so close keeps matrix draft (does not restore snapshot).
    state.selectionConfirmed = true;
    state.openRowSnapshot = captureRowSelectionSnapshot(row);
    if (panel instanceof HTMLElement) syncBulkMap(panel, row);
    updateAddSelectedState(state.detailsRoot);
    notifyQtyUpdated(state.detailsRoot);
    updateDetailsSummary(state.detailsRoot);
    closeWeightModal(state.detailsRoot);
  }

  /** @type {{ dialog: HTMLDialogElement, stage: HTMLElement, main: HTMLImageElement, thumbs: HTMLElement, prev: HTMLButtonElement, next: HTMLButtonElement, count: HTMLElement } | null} */
  let imageViewer = null;
  /** @type {{ src: string, alt: string }[]} */
  let imageViewerItems = [];
  let imageViewerIndex = 0;
  let imageViewerBodyOverflow = '';
  /** @type {HTMLElement | null} */
  let imageViewerTrigger = null;

  function ensureImageViewer() {
    if (imageViewer) return imageViewer;

    const dialog = document.createElement('dialog');
    dialog.className = 'aone-image-viewer';
    dialog.setAttribute('aria-label', 'Product image viewer');
    dialog.innerHTML = `
      <div class="aone-image-viewer__panel">
        <button type="button" class="aone-image-viewer__close" data-image-viewer-close aria-label="Close image viewer">×</button>
        <div class="aone-image-viewer__stage">
          <button type="button" class="aone-image-viewer__nav aone-image-viewer__nav--prev" data-image-viewer-prev aria-label="Previous image">‹</button>
          <img class="aone-image-viewer__image" alt="">
          <button type="button" class="aone-image-viewer__nav aone-image-viewer__nav--next" data-image-viewer-next aria-label="Next image">›</button>
        </div>
        <p class="aone-image-viewer__count" aria-live="polite"></p>
        <div class="aone-image-viewer__thumbs" role="list" aria-label="Product images"></div>
      </div>`;
    document.body.appendChild(dialog);

    const stage = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__stage'));
    const main = /** @type {HTMLImageElement | null} */ (dialog.querySelector('.aone-image-viewer__image'));
    const thumbs = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__thumbs'));
    const prev = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-image-viewer-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (dialog.querySelector('[data-image-viewer-next]'));
    const count = /** @type {HTMLElement | null} */ (dialog.querySelector('.aone-image-viewer__count'));
    if (!stage || !main || !thumbs || !prev || !next || !count) return null;

    const close = () => dialog.close();
    dialog.querySelector('[data-image-viewer-close]')?.addEventListener('click', close);
    prev.addEventListener('click', () => renderImageViewer(imageViewerIndex - 1));
    next.addEventListener('click', () => renderImageViewer(imageViewerIndex + 1));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        renderImageViewer(imageViewerIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        renderImageViewer(imageViewerIndex + 1);
      }
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = imageViewerBodyOverflow;
      imageViewerTrigger?.focus();
      imageViewerTrigger = null;
    });

    imageViewer = { dialog, stage, main, thumbs, prev, next, count };
    return imageViewer;
  }

  /** @param {number} requestedIndex */
  function renderImageViewer(requestedIndex) {
    const viewer = ensureImageViewer();
    if (!viewer || !imageViewerItems.length) return;
    const length = imageViewerItems.length;
    imageViewerIndex = ((requestedIndex % length) + length) % length;
    const item = imageViewerItems[imageViewerIndex];
    if (!item) return;
    viewer.main.removeAttribute('width');
    viewer.main.removeAttribute('height');
    viewer.main.src = item.src;
    viewer.main.alt = item.alt;
    viewer.count.textContent = `${imageViewerIndex + 1} / ${length}`;
    viewer.prev.hidden = length < 2;
    viewer.next.hidden = length < 2;
    viewer.stage.classList.toggle('aone-image-viewer__stage--single', length < 2);
    viewer.thumbs.querySelectorAll('[data-image-viewer-thumb]').forEach((thumb, index) => {
      thumb.classList.toggle('is-active', index === imageViewerIndex);
      thumb.setAttribute('aria-current', index === imageViewerIndex ? 'true' : 'false');
    });
  }

  /**
   * @param {Array<string | { src?: string, alt?: string }>} images
   * @param {number} [activeIndex]
   * @param {HTMLElement | null} [trigger]
   */
  function openImageViewer(images, activeIndex = 0, trigger = null) {
    const normalized = images
      .map((item) =>
        typeof item === 'string'
          ? { src: item, alt: '' }
          : { src: String(item?.src || ''), alt: String(item?.alt || '') }
      )
      .filter((item) => item.src);
    if (!normalized.length) return;

    const viewer = ensureImageViewer();
    if (!viewer) return;
    imageViewerItems = normalized;
    imageViewerTrigger = trigger;
    viewer.thumbs.innerHTML = '';
    normalized.forEach((item, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'aone-image-viewer__thumb';
      button.setAttribute('data-image-viewer-thumb', '');
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-label', `View image ${index + 1}`);
      const image = document.createElement('img');
      image.src = item.src;
      image.alt = item.alt;
      image.loading = 'lazy';
      button.appendChild(image);
      button.addEventListener('click', () => renderImageViewer(index));
      viewer.thumbs.appendChild(button);
    });
    renderImageViewer(activeIndex);
    imageViewerBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (!viewer.dialog.open) viewer.dialog.showModal();
  }

  /** @param {HTMLElement} gallery */
  function ensureGalleryViewButton(gallery) {
    gallery.querySelectorAll('.plp-exp-media__zoom').forEach((zoom) => zoom.remove());
    let button = gallery.querySelector('[data-plp-view-images]');
    const legacyLink = gallery.querySelector('.plp-exp-media__view-link');
    if (!button && legacyLink) {
      button = document.createElement('button');
      legacyLink.replaceWith(button);
    }
    if (!button) {
      button = document.createElement('button');
      gallery.querySelector('.plp-exp-media__main')?.insertAdjacentElement('afterend', button);
    }
    if (!(button instanceof HTMLButtonElement)) return;
    button.type = 'button';
    button.className = 'plp-exp-media__view-button';
    button.setAttribute('data-plp-view-images', '');
    button.setAttribute('aria-label', 'View product images');
    button.innerHTML = `
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none">
        <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.7"/>
        <circle cx="9" cy="10" r="2" stroke="currentColor" stroke-width="1.7"/>
        <path d="m5 18 5-5 3 3 2-2 4 4" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>View Images</span>`;
  }

  /**
   * Resolve a usable image URL from an <img> (handles srcset-only / lazy cases).
   * @param {HTMLImageElement | null} img
   * @returns {string}
   */
  function resolveImageSrc(img) {
    if (!(img instanceof HTMLImageElement)) return '';
    const fromAttr =
      img.getAttribute('data-thumb-src') ||
      img.getAttribute('data-image-src') ||
      img.getAttribute('data-src') ||
      img.getAttribute('src') ||
      '';
    if (fromAttr) return fromAttr;
    if (img.currentSrc) return img.currentSrc;
    const srcset = img.getAttribute('srcset') || '';
    if (!srcset) return '';
    const first = srcset.split(',')[0]?.trim().split(/\s+/)[0] || '';
    return first;
  }

  /** @param {HTMLElement} gallery */
  function getGalleryViewerData(gallery) {
    const popupItems = Array.from(
      gallery.querySelectorAll('[data-qvm-popup-image], [data-plp-popup-image]')
    );
    const popupImages = popupItems
      .map((item) => ({
        src: item.getAttribute('data-image-src') || '',
        alt: item.getAttribute('data-image-alt') || '',
      }))
      .filter((item) => item.src);
    if (popupImages.length) {
      const activePopup = gallery.querySelector(
        '[data-qvm-popup-image][data-active-image], [data-plp-popup-image][data-active-image]'
      );
      const activeSrc = activePopup?.getAttribute('data-image-src') || '';
      const activeIndex = Math.max(0, popupImages.findIndex((item) => item.src === activeSrc));
      return { images: popupImages, activeIndex };
    }

    const thumbs = Array.from(gallery.querySelectorAll('[data-plp-exp-thumb]'));
    const images = thumbs
      .map((thumb) => ({
        src: thumb.getAttribute('data-thumb-src') || resolveImageSrc(thumb.querySelector('img')),
        alt: thumb.querySelector('img')?.getAttribute('alt') || '',
      }))
      .filter((item) => item.src);
    const activeIndex = Math.max(
      0,
      thumbs.findIndex((thumb) => thumb.classList.contains('is-active'))
    );
    if (images.length) return { images, activeIndex };

    // Single-image products omit thumbnails — fall back to the main stage image.
    const main =
      gallery.querySelector('[data-plp-exp-main-img]') ||
      gallery.querySelector('[data-plp-exp-main-media] img.plp-exp-media__img') ||
      gallery.querySelector('[data-plp-exp-main-media] img') ||
      gallery.querySelector('.plp-exp-media__img');
    const mainSrc =
      gallery.getAttribute('data-gallery-src') ||
      (main instanceof HTMLImageElement ? resolveImageSrc(main) : '');
    const mainAlt = main instanceof HTMLImageElement ? main.getAttribute('alt') || '' : '';
    return {
      images: mainSrc ? [{ src: mainSrc, alt: mainAlt }] : [],
      activeIndex: 0,
    };
  }

  /** @param {HTMLElement} panel */
  function bindPanel(panel) {
    if (panel.dataset.plpDetailsBound === 'true') return;
    panel.dataset.plpDetailsBound = 'true';

    const details = panel.querySelector('[data-plp-expanded-details]');
    if (!details) return;
    // Quick View owns its matrix HTML + WMS/cart reconciliation. Running hydrate here
    // races QV open and can leave Available Stock positive while qty/weight stay disabled.
    const isQuickViewPanel =
      panel.hasAttribute('data-quick-view-details-panel') ||
      (details instanceof HTMLElement && details.hasAttribute('data-quick-view-details'));
    if (!isQuickViewPanel) {
      buildChainMatrix(/** @type {HTMLElement} */ (details));
      hydrateDetailsWmsAvailability(/** @type {HTMLElement} */ (details));
    }
    panel.querySelectorAll('[data-plp-exp-media]').forEach((gallery) => {
      if (gallery instanceof HTMLElement) ensureGalleryViewButton(gallery);
    });

    const weightState = getWeightModalState(/** @type {HTMLElement} */ (details));
    if (weightState) {
      bindWeightSearch(weightState);
      const modal = weightState.modal;
      if (modal.dataset.orderSheetWeightModalBound !== 'true') {
        modal.dataset.orderSheetWeightModalBound = 'true';

      modal.addEventListener('click', (e) => {
        const closeEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-close]') : null;
        const retryEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-retry]') : null;
        const confirmEl = e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-confirm]') : null;
        const rowMinus =
          e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-row-minus]') : null;
        const rowPlus =
          e.target instanceof Element ? e.target.closest('[data-order-sheet-weight-row-plus]') : null;

        if (closeEl) {
          e.preventDefault();
          if (
            closeEl instanceof HTMLButtonElement &&
            closeEl.dataset.orderSheetWeightClear === 'true'
          ) {
            clearWeightModalSelection(weightState);
          } else {
            closeWeightModal(/** @type {HTMLElement} */ (details));
          }
          return;
        }
        if (rowMinus || rowPlus) {
          e.preventDefault();
          const rowEl = e.target instanceof Element ? e.target.closest('[data-group-index]') : null;
          if (!(rowEl instanceof HTMLElement)) return;
          const groupIndex = Number(rowEl.dataset.groupIndex);
          if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
          const input = /** @type {HTMLInputElement | null} */ (
            rowEl.querySelector('[data-order-sheet-weight-row-input]')
          );
          const current = input ? readQty(input) : 0;
          const next = rowPlus ? current + 1 : Math.max(0, current - 1);
          setWeightGroupSelection(weightState, groupIndex, next);
          return;
        }
        if (retryEl) {
          e.preventDefault();
          submitWeightFetch(weightState);
          return;
        }
        if (confirmEl) {
          e.preventDefault();
          confirmWeightModal(weightState);
        }
      });

      modal.addEventListener('input', (e) => {
        const input =
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-order-sheet-weight-row-input]')
            ? e.target
            : null;
        if (!input) return;
        const rowEl = input.closest('[data-group-index]');
        if (!(rowEl instanceof HTMLElement)) return;
        const groupIndex = Number(rowEl.dataset.groupIndex);
        if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
        setWeightGroupSelection(weightState, groupIndex, readQty(input));
      });

      modal.addEventListener('change', (e) => {
        const input =
          e.target instanceof HTMLInputElement &&
          e.target.matches('[data-order-sheet-weight-row-input]')
            ? e.target
            : null;
        if (!input) return;
        const rowEl = input.closest('[data-group-index]');
        if (!(rowEl instanceof HTMLElement)) return;
        const groupIndex = Number(rowEl.dataset.groupIndex);
        if (!Number.isFinite(groupIndex) || groupIndex < 0) return;
        setWeightGroupSelection(weightState, groupIndex, readQty(input));
      });
      }
    }

    panel.addEventListener('click', (e) => {
      const viewImages = /** @type {HTMLElement | null} */ (
        e.target instanceof Element ? e.target.closest('[data-plp-view-images]') : null
      );
      if (viewImages && panel.contains(viewImages)) {
        e.preventDefault();
        e.stopPropagation();
        const gallery = viewImages.closest('[data-plp-exp-media]');
        if (gallery instanceof HTMLElement) {
          const { images, activeIndex } = getGalleryViewerData(gallery);
          openImageViewer(images, activeIndex, viewImages);
        }
        return;
      }

      const thumb = /** @type {HTMLElement | null} */ (
        e.target instanceof Element ? e.target.closest('[data-plp-exp-thumb]') : null
      );
      if (thumb && panel.contains(thumb)) {
        e.stopPropagation();
        const src = thumb.getAttribute('data-thumb-src');
        const mainWrap = panel.querySelector('[data-plp-exp-main-media]');
        if (src && mainWrap) {
          panel.querySelectorAll('[data-plp-exp-thumb]').forEach((t) => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
          const mediaType = thumb.getAttribute('data-media-type');
          if (mediaType === 'image' || !mediaType || mediaType === 'model') {
            mainWrap.innerHTML = `<img class="plp-exp-media__img" src="${escapeHtml(src)}" alt="" loading="lazy" data-plp-exp-main-img />`;
          }
        }
        return;
      }

      const weightOpen = e.target instanceof Element ? e.target.closest('[data-details-weight-open]') : null;

      if (weightOpen instanceof HTMLButtonElement && details.contains(weightOpen)) {
        e.preventDefault();
        e.stopPropagation();
        if (weightOpen.disabled) return;
        const row = weightOpen.closest('[data-details-variant-row]');
        if (!(row instanceof HTMLElement)) return;
        const cartBaseline = getRowCartBaselineQty(row);
        if (!isRowAvailable(row) && cartBaseline <= 0) return;
        openWeightModal(/** @type {HTMLElement} */ (details), row, weightOpen);
        return;
      }

      const clickEl =
        e.target instanceof Element ? e.target : e.target instanceof Node ? e.target.parentElement : null;
      const minusBtn = clickEl ? clickEl.closest('[data-details-qty-minus]') : null;
      const plusBtn = clickEl ? clickEl.closest('[data-details-qty-plus]') : null;
      const addBtn = clickEl ? clickEl.closest('[data-details-add-selected]') : null;

      if (minusBtn || plusBtn) {
        e.preventDefault();
        e.stopPropagation();
        const trigger = minusBtn || plusBtn;
        const row = trigger instanceof Element ? trigger.closest('[data-details-variant-row]') : null;
        if (!(row instanceof HTMLElement) || !details.contains(row)) return;
        const cartBaseline = getRowCartBaselineQty(row);
        if (!isRowAvailable(row) && cartBaseline <= 0) return;
        const input = getRowQtyInput(row);
        if (!input || input.disabled) return;
        const cur = readQty(input);
        const next = minusBtn ? Math.max(0, cur - 1) : cur + 1;
        // Modal-only SKU rows: reconcile auto-selects lowest WMS weights (Quick View parity).
        reconcileRowQuantitySelection(row, next);
        return;
      }

      if (addBtn instanceof HTMLButtonElement && details.contains(addBtn)) {
        e.preventDefault();
        e.stopPropagation();
        if (addBtn.disabled || addBtn.dataset.addState === 'loading') return;
        addSelectedToCart(panel, /** @type {HTMLElement} */ (details));
      }
    });

    const onQtyInput = (e) => {
      const displayInput =
        e.target instanceof HTMLInputElement &&
        e.target.matches('[data-details-qty-display], [data-qvm-qty-display-input]')
          ? e.target
          : null;
      if (displayInput) {
        const row = displayInput.closest('[data-details-variant-row]');
        if (!(row instanceof HTMLElement)) return;
        const cartBaseline = getRowCartBaselineQty(row);
        if (!isRowAvailable(row) && cartBaseline <= 0) return;
        const digitsOnly = String(displayInput.value || '').replace(/[^\d]/g, '');
        if (digitsOnly !== displayInput.value) displayInput.value = digitsOnly;
        if (e.type !== 'change' && e.type !== 'blur' && digitsOnly === '') return;
        const next = digitsOnly === '' ? 0 : Number.parseInt(digitsOnly, 10);
        reconcileRowQuantitySelection(row, Number.isFinite(next) ? next : 0);
        return;
      }

      const input =
        e.target instanceof HTMLInputElement && e.target.matches('[data-details-qty-input]')
          ? e.target
          : null;
      if (!input) return;
      const row = input.closest('[data-details-variant-row]');
      const cartBaseline = row instanceof HTMLElement ? getRowCartBaselineQty(row) : 0;
      if (!(row instanceof HTMLElement) || (!isRowAvailable(row) && cartBaseline <= 0)) {
        if (cartBaseline > 0 && row instanceof HTMLElement) {
          applyCartSelectionToRow(row, { force: true });
          return;
        }
        writeQty(input, 0, { silent: true });
        if (row instanceof HTMLElement) {
          clearRowWeight(row);
          syncBulkMap(panel, row);
        }
        notifyQtyUpdated(/** @type {HTMLElement} */ (details));
        return;
      }
      if (isRowQtyModalOnly(row)) return;
      const sanitized = readQty(input);
      reconcileRowQuantitySelection(row, sanitized);
    };

    details.addEventListener('input', onQtyInput);
    details.addEventListener('change', onQtyInput);
    details.addEventListener('blur', onQtyInput, true);

    details.querySelectorAll('[data-details-variant-row]').forEach((row) => {
      if (row instanceof HTMLElement) {
        syncRowMinusState(row);
        syncWeightButtonState(row);
      }
    });
    updateAddSelectedState(details);
    updateDetailsSummary(details);
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} open
   */
  function setPanelOpen(item, open) {
    const panel = /** @type {HTMLElement | null} */ (item.querySelector(PANEL_SEL));
    const row = /** @type {HTMLElement | null} */ (item.querySelector(ROW_SEL));
    const btn = /** @type {HTMLButtonElement | null} */ (item.querySelector(TOGGLE_SEL));
    const textEl = btn?.querySelector('[data-plp-details-toggle-text]');

    if (!panel || !btn) return;

    if (open) {
      clearCloseTimer(panel);
      bindPanel(panel);
      panel.hidden = false;
      panel.classList.add('is-open');
      const details = panel.querySelector('[data-plp-expanded-details]');
      if (details instanceof HTMLElement) {
        resetVariantTable(details);
        hydrateDetailsWmsAvailability(details);
      }
      requestAnimationFrame(() => {
        panel.style.setProperty('--plp-details-height', `${panel.scrollHeight}px`);
      });
      row?.classList.add('inventory-product-row--expanded');
      if (row) {
        row.setAttribute('aria-expanded', 'true');
        if (panel.id) row.setAttribute('aria-controls', panel.id);
      }
      btn.setAttribute('aria-expanded', 'true');
      if (textEl) textEl.textContent = btn.dataset.labelClose || 'Close';
    } else {
      const details = panel.querySelector('[data-plp-expanded-details]');
      if (details instanceof HTMLElement) closeWeightModal(details);
      panel.classList.remove('is-open');
      panel.style.removeProperty('--plp-details-height');
      row?.classList.remove('inventory-product-row--expanded');
      if (row) {
        row.setAttribute('aria-expanded', 'false');
        if (panel.id) row.setAttribute('aria-controls', panel.id);
      }
      btn.setAttribute('aria-expanded', 'false');
      if (textEl) textEl.textContent = btn.dataset.labelOpen || 'View Details';
      clearCloseTimer(panel);
      const timer = window.setTimeout(() => {
        if (!panel.classList.contains('is-open')) {
          panel.hidden = true;
        }
        delete panel.dataset.closeTimerId;
      }, 380);
      panel.dataset.closeTimerId = String(timer);
    }
  }

  /** @param {HTMLElement} host @param {HTMLElement | null} exceptItem */
  function closeAllExcept(host, exceptItem) {
    host.querySelectorAll(ITEM_SEL).forEach((item) => {
      if (exceptItem && item === exceptItem) return;
      setPanelOpen(/** @type {HTMLElement} */ (item), false);
    });
  }

  /**
   * Clicks on these controls inside an order-sheet row should keep their normal behavior
   * (qty, checkbox, add to cart) instead of toggling the details panel.
   * @param {Element} target
   */
  function isOrderSheetRowInteractive(target) {
    return Boolean(
      target.closest(
        [
          'button',
          'input',
          'select',
          'textarea',
          'label',
          '[role="button"]',
          '[data-no-row-toggle]',
          'product-form-component',
          '[data-inventory-row-select]',
          '[data-quantity-minus]',
          '[data-quantity-plus]',
          '[data-inv-qty-adj]',
          '[data-quantity-input]',
          '[data-add-to-cart]',
          '.inventory-product-row__select',
          '.inventory-product-row__qty',
          '.inventory-product-row__add-btn',
          '.inventory-product-row__form',
        ].join(', ')
      )
    );
  }

  /** @param {string} value */
  function normalizeProductTypeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ');
  }

  /** @param {string} value */
  function textLooksLikeCharm(value) {
    return /(^|[^a-z])charm(s)?([^a-z]|$)/i.test(String(value || ''));
  }

  /** @param {string} value */
  function textLooksLikeChain(value) {
    return /(^|[^a-z])chain(s)?([^a-z]|$)/i.test(String(value || ''));
  }

  /** @param {string} value */
  function textLooksLikeBracelet(value) {
    return /(^|[^a-z])bracelet(s)?([^a-z]|$)/i.test(String(value || ''));
  }

  /**
   * Matrix products (chains, bracelets) keep Quick View behavior in Order Sheet.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} [row]
   * @param {Record<string, unknown> | null} [product]
   */
  function isChainOrderSheetProduct(item, row, product) {
    const hosts = [item, row].filter((el) => el instanceof HTMLElement);
    for (const el of hosts) {
      if (el.getAttribute('data-order-sheet-expandable') === 'false') return true;
      if (el.getAttribute('data-is-chain-product') === 'true') return true;
      const typeAttr = normalizeProductTypeKey(el.getAttribute('data-product-type') || '');
      if (
        typeAttr === 'chain' ||
        typeAttr === 'chains' ||
        textLooksLikeChain(typeAttr) ||
        typeAttr === 'bracelet' ||
        typeAttr === 'bracelets' ||
        textLooksLikeBracelet(typeAttr)
      ) {
        return true;
      }
    }

    if (product && typeof product === 'object') {
      const type = normalizeProductTypeKey(
        /** @type {any} */ (product).product_type ||
          /** @type {any} */ (product).type ||
          /** @type {any} */ (product).productType ||
          ''
      );
      if (
        type === 'chain' ||
        type === 'chains' ||
        textLooksLikeChain(type) ||
        type === 'bracelet' ||
        type === 'bracelets' ||
        textLooksLikeBracelet(type)
      ) {
        return true;
      }
      const tags = /** @type {any} */ (product).tags;
      if (
        Array.isArray(tags) &&
        tags.some((tag) => textLooksLikeChain(tag) || textLooksLikeBracelet(tag))
      ) {
        return true;
      }
      const title = /** @type {any} */ (product).title || /** @type {any} */ (product).name || '';
      if (textLooksLikeChain(title) || textLooksLikeBracelet(title)) return true;
    }

    const titleEl = item.querySelector('.inventory-product-row__title, .plp-card__title');
    if (
      titleEl &&
      (textLooksLikeChain(titleEl.textContent || '') ||
        textLooksLikeBracelet(titleEl.textContent || ''))
    ) {
      return true;
    }

    const path = String(window.location.pathname || '').toLowerCase();
    if (
      path.includes('/chains') ||
      path.includes('/chain-') ||
      path.includes('/bracelets') ||
      path.includes('/bracelet-')
    ) {
      return true;
    }

    return false;
  }

  /**
   * Order Sheet inline accordion is shared across non-matrix product rows.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} [row]
   * @param {Record<string, unknown> | null} [product]
   */
  function shouldUseInlineOrderSheetDetails(item, row, product) {
    const hosts = [item, row].filter((el) => el instanceof HTMLElement);
    for (const el of hosts) {
      if (el.getAttribute('data-order-sheet-expandable') === 'true') return true;
    }
    return !isChainOrderSheetProduct(item, row, product);
  }

  /** @param {HTMLElement} detailsRoot */
  function expandedDetailsHasContent(detailsRoot) {
    return Boolean(
      detailsRoot.querySelector(
        '[data-details-variant-table], [data-chain-matrix], [data-plp-product-json], .expanded-product-details__grid'
      )
    );
  }

  /** @param {HTMLElement} detailsRoot */
  function hasModernOrderSheetDetailsMarkup(detailsRoot) {
    if (!expandedDetailsHasContent(detailsRoot)) return false;
    if (detailsRoot.querySelector('[data-chain-matrix]')) return true;

    const tableHost = detailsRoot.querySelector('[data-details-variant-table]');
    if (!(tableHost instanceof HTMLElement)) return false;

    const hasModernHeaders = Boolean(
      tableHost.querySelector('.order-sheet-details-variant-table__col-min-weight') &&
        tableHost.querySelector('.order-sheet-details-variant-table__col-stock') &&
        tableHost.querySelector('.order-sheet-details-variant-table__col-qty') &&
        tableHost.querySelector('.order-sheet-details-variant-table__col-weight')
    );
    const hasModernFooter = Boolean(
      detailsRoot.querySelector('[data-details-selected-count]') &&
        detailsRoot.querySelector('[data-details-total-qty]') &&
        detailsRoot.querySelector('[data-details-total-weight]') &&
        detailsRoot.querySelector('[data-details-add-selected]')
    );

    return hasModernHeaders && hasModernFooter;
  }

  /**
   * Normalize a Shopify product handle from DOM attributes / product URLs.
   * Expects a handle slug — NOT numeric IDs, GIDs, or Wizzy-only IDs.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} [row]
   * @param {string} [hint]
   */
  function resolveOrderSheetProductHandle(item, row, hint) {
    const candidates = [
      hint,
      row?.getAttribute('data-product-handle'),
      item.getAttribute('data-product-handle'),
      item.querySelector('[data-plp-expanded-details]')?.getAttribute('data-product-handle'),
    ];

    for (const raw of candidates) {
      const normalized = normalizeProductHandleCandidate(raw);
      if (normalized) return normalized;
    }

    const gridItem = item.closest('.plp-product-grid__item, .wizzy-result-product');
    const links = (gridItem || item).querySelectorAll('a[href*="/products/"]');
    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const normalized = normalizeProductHandleCandidate(link.getAttribute('href') || '');
      if (normalized) {
        row?.setAttribute('data-product-handle', normalized);
        item.setAttribute('data-product-handle', normalized);
        return normalized;
      }
    }
    return '';
  }

  /** @param {unknown} value */
  function normalizeProductHandleCandidate(value) {
    let raw = String(value || '').trim();
    if (!raw || raw === 'undefined' || raw === 'null' || /\[\[/.test(raw)) return '';
    if (/^gid:\/\/shopify\/Product\//i.test(raw)) return '';
    try {
      if (/^https?:\/\//i.test(raw) || raw.startsWith('//') || raw.startsWith('/')) {
        const url = new URL(raw, window.location.origin);
        raw = url.pathname || raw;
      }
    } catch (_err) {
      /* keep raw */
    }
    const match = raw.match(/\/products\/([^/?#]+)/i);
    if (match && match[1]) raw = match[1];
    try {
      raw = decodeURIComponent(raw).trim().replace(/^\/+|\/+$/g, '');
    } catch (_err) {
      raw = raw.trim().replace(/^\/+|\/+$/g, '');
    }
    if (!raw || raw.includes('/') || /\[\[/.test(raw)) return '';
    // Reject bare numeric Shopify IDs / Wizzy IDs — product.js needs a handle slug.
    if (/^\d+$/.test(raw)) return '';
    return raw;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string[]}
   */
  function getProductOptionNames(product) {
    const options = Array.isArray(product?.options) ? product.options : [];
    return options
      .map((opt) => {
        if (typeof opt === 'string') return opt.trim();
        if (opt && typeof opt === 'object') return String(opt.name || '').trim();
        return '';
      })
      .filter(Boolean);
  }

  /**
   * Build Order Sheet expanded-details markup from /products/{handle}.js
   * using the same data-attribute contracts as plp-expanded-product-details.liquid
   * (not Quick View classes/markup).
   * @param {Record<string, any>} product
   * @param {string} rowUid
   */
  function buildOrderSheetExpandedDetailsHtml(product, rowUid) {
    const handle = String(product.handle || '').trim();
    const productId = String(product.id || '').trim();
    const title = String(product.title || 'Product');
    const type = String(product.type || product.product_type || 'Product');
    const normalizedType = normalizeProductTypeKey(type);
    const isChainProduct = normalizedType === 'chain' || normalizedType === 'chains';
    const isBraceletProduct = normalizedType === 'bracelet' || normalizedType === 'bracelets';
    const isMatrixProduct = isChainProduct || isBraceletProduct;
    const isCharmProduct =
      normalizedType === 'charm' || normalizedType === 'charms' || textLooksLikeCharm(normalizedType);
    const vendor = String(product.vendor || '');
    const variants = Array.isArray(product.variants) ? product.variants : [];
    // Render filter only. product.variants stays intact for cart / weight logic.
    const tableVariants = variants.filter((variant) => {
      if (!isVariantShopifyQtyKnown(variant)) return true;
      return resolveVariantShopifyQty(variant) > 0;
    });
    const optionNames = getProductOptionNames(product);
    const onlyDefault =
      variants.length <= 1 &&
      (optionNames.length === 0 ||
        (optionNames.length === 1 && /default title/i.test(optionNames[0])) ||
        String(variants[0]?.title || '') === 'Default Title');

    const images = [];
    if (Array.isArray(product.media)) {
      product.media.forEach((media) => {
        const src =
          media?.preview_image?.src ||
          media?.src ||
          (typeof media?.preview_image === 'string' ? media.preview_image : '');
        if (src) images.push({ src: String(src), alt: String(media?.alt || title) });
      });
    }
    if (!images.length && Array.isArray(product.images)) {
      product.images.forEach((img) => {
        const src = typeof img === 'string' ? img : img?.src;
        if (src) images.push({ src: String(src), alt: title });
      });
    }
    if (!images.length && product.featured_image) {
      const src =
        typeof product.featured_image === 'string'
          ? product.featured_image
          : product.featured_image.src;
      if (src) images.push({ src: String(src), alt: title });
    }

    const mainSrc = images[0]?.src || '';
    const thumbsHtml =
      images.length > 1
        ? `<div class="plp-exp-media__thumbs" role="tablist" aria-label="Gallery">${images
            .map(
              (img, index) => `
<button type="button" class="plp-exp-media__thumb${index === 0 ? ' is-active' : ''}" data-plp-exp-thumb="${index}" data-media-type="image" data-thumb-src="${escapeHtml(img.src)}" aria-label="Load image ${index + 1}" aria-selected="${index === 0 ? 'true' : 'false'}">
  <img src="${escapeHtml(img.src)}" alt="${escapeHtml(img.alt)}" loading="lazy" width="200" height="200" sizes="72px">
</button>`
            )
            .join('')}</div>`
        : '';

    const popupHtml = images
      .map(
        (img, index) =>
          `<span data-plp-popup-image data-image-src="${escapeHtml(img.src)}" data-image-alt="${escapeHtml(img.alt)}"${index === 0 ? ' data-active-image' : ''} hidden></span>`
      )
      .join('');

    const headerCells = onlyDefault
      ? `<th scope="col">Variant</th>`
      : optionNames.map((name) => `<th scope="col">${escapeHtml(name)}</th>`).join('');
    const optionColGroup = onlyDefault
      ? `<col class="order-sheet-details-variant-table__col-option">`
      : optionNames.map(() => `<col class="order-sheet-details-variant-table__col-option">`).join('');

    const rowsHtml = tableVariants
      .map((variant) => {
        const variantId = String(variant.id || '');
        const sku = String(variant.sku || '').trim();
        const shopifyKnown = isVariantShopifyQtyKnown(variant);
        const shopifyQty = shopifyKnown ? resolveVariantShopifyQty(variant) : 0;
        const shopifyStockLabel = shopifyKnown
          ? formatShopifyStockLabel(shopifyQty, { numericOnly: true })
          : '';
        const shopifyAttr = shopifyKnown ? ` data-shopify-inventory-qty="${shopifyQty}"` : '';
        const inputId = `PlpDetailsQty-${rowUid}-${variantId}`;
        const optionCells = onlyDefault
          ? `<td class="order-sheet-details-variant-table__option">${escapeHtml(
              variant.title && variant.title !== 'Default Title' ? variant.title : 'Variant'
            )}</td>`
          : optionNames
              .map((_, index) => {
                const key = `option${index + 1}`;
                const value =
                  variant[key] || (Array.isArray(variant.options) ? variant.options[index] : '') || '—';
                return `<td class="order-sheet-details-variant-table__option">${escapeHtml(String(value))}</td>`;
              })
              .join('');

        const weightAction = sku
          ? `<button type="button" class="order-sheet-details-variant-table__weight-btn" data-details-weight-open aria-haspopup="dialog" disabled>
               <span data-details-weight-label>Select weight</span>
             </button>
             <input type="hidden" id="${escapeHtml(inputId)}" data-details-qty-input data-details-qty-modal-only value="0">`
          : `<button type="button" class="order-sheet-details-variant-table__weight-btn order-sheet-details-variant-table__weight-btn--coming-soon" disabled aria-disabled="true">
               <span>Coming Soon</span>
             </button>`;

        const qtyHtml = sku
          ? `<div class="order-sheet-details-qty quantity-control" role="group" aria-label="Quantity">
               <button type="button" class="order-sheet-details-qty__btn" data-details-qty-minus aria-label="Decrease quantity" disabled>−</button>
               <input type="number" id="${escapeHtml(inputId)}-display" class="order-sheet-details-qty__input" data-details-qty-display value="0" min="0" step="1" inputmode="numeric" aria-label="Quantity" disabled>
               <button type="button" class="order-sheet-details-qty__btn" data-details-qty-plus aria-label="Increase quantity" disabled>+</button>
             </div>`
          : `<div class="order-sheet-details-qty quantity-control" role="group" aria-label="Quantity">
               <button type="button" class="order-sheet-details-qty__btn" data-details-qty-minus aria-label="Decrease quantity" disabled>−</button>
               <input type="number" id="${escapeHtml(inputId)}" class="order-sheet-details-qty__input" value="0" min="0" step="1" inputmode="numeric" data-details-qty-input aria-label="Quantity" disabled>
               <button type="button" class="order-sheet-details-qty__btn" data-details-qty-plus aria-label="Increase quantity" disabled>+</button>
             </div>`;

        return `<tr class="order-sheet-details-variant-row order-sheet-details-variant-row--sold-out is-sold-out" data-details-variant-row data-variant-id="${escapeHtml(variantId)}" data-variant-sku="${escapeHtml(sku)}" data-variant-price-cents="${escapeHtml(String(variant.price || 0))}" data-variant-available="false" data-wms-status="loading"${shopifyAttr} data-variant-inventory-qty="0">
  ${optionCells}
  <td class="order-sheet-details-variant-table__sku">${escapeHtml(sku || '—')}</td>
  <td class="order-sheet-details-variant-table__min-weight">Checking...</td>
  <td class="order-sheet-details-variant-table__stock">
    <span class="order-sheet-details-variant-table__stock-badge order-sheet-details-variant-table__stock-badge--${shopifyKnown && shopifyQty > 0 ? 'available' : 'sold-out'}">${escapeHtml(shopifyStockLabel)}</span>
  </td>
  <td class="order-sheet-details-variant-table__qty-col">${qtyHtml}</td>
  <td class="order-sheet-details-variant-table__weight">${weightAction}</td>
</tr>`;
      })
      .join('');

    const featuredSrc = mainSrc || '';
    const productJson = JSON.stringify(product).replace(/</g, '\\u003c');
    const colSpan = Math.max(5, (onlyDefault ? 1 : optionNames.length) + 5);

    return `
<div class="expanded-product-details expanded-product-details--compact" data-plp-expanded-details data-product-id="${escapeHtml(productId)}" data-product-handle="${escapeHtml(handle)}" data-is-chain-product="${isMatrixProduct ? 'true' : 'false'}" data-is-charm-product="${isCharmProduct ? 'true' : 'false'}" data-section-id="wizzy-search" data-details-hydrated="true">
  <div class="expanded-product-details__grid">
    <div class="expanded-product-details__media-col">
      <div class="plp-exp-media" data-plp-exp-media${mainSrc ? ` data-gallery-src="${escapeHtml(mainSrc)}"` : ''}>
        <div class="plp-exp-media__main">
          <div class="plp-exp-media__main-inner" data-plp-exp-main-media>
            ${
              mainSrc
                ? `<img class="plp-exp-media__img" src="${escapeHtml(mainSrc)}" alt="${escapeHtml(title)}" loading="lazy" width="1600" height="1600" data-plp-exp-main-img="true">`
                : `<img class="plp-exp-media__img plp-exp-media__placeholder-svg" alt="${escapeHtml(title)}" loading="lazy" width="1600" height="1600">`
            }
          </div>
        </div>
        <button type="button" class="plp-exp-media__view-button" data-plp-view-images aria-label="View product images">
          <span>View Images</span>
        </button>
        <span class="plp-exp-media__popup-data" aria-hidden="true">${popupHtml}</span>
        ${thumbsHtml}
      </div>
    </div>
    <div class="expanded-product-details__info-col">
      <div class="expanded-product-meta">
        ${vendor ? `<p class="expanded-product-meta__item"><span class="expanded-product-meta__label">Collection</span><span class="expanded-product-meta__value">${escapeHtml(vendor)}</span></p>` : ''}
        <p class="expanded-product-meta__item"><span class="expanded-product-meta__label">Category</span><span class="expanded-product-meta__value">${escapeHtml(type || 'Product')}</span></p>
        <p class="expanded-product-meta__item"><span class="expanded-product-meta__label">Stock Status</span><span class="expanded-product-meta__value">Checking availability</span></p>
        <p class="expanded-product-meta__item"><span class="expanded-product-meta__label">Variants</span><span class="expanded-product-meta__value">${isMatrixProduct ? variants.length : tableVariants.length}</span></p>
      </div>
      <div class="expanded-product-details__variant-table" data-plp-exp-variant-table>
        <div class="order-sheet-details-variant-table-wrapper" data-details-variant-table>
          <div class="order-sheet-details-variant-table__sku-panel">
            <div class="order-sheet-details-variant-table__sku-panel-header">
              <span class="order-sheet-details-variant-table__sku-panel-title">SKUs</span>
              <span class="order-sheet-details-variant-table__sku-panel-count">(${tableVariants.length})</span>
            </div>
            <div class="variant-table-scroll order-sheet-details-variant-table__sku-panel-scroll">
              <table class="order-sheet-details-variant-table">
                <colgroup>
                  ${optionColGroup}
                  <col class="order-sheet-details-variant-table__col-sku">
                  <col class="order-sheet-details-variant-table__col-min-weight">
                  <col class="order-sheet-details-variant-table__col-stock">
                  <col class="order-sheet-details-variant-table__col-qty">
                  <col class="order-sheet-details-variant-table__col-weight">
                </colgroup>
                <thead>
                  <tr>
                    ${headerCells}
                    <th scope="col" class="order-sheet-details-variant-table__col-sku">SKU</th>
                    <th scope="col" class="order-sheet-details-variant-table__col-min-weight">Min Weight</th>
                    <th scope="col" class="order-sheet-details-variant-table__col-stock">Available Stock</th>
                    <th scope="col" class="order-sheet-details-variant-table__col-qty">Qty</th>
                    <th scope="col" class="order-sheet-details-variant-table__col-weight">Select</th>
                  </tr>
                </thead>
                <tbody>${rowsHtml || `<tr><td colspan="${colSpan}">${variants.length ? 'No variants currently in stock.' : 'No variants available.'}</td></tr>`}</tbody>
              </table>
            </div>
          </div>

          <div class="order-sheet-weight-modal" data-order-sheet-weight-modal hidden role="dialog" aria-modal="true" aria-labelledby="order-sheet-weight-modal-title-${escapeHtml(rowUid)}" data-weight-placeholder="Select weight" data-weight-insufficient-msg="Only {available} weight(s) available. You requested {required}.">
            <div class="order-sheet-weight-modal__backdrop" data-order-sheet-weight-close tabindex="-1"></div>
            <div class="order-sheet-weight-modal__dialog">
              <header class="order-sheet-weight-modal__header">
                <div class="order-sheet-weight-modal__product">
                  <div class="order-sheet-weight-modal__product-image-wrap">
                    ${
                      featuredSrc
                        ? `<img class="order-sheet-weight-modal__product-image" src="${escapeHtml(featuredSrc)}" alt="${escapeHtml(title)}" loading="lazy" width="160" height="160">`
                        : ''
                    }
                  </div>
                  <div class="order-sheet-weight-modal__product-content">
                    <p class="order-sheet-weight-modal__product-vendor">${escapeHtml(vendor || 'A-ONE GOLD & DIAMONDS LLC')}</p>
                    <h4 class="order-sheet-weight-modal__title" id="order-sheet-weight-modal-title-${escapeHtml(rowUid)}">${escapeHtml(title)}</h4>
                    <p class="order-sheet-weight-modal__subtitle">Choose an available weight and quantity.</p>
                  </div>
                </div>
                <button type="button" class="order-sheet-weight-modal__close" data-order-sheet-weight-close aria-label="Close"><span aria-hidden="true">×</span></button>
              </header>
              <div class="order-sheet-weight-modal__body" data-order-sheet-weight-block>
                <p class="order-sheet-weight-modal__status" data-order-sheet-weight-status aria-live="polite"></p>
                <div class="order-sheet-weight-modal__loading" data-order-sheet-weight-loading hidden>
                  <div class="order-sheet-weight-modal__loading-head">
                    <span class="order-sheet-weight-modal__spinner" aria-hidden="true"></span>
                    <p class="order-sheet-weight-modal__loading-title">Fetching available weights...</p>
                  </div>
                </div>
                <div class="order-sheet-weight-modal__options" data-order-sheet-weight-options-wrap hidden>
                  <div class="order-sheet-weight-modal__dropdown" data-order-sheet-weight-dropdown>
                    <div class="order-sheet-weight-modal__dropdown-panel" data-order-sheet-weight-dropdown-panel hidden>
                      <div class="order-sheet-weight-modal__dropdown-search-wrap">
                        <input type="search" class="order-sheet-weight-modal__dropdown-search" data-order-sheet-weight-dropdown-search placeholder="Search weights..." autocomplete="off" aria-label="Search weights">
                      </div>
                      <div class="order-sheet-weight-modal__checkbox-list" data-order-sheet-weight-options role="listbox" aria-multiselectable="true"></div>
                      <p class="order-sheet-weight-modal__dropdown-empty" data-order-sheet-weight-dropdown-empty hidden>No weights match your search.</p>
                    </div>
                  </div>
                </div>
                <div class="order-sheet-weight-modal__empty" data-order-sheet-weight-empty hidden>
                  <p class="order-sheet-weight-modal__empty-title">No weights available</p>
                </div>
                <div class="order-sheet-weight-modal__error" data-order-sheet-weight-error hidden>
                  <p class="order-sheet-weight-modal__error-text">Unable to load available weights</p>
                  <button type="button" class="order-sheet-weight-modal__retry" data-order-sheet-weight-retry>Retry</button>
                </div>
              </div>
              <footer class="order-sheet-weight-modal__footer">
                <div class="order-sheet-weight-modal__footer-summary" aria-live="polite">
                  <div class="order-sheet-weight-modal__summary-card">
                    <span class="order-sheet-weight-modal__summary-label">Selected Weight</span>
                    <p class="order-sheet-weight-modal__summary" data-order-sheet-weight-summary>0</p>
                  </div>
                  <div class="order-sheet-weight-modal__summary-card">
                    <span class="order-sheet-weight-modal__summary-label">Selected Quantity</span>
                    <p class="order-sheet-weight-modal__summary" data-order-sheet-weight-summary-qty>0</p>
                  </div>
                  <div class="order-sheet-weight-modal__summary-card">
                    <span class="order-sheet-weight-modal__summary-label">Total Weight</span>
                    <p class="order-sheet-weight-modal__summary" data-order-sheet-weight-summary-total>0.0g</p>
                  </div>
                </div>
                <div class="order-sheet-weight-modal__footer-actions">
                  <button type="button" class="order-sheet-weight-modal__cancel" data-order-sheet-weight-close>Cancel</button>
                  <button type="button" class="order-sheet-weight-modal__confirm" data-order-sheet-weight-confirm disabled>Add Selection</button>
                </div>
              </footer>
            </div>
          </div>

          <div class="order-sheet-details-variant-table__footer">
            <div class="order-sheet-details-variant-table__footer-summary" aria-live="polite">
              <div class="order-sheet-details-variant-table__footer-stat">
                <span class="order-sheet-details-variant-table__footer-label">Selected Variants</span>
                <span class="order-sheet-details-variant-table__footer-value" data-details-selected-count>0</span>
              </div>
              <div class="order-sheet-details-variant-table__footer-stat">
                <span class="order-sheet-details-variant-table__footer-label">Total Quantity</span>
                <span class="order-sheet-details-variant-table__footer-value" data-details-total-qty>0</span>
              </div>
              <div class="order-sheet-details-variant-table__footer-stat">
                <span class="order-sheet-details-variant-table__footer-label">Total Weight</span>
                <span class="order-sheet-details-variant-table__footer-value" data-details-total-weight>0.00g</span>
              </div>
            </div>
            <button type="button" class="button order-sheet-details-variant-table__add-selected quick-view-add-to-cart__button" data-details-add-selected data-add-label="Add Selected To Cart" data-add-loading="Adding…" data-add-success="Added" disabled>
              <svg class="quick-view-add-to-cart__cart-icon" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 6h15l-1.5 9h-12L6 6z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>
                <path d="M6 6l-1-3H2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="9.5" cy="19.5" r="1.25" fill="currentColor"/>
                <circle cx="17.5" cy="19.5" r="1.25" fill="currentColor"/>
              </svg>
              <span data-details-add-selected-text>Add Selected To Cart</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <script type="application/json" data-plp-product-json>${productJson}</script>
</div>`;
  }

  /**
   * @param {string} handle
   * @returns {Promise<string>}
   */
  async function fetchOrderSheetSectionHtml(handle) {
    const sectionId = 'order-sheet-expanded-details';
    // IMPORTANT: do NOT send Accept: application/json — Shopify then returns product JSON
    // and ignores ?sections=, which caused empty section HTML / the load toast.
    const url = `/products/${encodeURIComponent(handle)}?sections=${encodeURIComponent(sectionId)}`;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`[OrderSheet] Section fetch HTTP ${res.status} for handle="${handle}"`);
    }
    const payload = await res.json();
    if (payload && payload.product && !payload[sectionId] && !(payload.sections && payload.sections[sectionId])) {
      throw new Error(
        `[OrderSheet] Section API returned product JSON instead of section HTML (bad Accept/header). handle="${handle}"`
      );
    }
    const html = payload?.[sectionId] || payload?.sections?.[sectionId] || '';
    if (html == null || html === '') {
      throw new Error(`[OrderSheet] Section "${sectionId}" returned empty/null for handle="${handle}"`);
    }
    return String(html);
  }

  /**
   * @param {string} handle
   * @returns {Promise<Record<string, any>>}
   */
  async function fetchOrderSheetProductJson(handle) {
    const url = `/products/${encodeURIComponent(handle)}.js`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) {
      throw new Error(`[OrderSheet] product.js HTTP ${res.status} for handle="${handle}"`);
    }
    const product = await res.json();
    if (!product || typeof product !== 'object' || !product.id) {
      throw new Error(`[OrderSheet] product.js returned invalid product for handle="${handle}"`);
    }
    // product.js omits inventory_quantity — merge Shopify qty from ?view=qty.
    try {
      const stockMap = await fetchShopifyStockMapForHandle(product.handle || handle);
      applyShopifyStockMapToProduct(product, stockMap);
    } catch (stockErr) {
      console.warn('[OrderSheet] Shopify qty view failed; stock display may be 0', stockErr);
    }
    return product;
  }

  /**
   * Populate empty Wizzy order-sheet shells.
   * 1) Prefer Section Rendering of plp-expanded-product-details (Liquid reuse)
   * 2) Fall back to /products/{handle}.js + order-sheet markup contracts
   * @param {HTMLElement} item
   * @param {string} handle
   * @returns {Promise<HTMLElement | null>}
   */
  async function hydrateOrderSheetExpandedDetails(item, handle) {
    const panel = /** @type {HTMLElement | null} */ (item.querySelector(PANEL_SEL));
    if (!panel) {
      console.error('[OrderSheet] Missing details panel on item', item);
      return null;
    }
    let details = /** @type {HTMLElement | null} */ (panel.querySelector('[data-plp-expanded-details]'));
    if (!details) {
      console.error('[OrderSheet] Missing data-plp-expanded-details shell', item);
      return null;
    }
    if (hasModernOrderSheetDetailsMarkup(details)) return details;

    const safeHandle = resolveOrderSheetProductHandle(item, item.querySelector(ROW_SEL), handle);
    if (!safeHandle) {
      console.error('[OrderSheet] Missing product handle for details hydrate', {
        itemHandle: item.getAttribute('data-product-handle'),
        rowHandle: item.querySelector(ROW_SEL)?.getAttribute('data-product-handle'),
        productId: item.getAttribute('data-product-id'),
        hint: handle,
      });
      return null;
    }

    if (details.dataset.detailsHydrating === 'true') {
      return /** @type {Promise<HTMLElement | null>} */ (
        /** @type {any} */ (details)._detailsHydratePromise || Promise.resolve(null)
      );
    }

    details.dataset.detailsHydrating = 'true';
    details.setAttribute('aria-busy', 'true');
    const hydratePromise = (async () => {
      let rendered = /** @type {HTMLElement | null} */ (null);

      try {
        const sectionHtml = await fetchOrderSheetSectionHtml(safeHandle);
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        rendered = doc.querySelector('[data-plp-expanded-details]');
        if (!(rendered instanceof HTMLElement)) {
          throw new Error('[OrderSheet] Section HTML missing data-plp-expanded-details');
        }
      } catch (sectionErr) {
        console.warn('[OrderSheet] Section hydrate failed; falling back to product.js', sectionErr);
        const product = await fetchOrderSheetProductJson(safeHandle);
        const rowUid = `wizzy-os-${product.id}`;
        const html = buildOrderSheetExpandedDetailsHtml(product, rowUid);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        rendered = doc.querySelector('[data-plp-expanded-details]');
        if (!(rendered instanceof HTMLElement)) {
          throw new Error('[OrderSheet] product.js renderer failed to create details root');
        }
      }

      delete panel.dataset.plpDetailsBound;
      details.replaceWith(rendered);
      rendered.dataset.detailsHydrated = 'true';
      rendered.removeAttribute('aria-busy');
      delete rendered.dataset.detailsHydrating;
      return rendered;
    })().catch((err) => {
      console.error('[OrderSheet] Failed to hydrate expanded details', err);
      details.removeAttribute('aria-busy');
      delete details.dataset.detailsHydrating;
      return null;
    });

    /** @type {any} */ (details)._detailsHydratePromise = hydratePromise;
    const result = await hydratePromise;
    delete /** @type {any} */ (details)._detailsHydratePromise;
    return result;
  }

  /**
   * Toggle inline accordion (hydrate empty Wizzy shells first).
   * @param {HTMLElement} host
   * @param {HTMLElement} item
   * @param {string} handle
   */
  async function toggleInlineDetails(host, item, handle) {
    if (item.dataset.detailsToggleBusy === 'true') return;
    const toggleBtn = /** @type {HTMLButtonElement | null} */ (item.querySelector(TOGGLE_SEL));
    const currentlyOpen = toggleBtn?.getAttribute('aria-expanded') === 'true';

    if (currentlyOpen) {
      setPanelOpen(item, false);
      return;
    }

    item.dataset.detailsToggleBusy = 'true';
    try {
      const panel = /** @type {HTMLElement | null} */ (item.querySelector(PANEL_SEL));
      const details = /** @type {HTMLElement | null} */ (panel?.querySelector('[data-plp-expanded-details]'));
      if (details && !hasModernOrderSheetDetailsMarkup(details)) {
        const hydrated = await hydrateOrderSheetExpandedDetails(item, handle);
        if (!hydrated) {
          showToast('Unable to load product details. Please try again.', 'error');
          setPanelOpen(item, false);
          return;
        }
      }

      if (!allowMultiple(host)) {
        closeAllExcept(host, item);
      }
      setPanelOpen(item, true);
    } finally {
      delete item.dataset.detailsToggleBusy;
    }
  }

  function bindDocumentToggle() {
    if (documentToggleBound) return;
    documentToggleBound = true;

    // Whole order-sheet card click:
    // - Non-chain products → toggle existing inline expanded details
    // - Chain products → keep Quick View modal behavior
    document.addEventListener(
      'click',
      (e) => {
        if (!(e.target instanceof Element)) return;

        // Never intercept interactions inside an already-open details panel.
        if (e.target.closest(PANEL_SEL)) return;

        const item = /** @type {HTMLElement | null} */ (e.target.closest(ITEM_SEL));
        if (!item) return;

        const host = /** @type {HTMLElement | null} */ (item.closest('.plp-view-host'));
        if (!host || host.dataset.plpEnableInventory !== 'true') return;

        const row = /** @type {HTMLElement | null} */ (
          e.target.closest(ROW_SEL) || item.querySelector(ROW_SEL)
        );
        if (!row || !item.contains(row)) return;

        const toggleBtn = /** @type {HTMLButtonElement | null} */ (item.querySelector(TOGGLE_SEL));
        if (!toggleBtn) return;

        const clickedToggle = Boolean(e.target.closest(TOGGLE_SEL));
        if (!clickedToggle && isOrderSheetRowInteractive(e.target)) return;

        // Block PDP links / default navigation from image + title clicks.
        e.preventDefault();
        e.stopPropagation();

        const rowHandle = resolveOrderSheetProductHandle(item, row, '');
        const callerProduct = parseDetailsProductJson(item) || parseDetailsProductJson(row);
        const useInlineDetails = shouldUseInlineOrderSheetDetails(item, row, callerProduct);

        if (useInlineDetails) {
          void toggleInlineDetails(host, item, rowHandle);
          return;
        }

        // Matrix Order Sheet (chains, bracelets): keep intentional Quick View behavior.
        closeAllExcept(host, null);
        setPanelOpen(item, false);
        const qv = /** @type {any} */ (window).__aoneQuickViewModalController;
        if (rowHandle && qv && typeof qv.open === 'function') {
          qv.open({ handle: rowHandle, product: callerProduct });
        }
      },
      true
    );

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      document.querySelectorAll('[data-order-sheet-weight-modal]:not([hidden])').forEach((modal) => {
        if (!(modal instanceof HTMLElement)) return;
        const state = getWeightModalStateFromModal(modal);
        if (!state) return;
        closeWeightModal(state.detailsRoot);
      });
    });
  }

  /** @param {HTMLElement} host */
  function bindHost(host) {
    hostControllers.get(host)?.abort();
    const ac = new AbortController();
    hostControllers.set(host, ac);

    host.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Escape') return;
        if (host.dataset.plpEnableInventory !== 'true') return;
        const grid = host.querySelector('[data-plp-layout="inventory"], [data-plp-view="inventory"]');
        if (!grid) return;
        closeAllExcept(host, null);
      },
      { signal: ac.signal }
    );
  }

  function init() {
    bindDocumentToggle();
    document.querySelectorAll('.plp-view-host').forEach((host) => {
      if (!(host instanceof HTMLElement)) return;
      if (host.dataset.plpEnableInventory !== 'true') return;
      bindHost(host);
    });
  }

  function rehydrateOpenDetailsFromCart(event) {
    const detail = event && event.detail ? event.detail : null;
    const availabilityApi = getWmsAvailabilityApi();
    const cartReady =
      availabilityApi && typeof availabilityApi.isCartStateReady === 'function'
        ? availabilityApi.isCartStateReady()
        : true;
    const loading =
      (detail && (detail.loading === true || detail.cartReady === false)) ||
      !cartReady;

    document
      .querySelectorAll(
        '[data-plp-expanded-details], [data-order-sheet-expanded-details], [data-quick-view-details], [data-premium-pdp-details]'
      )
      .forEach((detailsRoot) => {
        if (!(detailsRoot instanceof HTMLElement)) return;
        if (detailsRoot.hidden) return;
        // Quick View listens on aone:cart-inventory-updated and rehydrates itself.
        if (detailsRoot.hasAttribute('data-quick-view-details')) return;
        // Premium PDP has its own cart rehydrate (rebuilds matrix + syncPdpDraftFromCart).
        if (detailsRoot.closest('[data-pdp-section], [data-pdp-panel]')) return;
        if (loading) {
          detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
            if (!(row instanceof HTMLElement)) return;
            setRowWmsStatus(row, 'loading', 0);
            syncRowWeightActionLabel(row, { isLoading: true });
            syncRowMinusState(row);
            syncWeightButtonState(row);
          });
          updateAddSelectedState(detailsRoot);
          return;
        }
        clearRowSelectionDirtyFlags(detailsRoot);
        hydrateDetailsWmsAvailability(detailsRoot).catch(() => {});
      });
  }

  if (document.documentElement.dataset.aonePlpCartInventoryBound !== 'true') {
    document.documentElement.dataset.aonePlpCartInventoryBound = 'true';
    const availabilityApi = getWmsAvailabilityApi();
    const eventName =
      (availabilityApi && availabilityApi.CART_INVENTORY_EVENT) || 'aone:cart-inventory-updated';
    document.addEventListener(eventName, (event) => {
      rehydrateOpenDetailsFromCart(event);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => queueMicrotask(init));
  document.addEventListener(FILTER_EVT, () => {
    document.querySelectorAll('.plp-view-host').forEach((h) => {
      if (h instanceof HTMLElement) hostControllers.get(h)?.abort();
    });
    queueMicrotask(() => setTimeout(init, 0));
  });

  window.plpVariantDetails = {
    bindPanel,
    openImageViewer,
    bindChainMatrixStickyHeaderSync,
    syncChainMatrixStickyHeaderHeights,
    reconcileRowQuantitySelection,
    getSelectableWeightEntriesForSku,
    applyCartSelectionToRow,
    commitDraftRowsToCart,
    isRowDraftDifferentFromCart,
    isRowDraftCommittable,
    isRowDraftVisibleInSummary,
    getRowFullDraftPayload,
    /**
     * Recompute footer totals + ATC enabled state from current draft rows.
     * Used by PDP / Order Sheet after cart hydration.
     * @param {HTMLElement} detailsRoot
     */
    syncDraftSummary(detailsRoot) {
      if (!(detailsRoot instanceof HTMLElement)) return;
      updateDetailsSummary(detailsRoot);
      updateAddSelectedState(detailsRoot);
    },
    /**
     * Paint cart → draft on every variant row, then refresh footer.
     * @param {HTMLElement} detailsRoot
     * @param {{ force?: boolean }} [options]
     */
    hydrateCartDraftSelections(detailsRoot, options = {}) {
      if (!(detailsRoot instanceof HTMLElement)) return;
      detailsRoot.querySelectorAll('[data-details-variant-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        applyCartSelectionToRow(row, { force: Boolean(options.force) });
      });
      updateDetailsSummary(detailsRoot);
      updateAddSelectedState(detailsRoot);
    },
    syncPremiumPdpListEmptyState,
  };
})();
